"use client";

  import React, { useState, useEffect } from "react";
  import {
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    IconButton,
    Alert,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Autocomplete,
    Stack,
    Paper,
    useTheme,
    useMediaQuery,
  } from "@mui/material";
  import { alpha } from "@mui/material/styles";
  import {
    PostAdd,
    People,
    Visibility,
    Schedule,
    ArrowForward,
    Close,
    Edit,
    Save,
    LocationOn,
  } from "@mui/icons-material";
  import apiClient from "../context/apiClient";
  import Layout from "../components/common/Layout";
  import { useNavigate } from "react-router-dom";


  const EmployerDashboard = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // --- STATE AND DATA FETCHING LOGIC (NO CHANGES) ---
    const [jobPostings, setJobPostings] = useState([]);
    const [topCandidates, setTopCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [candidatesLoading, setCandidatesLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState("");
    const [stats, setStats] = useState({
      activeJobs: 0,
      totalApplications: 0,
      interviewsScheduled: 0,
      offersExtended: 0,
    });
    const topCardsData = [
      { label: "ACTIVE JOBS", value: stats.activeJobs },
      { label: "TOTAL APPLICATIONS", value: stats.totalApplications },
      { label: "INTERVIEWS SCHEDULED", value: stats.interviewsScheduled },
      { label: "OFFERS EXTENDED", value: stats.offersExtended },
    ];
    useEffect(() => {
      const fetchDashboardData = async () => {
        setLoading(true);
        setCandidatesLoading(true);
        setError(null);
        try {
          const [jobResponse, statsResponse] = await Promise.all([
            apiClient.get("/job-postings/"),
            apiClient.get("/employer-dashboard/"),
          ]);
          const jobs = jobResponse?.data || [];
          setJobPostings(jobs);
          const dashboardStats = statsResponse.data;
          setStats({
            activeJobs: dashboardStats.active_jobs ?? 0,
            totalApplications: dashboardStats.total_applicants ?? 0,
            interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
            offersExtended: dashboardStats.offer_extended ?? 0,
          });
          setLoading(false);
          if (jobs.length > 0) {
            const mostRecentJobId = jobs[0].id;
            const candidatesResponse = await apiClient.get(
              `/job-postings/${mostRecentJobId}/potential-candidates/`
            );
            const potentialCandidates = candidatesResponse.data || [];
            const formattedCandidates = potentialCandidates.map((candidate) => ({
              id: candidate.talent_id,
              applicant_name: candidate.name,
              matching_percentage: Math.round(candidate.ai_match_score),
              location: candidate.location,
              resume_url: candidate.resume_url,
              experience: "5+ year of Experience",
            }));
            const sortedCandidates = formattedCandidates.sort(
              (a, b) => b.matching_percentage - a.matching_percentage
            );
            setTopCandidates(sortedCandidates.slice(0, 5));
          } else {
            setTopCandidates([]);
          }
        } catch (err) {
          let errorMessage = "An unexpected error occurred.";
          if (err.response) {
            errorMessage = `Error: ${
              err.response.data?.detail || err.response.statusText
            }`;
          } else if (err.request) {
            errorMessage = "No response from server. Check your connection.";
          } else {
            errorMessage = err.message;
          }
          if (err.response?.status !== 401) {
            setError(errorMessage);
          }
          setLoading(false);
        } finally {
          setCandidatesLoading(false);
        }
      };
      fetchDashboardData();
    }, []);

    useEffect(() => {
      if (selectedJob) {
        setEditedData({
          title: selectedJob.title || "",
          location: selectedJob.location || "",
          required_skills: selectedJob.required_skills || [],
        });
        setIsEditing(false);
        setModalError("");
      }
    }, [selectedJob]);

    // --- EVENT HANDLERS (NO CHANGES) ---
    const jobsToDisplay = jobPostings.slice(0, 6);
    const handleOpenModal = (job) => {
      setSelectedJob(job);
      setIsModalOpen(true);
    };
    const handleCloseModal = () => {
      setIsModalOpen(false);
      setSelectedJob(null);
    };
    const handleJobUpdate = (updatedJob) => {
      setJobPostings((prevJobs) =>
        prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
      );
    };
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setEditedData((prev) => ({ ...prev, [name]: value }));
    };
    const handleSkillsChange = (event, newValue) => {
      setEditedData((prev) => ({ ...prev, required_skills: newValue }));
    };
    const handleSaveChanges = async () => {
      setModalLoading(true);
      setModalError("");
      try {
        const response = await apiClient.put(
          `/job-postings/${selectedJob.id}/`,
          { ...selectedJob, ...editedData }
        );
        handleJobUpdate(response.data);
        setIsEditing(false);
        handleCloseModal();
      } catch (err) {
        setModalError("Failed to save changes. Please try again.");
      } finally {
        setModalLoading(false);
      }
    };
    const handleCancelEdit = () => {
      if (selectedJob) {
        setEditedData({
          title: selectedJob.title,
          location: selectedJob.location,
          required_skills: selectedJob.required_skills,
        });
      }
      setIsEditing(false);
    };
    const handleViewResume = (resumeUrl) => {
      if (resumeUrl) {
        window.open(resumeUrl, "_blank", "noopener,noreferrer");
      }
    };
    const handleScheduleInterview = () => {
      navigate("/db-shiksha/gatep/employer/interviews");
    };

    // --- SKELETON RENDERERS (REFACTORED for THEME) ---
    const renderJobSkeletons = () =>
      [...Array(5)].map((_, index) => (
        <TableRow key={index} sx={{ "& td, & th": { border: 0, py: 1.5 } }}>
          <TableCell component="th" scope="row">
            <Box>
              <Skeleton variant="text" width="80%" sx={{ fontSize: "1rem" }} />
              <Skeleton variant="text" width="50%" sx={{ fontSize: "0.8rem" }} />
            </Box>
          </TableCell>
          <TableCell>
            <Skeleton variant="circular" width={32} height={32} />
          </TableCell>
        </TableRow>
      ));

    const renderCandidateSkeletons = () =>
      [...Array(4)].map((_, index) => (
        <Paper
          key={index}
          sx={{
            p: 2,
            borderRadius: 3,
            boxShadow: "none",
            // The background color will be inherited from the theme's MuiPaper override
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box flexGrow={1}>
              <Skeleton variant="text" sx={{ fontSize: "1rem" }} width="80%" />
              <Skeleton
                variant="text"
                sx={{ fontSize: "0.875rem" }}
                width="50%"
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} mt={2}>
            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
          </Stack>
        </Paper>
      ));

    return (
      <Layout title="Employer Dashboard">
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            height: "calc(100vh - 64px)",
            overflowY: "auto",
            // Global scrollbar style from theme is used here
          }}
        >
          <Stack direction="column" spacing={{ xs: 2, md: 3 }}>
            {/* --- QUICK ACTIONS (REFACTORED FOR THEME) --- */}
            <Paper
              sx={{
                p: 3,
                backgroundColor: "mint.main", // Using theme color
                borderRadius: "12px", // Kept from original
              }}
            >
              <Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Quick Actions
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    color="primary" // Use theme color
                    startIcon={<PostAdd />}
                    onClick={() => navigate("/db-shiksha/gatep/employer/post-job")}
                    sx={{
                      // Kept original styles not covered by theme overrides
                      borderRadius: 2,
                      boxShadow: "0 4px 12px 0 rgba(79,70,186,0.3)",
                      "&:hover": { backgroundColor: "#40389e" }, // Can be refined further if needed
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    Post a New Job
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<People />}
                    onClick={() => navigate("/db-shiksha/gatep/employer/candidates")}
                    sx={{
                      borderColor: "divider", // Use theme color
                      color: "primary.main", // Use theme color
                      borderRadius: 2,
                      backgroundColor: "background.paper", // Use theme color
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    Manage Candidates
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Schedule />}
                    onClick={() => navigate("/db-shiksha/gatep/employer/interviews")}
                    sx={{
                      borderColor: "divider", // Use theme color
                      color: "primary.main", // Use theme color
                      borderRadius: 2,
                      backgroundColor: "background.paper", // Use theme color
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    Scheduled Interviews
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {/* --- STATS CARDS (REFACTORED FOR THEME) --- */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, md: 3 },
              }}
            >
              {loading
                ? [...Array(4)].map((_, index) => (
                    <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={140}
                        sx={{ borderRadius: "12px" }}
                      />
                    </Box>
                  ))
                : topCardsData.map((card, index) => (
                    <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: 140,
                          backgroundColor: "primary.main", // Use theme color
                          borderRadius: "12px",
                          boxShadow: theme.shadows[3],
                          position: "relative",
                          overflow: "hidden",
                          p: { xs: 1.5, sm: 2, md: 2.5 },
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute", top: 0, right: 0,
                            width: "100%", height: "100%", zIndex: 0,
                          }}
                        >
                          <svg
                            width="100%" height="100%" viewBox="0 0 280 140"
                            preserveAspectRatio="none"
                            style={{ position: "absolute", right: 0, top: 0 }}
                          >

                            
                            <path
                              d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
                              fill={theme.palette.accent?.main || theme.palette.secondary.light}
                            />
                          </svg>
                        </Box>
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              fontWeight: 600,
                              color: alpha(theme.palette.primary.contrastText, 0.8),
                              textTransform: "uppercase",
                              lineHeight: 1.2,
                            }}
                          >
                            {card.label}
                          </Typography>
                        </Box>
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: "1.75rem", md: "2.25rem" },
                              fontWeight: "bold",
                              color: "primary.contrastText", // Use theme color
                              lineHeight: 1,
                            }}
                          >
                            {card.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              {/* --- YOUR RECENT JOB POSTINGS (ADJUSTED GRID) --- */}
              <Grid item xs={12} md={8}>
                <Card
                  sx={{
                    borderRadius: 4, 
                    boxShadow: "none", 
                    p: { xs: 1, sm: 2 }, 
                    backgroundColor: "mint.main", 
                    width: "100%", 
                    height: "100%", 
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row" justifyContent="space-between"
                      alignItems="center" mb={2}
                    >
                      <Typography
                        variant={isMobile ? "h6" : "h6"}
                        fontWeight="bold"
                      >
                        Your Recent Job Postings
                      </Typography>
                      {!loading && jobPostings.length > 5 && (
                        <Button
                          onClick={() => navigate("/db-shiksha/gatep/employer/candidates")}
                          endIcon={<ArrowForward />}
                          color="primary" // Use theme color
                          sx={{ textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          View All Jobs
                        </Button>
                      )}
                    </Stack>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TableContainer>
                      <Table sx={{ minWidth: isMobile ? 300 : 650 }}>
                        <TableHead
                          sx={{
                            "& .MuiTableCell-root": {
                              borderBottom: "none",
                              color: "text.secondary", // Use theme color
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            },
                          }}
                        >
                          <TableRow>
                            <TableCell>Position</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loading
                            ? renderJobSkeletons()
                            : jobsToDisplay.map((job) => (
                                <TableRow
                                  key={job.id} hover
                                  sx={{ "& td, & th": { border: 0, py: { xs: 1, sm: 1.5 } } }}
                                >
                                  <TableCell component="th" scope="row">
                                    <Box>
                                      <Typography
                                        variant={isMobile ? "body2" : "subtitle2"}
                                        fontWeight="bold"
                                      >
                                        {job.title}
                                      </Typography>
                                      <Stack
                                        direction="row" alignItems="center"
                                        color="text.secondary" // Use theme color
                                        spacing={0.5}
                                      >
                                        <LocationOn sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                        <Typography variant={isMobile ? "caption" : "body2"}>
                                          {job.location}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => handleOpenModal(job)}>
                                      <Visibility
                                        sx={{
                                          color: "text.secondary", // Use theme color
                                          fontSize: { xs: 18, sm: 24 },
                                        }}
                                      />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                          {!loading && !error && jobPostings.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: { xs: 2, sm: 3 } }}>
                                No jobs posted yet.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* --- TOP MATCHED CANDIDATES (ADJUSTED GRID & WIDTH) --- */}
              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    borderRadius: 4, 
                    boxShadow: "none",
                    p: { xs: 1, sm: 2 }, 
                    backgroundColor: "mint.main", 
                    height: "100%", 
                    width: '100%',
                  }}
                >
                  <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant={isMobile ? "h6" : "h6"}
                      gutterBottom
                      fontWeight="bold"
                    >
                      Top Matched Candidates
                    </Typography>
                    {candidatesLoading ? (
                      <Stack spacing={2}>{renderCandidateSkeletons()}</Stack>
                    ) : error ? (
                      <Alert severity="warning">Could not load candidate data.</Alert>
                    ) : topCandidates.length > 0 ? (
                      <Box
                        sx={{
                          flexGrow: 1, overflowY: "auto",
                          maxHeight: { xs: "300px", sm: "450px" }, pr: 1,
                          // Using global scrollbar from theme
                        }}
                      >
                        <Stack spacing={2}>
                          {topCandidates.map((candidate) => (
                            <Paper
                              key={candidate.id}
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 3,
                                boxShadow: "none",
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: "background.paper", // Use theme color
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
                                <Avatar
                                  sx={{
                                    bgcolor: "accent.main", // Use theme accent color
                                    color: "accent.contrastText", // Use corresponding contrast text
                                    fontWeight: "bold",
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  }}
                                >
                                  {candidate.applicant_name.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box flexGrow={1}>
                                  <Typography
                                    variant={isMobile ? "subtitle2" : "subtitle1"}
                                    fontWeight="bold"
                                  >
                                    {candidate.applicant_name}
                                  </Typography>
                                  <Typography
                                    variant={isMobile ? "caption" : "body2"}
                                    color="text.secondary" // Use theme color
                                  >
                                    {candidate.location}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={2}>
                                <Button
                                  size="small" variant="contained" color="primary"
                                  sx={{
                                    flex: 1,
                                    borderRadius: 2,
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    py: { xs: 0.75, sm: 1 },
                                  }}
                                  onClick={() => handleViewResume(candidate.resume_url)}
                                >
                                  View Resume   
                                </Button>
                                <Button
                                  size="small" variant="outlined" color="primary"
                                  sx={{
                                    flex: 1,
                                    borderRadius: 2,
                                    "&:hover": {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                    },
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    py: { xs: 0.75, sm: 1 },
                                  }}
                                  onClick={handleScheduleInterview}
                                >
                                  Schedule Interview
                                </Button>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2" color="text.secondary"
                        sx={{ textAlign: "center", py: { xs: 2, sm: 3 } }}
                      >
                        No potential candidates found.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>

          {/* --- DIALOG MODAL (REFACTORED FOR THEME) --- */}
          {selectedJob && (
            <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
              <DialogTitle sx={{ pb: 1, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                Job Details
                <IconButton
                  onClick={handleCloseModal}
                  sx={{ position: "absolute", right: { xs: 8, sm: 16 }, top: { xs: 8, sm: 12 } }}
                >
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
                {modalError && <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Role</Typography>
                    {isEditing ? (
                      <TextField fullWidth name="title" value={editedData.title} onChange={handleInputChange} size={isMobile ? "small" : "medium"} />
                    ) : (
                      <Typography variant={isMobile ? "body1" : "h6"}>{selectedJob.title}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Location</Typography>
                    {isEditing ? (
                      <TextField fullWidth name="location" value={editedData.location} onChange={handleInputChange} size={isMobile ? "small" : "medium"} />
                    ) : (
                      <Typography variant={isMobile ? "body2" : "body1"}>{selectedJob.location}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Posted Date</Typography>
                    <Typography variant={isMobile ? "body2" : "body1"}>
                      {new Date(selectedJob.created_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Required Skills</Typography>
                    {isEditing ? (
                      <Autocomplete
                        multiple freeSolo options={[]}
                        value={editedData.required_skills || []}
                        onChange={handleSkillsChange}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip variant="outlined" label={option} {...getTagProps({ index })} size={isMobile ? "small" : "medium"} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" placeholder="Add skills" size={isMobile ? "small" : "medium"} />
                        )}
                      />
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                        {(selectedJob.required_skills || []).map((skill) => (
                          <Chip key={skill} label={skill} size={isMobile ? "small" : "medium"} />
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
                {isEditing ? (
                  <>
                    <Button onClick={handleCancelEdit} color="secondary" size={isMobile ? "small" : "medium"}>Cancel</Button>
                    <Button
                      onClick={handleSaveChanges} variant="contained"
                      startIcon={modalLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                      disabled={modalLoading} size={isMobile ? "small" : "medium"}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => setIsEditing(true)} variant="outlined" startIcon={<Edit />} size={isMobile ? "small" : "medium"}>Edit</Button>
                    <Button onClick={handleCloseModal} size={isMobile ? "small" : "medium"}>Close</Button>
                  </>
                )}
              </DialogActions>
            </Dialog>
          )}
        </Box>
      </Layout>
    );
  };

  export default EmployerDashboard;


// "use client";

  // import React, { useState, useEffect } from "react";
  // import {
  //   Grid,
  //   Card,
  //   CardContent,
  //   Typography,
  //   Button,
  //   Box,
  //   Chip,
  //   Table,
  //   TableBody,
  //   TableCell,
  //   TableContainer,
  //   TableHead,
  //   TableRow,
  //   Avatar,
  //   IconButton,
  //   Alert,
  //   Skeleton,
  //   Dialog,
  //   DialogTitle,
  //   DialogContent,
  //   DialogActions,
  //   TextField,
  //   CircularProgress,
  //   Autocomplete,
  //   Stack,
  //   Paper,
  //   useTheme,
  //   useMediaQuery,
  // } from "@mui/material";
  // import { alpha } from "@mui/material/styles";
  // import {
  //   PostAdd,
  //   People,
  //   Visibility,
  //   Schedule,
  //   ArrowForward,
  //   Close,
  //   Edit,
  //   Save,
  //   LocationOn,
  // } from "@mui/icons-material";
  // import apiClient from "../context/apiClient";
  // import Layout from "../components/common/Layout";
  // import { useNavigate } from "react-router-dom";


  // const EmployerDashboard = () => {
  //   const navigate = useNavigate();
  //   const theme = useTheme();
  //   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  //   // --- STATE AND DATA FETCHING LOGIC (NO CHANGES) ---
  //   const [jobPostings, setJobPostings] = useState([]);
  //   const [topCandidates, setTopCandidates] = useState([]);
  //   const [loading, setLoading] = useState(true);
  //   const [error, setError] = useState(null);
  //   const [candidatesLoading, setCandidatesLoading] = useState(true);
  //   const [isModalOpen, setIsModalOpen] = useState(false);
  //   const [selectedJob, setSelectedJob] = useState(null);
  //   const [isEditing, setIsEditing] = useState(false);
  //   const [editedData, setEditedData] = useState({});
  //   const [modalLoading, setModalLoading] = useState(false);
  //   const [modalError, setModalError] = useState("");
  //   const [stats, setStats] = useState({
  //     activeJobs: 0,
  //     totalApplications: 0,
  //     interviewsScheduled: 0,
  //     offersExtended: 0,
  //   });
  //   const topCardsData = [
  //     { label: "ACTIVE JOBS", value: stats.activeJobs },
  //     { label: "TOTAL APPLICATIONS", value: stats.totalApplications },
  //     { label: "INTERVIEWS SCHEDULED", value: stats.interviewsScheduled },
  //     { label: "OFFERS EXTENDED", value: stats.offersExtended },
  //   ];
  //   useEffect(() => {
  //     const fetchDashboardData = async () => {
  //       setLoading(true);
  //       setCandidatesLoading(true);
  //       setError(null);
  //       try {
  //         const [jobResponse, statsResponse] = await Promise.all([
  //           apiClient.get("/job-postings/"),
  //           apiClient.get("/employer-dashboard/"),
  //         ]);
  //         const jobs = jobResponse?.data || [];
  //         setJobPostings(jobs);
  //         const dashboardStats = statsResponse.data;
  //         setStats({
  //           activeJobs: dashboardStats.active_jobs ?? 0,
  //           totalApplications: dashboardStats.total_applicants ?? 0,
  //           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
  //           offersExtended: dashboardStats.offer_extended ?? 0,
  //         });
  //         setLoading(false);
  //         if (jobs.length > 0) {
  //           const mostRecentJobId = jobs[0].id;
  //           const candidatesResponse = await apiClient.get(
  //             `/job-postings/${mostRecentJobId}/potential-candidates/`
  //           );
  //           const potentialCandidates = candidatesResponse.data || [];
  //           const formattedCandidates = potentialCandidates.map((candidate) => ({
  //             id: candidate.talent_id,
  //             applicant_name: candidate.name,
  //             matching_percentage: Math.round(candidate.ai_match_score),
  //             location: candidate.location,
  //             resume_url: candidate.resume_url,
  //             experience: "5+ year of Experience",
  //           }));
  //           const sortedCandidates = formattedCandidates.sort(
  //             (a, b) => b.matching_percentage - a.matching_percentage
  //           );
  //           setTopCandidates(sortedCandidates.slice(0, 5));
  //         } else {
  //           setTopCandidates([]);
  //         }
  //       } catch (err) {
  //         let errorMessage = "An unexpected error occurred.";
  //         if (err.response) {
  //           errorMessage = `Error: ${
  //             err.response.data?.detail || err.response.statusText
  //           }`;
  //         } else if (err.request) {
  //           errorMessage = "No response from server. Check your connection.";
  //         } else {
  //           errorMessage = err.message;
  //         }
  //         if (err.response?.status !== 401) {
  //           setError(errorMessage);
  //         }
  //         setLoading(false);
  //       } finally {
  //         setCandidatesLoading(false);
  //       }
  //     };
  //     fetchDashboardData();
  //   }, []);

  //   useEffect(() => {
  //     if (selectedJob) {
  //       setEditedData({
  //         title: selectedJob.title || "",
  //         location: selectedJob.location || "",
  //         required_skills: selectedJob.required_skills || [],
  //       });
  //       setIsEditing(false);
  //       setModalError("");
  //     }
  //   }, [selectedJob]);

  //   // --- EVENT HANDLERS (NO CHANGES) ---
  //   const jobsToDisplay = jobPostings.slice(0, 6);
  //   const handleOpenModal = (job) => {
  //     setSelectedJob(job);
  //     setIsModalOpen(true);
  //   };
  //   const handleCloseModal = () => {
  //     setIsModalOpen(false);
  //     setSelectedJob(null);
  //   };
  //   const handleJobUpdate = (updatedJob) => {
  //     setJobPostings((prevJobs) =>
  //       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
  //     );
  //   };
  //   const handleInputChange = (e) => {
  //     const { name, value } = e.target;
  //     setEditedData((prev) => ({ ...prev, [name]: value }));
  //   };
  //   const handleSkillsChange = (event, newValue) => {
  //     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
  //   };
  //   const handleSaveChanges = async () => {
  //     setModalLoading(true);
  //     setModalError("");
  //     try {
  //       const response = await apiClient.put(
  //         `/job-postings/${selectedJob.id}/`,
  //         { ...selectedJob, ...editedData }
  //       );
  //       handleJobUpdate(response.data);
  //       setIsEditing(false);
  //       handleCloseModal();
  //     } catch (err) {
  //       setModalError("Failed to save changes. Please try again.");
  //     } finally {
  //       setModalLoading(false);
  //     }
  //   };
  //   const handleCancelEdit = () => {
  //     if (selectedJob) {
  //       setEditedData({
  //         title: selectedJob.title,
  //         location: selectedJob.location,
  //         required_skills: selectedJob.required_skills,
  //       });
  //     }
  //     setIsEditing(false);
  //   };
  //   const handleViewResume = (resumeUrl) => {
  //     if (resumeUrl) {
  //       window.open(resumeUrl, "_blank", "noopener,noreferrer");
  //     }
  //   };
  //   const handleScheduleInterview = () => {
  //     navigate("/gatep/employer/interviews");
  //   };

  //   // --- SKELETON RENDERERS (REFACTORED for THEME) ---
  //   const renderJobSkeletons = () =>
  //     [...Array(5)].map((_, index) => (
  //       <TableRow key={index} sx={{ "& td, & th": { border: 0, py: 1.5 } }}>
  //         <TableCell component="th" scope="row">
  //           <Box>
  //             <Skeleton variant="text" width="80%" sx={{ fontSize: "1rem" }} />
  //             <Skeleton variant="text" width="50%" sx={{ fontSize: "0.8rem" }} />
  //           </Box>
  //         </TableCell>
  //         <TableCell>
  //           <Skeleton variant="circular" width={32} height={32} />
  //         </TableCell>
  //       </TableRow>
  //     ));

  //   const renderCandidateSkeletons = () =>
  //     [...Array(4)].map((_, index) => (
  //       <Paper
  //         key={index}
  //         sx={{
  //           p: 2,
  //           borderRadius: 3,
  //           boxShadow: "none",
  //           // The background color will be inherited from the theme's MuiPaper override
  //           border: `1px solid ${theme.palette.divider}`,
  //         }}
  //       >
  //         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
  //           <Skeleton variant="circular" width={40} height={40} />
  //           <Box flexGrow={1}>
  //             <Skeleton variant="text" sx={{ fontSize: "1rem" }} width="80%" />
  //             <Skeleton
  //               variant="text"
  //               sx={{ fontSize: "0.875rem" }}
  //               width="50%"
  //             />
  //           </Box>
  //         </Stack>
  //         <Stack direction="row" spacing={1} mt={2}>
  //           <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
  //           <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
  //         </Stack>
  //       </Paper>
  //     ));

  //   return (
  //     <Layout title="Employer Dashboard">
  //       <Box
  //         sx={{
  //           p: { xs: 2, md: 3 },
  //           height: "calc(100vh - 64px)",
  //           overflowY: "auto",
  //           // Global scrollbar style from theme is used here
  //         }}
  //       >
  //         <Stack direction="column" spacing={{ xs: 2, md: 3 }}>
  //           {/* --- QUICK ACTIONS (REFACTORED FOR THEME) --- */}
  //           <Paper
  //             sx={{
  //               p: 3,
  //               backgroundColor: "mint.main", // Using theme color
  //               borderRadius: "12px", // Kept from original
  //             }}
  //           >
  //             <Box>
  //               <Typography
  //                 variant={isMobile ? "h6" : "h5"}
  //                 sx={{ mb: 2, fontWeight: "bold" }}
  //               >
  //                 Quick Actions
  //               </Typography>
  //               <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
  //                 <Button
  //                   variant="contained"
  //                   color="primary" // Use theme color
  //                   startIcon={<PostAdd />}
  //                   onClick={() => navigate("/gatep/employer/post-job")}
  //                   sx={{
  //                     // Kept original styles not covered by theme overrides
  //                     borderRadius: 2,
  //                     boxShadow: "0 4px 12px 0 rgba(79,70,186,0.3)",
  //                     "&:hover": { backgroundColor: "#40389e" }, // Can be refined further if needed
  //                     width: isMobile ? "100%" : "auto",
  //                   }}
  //                 >
  //                   Post a New Job
  //                 </Button>
  //                 <Button
  //                   variant="outlined"
  //                   startIcon={<People />}
  //                   onClick={() => navigate("/gatep/employer/candidates")}
  //                   sx={{
  //                     borderColor: "divider", // Use theme color
  //                     color: "primary.main", // Use theme color
  //                     borderRadius: 2,
  //                     backgroundColor: "background.paper", // Use theme color
  //                     width: isMobile ? "100%" : "auto",
  //                   }}
  //                 >
  //                   Manage Candidates
  //                 </Button>
  //                 <Button
  //                   variant="outlined"
  //                   startIcon={<Schedule />}
  //                   onClick={() => navigate("/gatep/employer/interviews")}
  //                   sx={{
  //                     borderColor: "divider", // Use theme color
  //                     color: "primary.main", // Use theme color
  //                     borderRadius: 2,
  //                     backgroundColor: "background.paper", // Use theme color
  //                     width: isMobile ? "100%" : "auto",
  //                   }}
  //                 >
  //                   Scheduled Interviews
  //                 </Button>
  //               </Stack>
  //             </Box>
  //           </Paper>

  //           {/* --- STATS CARDS (REFACTORED FOR THEME) --- */}
  //           <Box
  //             sx={{
  //               display: "flex",
  //               flexDirection: { xs: "column", sm: "row" },
  //               gap: { xs: 2, md: 3 },
  //             }}
  //           >
  //             {loading
  //               ? [...Array(4)].map((_, index) => (
  //                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
  //                     <Skeleton
  //                       variant="rectangular"
  //                       width="100%"
  //                       height={140}
  //                       sx={{ borderRadius: "12px" }}
  //                     />
  //                   </Box>
  //                 ))
  //               : topCardsData.map((card, index) => (
  //                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
  //                     <Box
  //                       sx={{
  //                         width: "100%",
  //                         height: 140,
  //                         backgroundColor: "primary.main", // Use theme color
  //                         borderRadius: "12px",
  //                         boxShadow: theme.shadows[3],
  //                         position: "relative",
  //                         overflow: "hidden",
  //                         p: { xs: 1.5, sm: 2, md: 2.5 },
  //                         display: "flex",
  //                         flexDirection: "column",
  //                         justifyContent: "space-between",
  //                       }}
  //                     >
  //                       <Box
  //                         sx={{
  //                           position: "absolute", top: 0, right: 0,
  //                           width: "100%", height: "100%", zIndex: 0,
  //                         }}
  //                       >
  //                         <svg
  //                           width="100%" height="100%" viewBox="0 0 280 140"
  //                           preserveAspectRatio="none"
  //                           style={{ position: "absolute", right: 0, top: 0 }}
  //                         >

                            
  //                           <path
  //                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
  //                             fill={theme.palette.accent?.main || theme.palette.secondary.light}
  //                           />
  //                         </svg>
  //                       </Box>
  //                       <Box sx={{ position: "relative", zIndex: 1 }}>
  //                         <Typography
  //                           sx={{
  //                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
  //                             fontWeight: 600,
  //                             color: alpha(theme.palette.primary.contrastText, 0.8),
  //                             textTransform: "uppercase",
  //                             lineHeight: 1.2,
  //                           }}
  //                         >
  //                           {card.label}
  //                         </Typography>
  //                       </Box>
  //                       <Box sx={{ position: "relative", zIndex: 1 }}>
  //                         <Typography
  //                           sx={{
  //                             fontSize: { xs: "1.75rem", md: "2.25rem" },
  //                             fontWeight: "bold",
  //                             color: "primary.contrastText", // Use theme color
  //                             lineHeight: 1,
  //                           }}
  //                         >
  //                           {card.value}
  //                         </Typography>
  //                       </Box>
  //                     </Box>
  //                   </Box>
  //                 ))}
  //           </Box>

  //           <Grid container spacing={{ xs: 2, md: 3 }} sx={{ display: 'flex' }}>
  //             {/* --- YOUR RECENT JOB POSTINGS (SIZE & LAYOUT PRESERVED) --- */}
  //             <Grid item xs={12} lg={8}>
  //               <Card
  //                 sx={{
  //                   borderRadius: 4, // Kept from original
  //                   boxShadow: "none", // Kept from original
  //                   p: { xs: 1, sm: 2 }, // Kept from original
  //                   backgroundColor: "mint.main", // CHANGED for theme
  //                   width: "100%", // Kept from original
  //                   height: "100%", // Kept from original
  //                 }}
  //               >
  //                 <CardContent>
  //                   <Stack
  //                     direction="row" justifyContent="space-between"
  //                     alignItems="center" mb={2}
  //                   >
  //                     <Typography
  //                       variant={isMobile ? "h6" : "h6"}
  //                       fontWeight="bold"
  //                     >
  //                       Your Recent Job Postings
  //                     </Typography>
  //                     {!loading && jobPostings.length > 5 && (
  //                       <Button
  //                         onClick={() => navigate("/gatep/employer/candidates")}
  //                         endIcon={<ArrowForward />}
  //                         color="primary" // Use theme color
  //                         sx={{ textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
  //                       >
  //                         View All Jobs
  //                       </Button>
  //                     )}
  //                   </Stack>
  //                   {error && <Alert severity="error">{error}</Alert>}
  //                   <TableContainer>
  //                     <Table sx={{ minWidth: isMobile ? 300 : 650 }}>
  //                       <TableHead
  //                         sx={{
  //                           "& .MuiTableCell-root": {
  //                             borderBottom: "none",
  //                             color: "text.secondary", // Use theme color
  //                             fontSize: { xs: "0.75rem", sm: "0.875rem" },
  //                           },
  //                         }}
  //                       >
  //                         <TableRow>
  //                           <TableCell>Position</TableCell>
  //                           <TableCell>Action</TableCell>
  //                         </TableRow>
  //                       </TableHead>
  //                       <TableBody>
  //                         {loading
  //                           ? renderJobSkeletons()
  //                           : jobsToDisplay.map((job) => (
  //                               <TableRow
  //                                 key={job.id} hover
  //                                 sx={{ "& td, & th": { border: 0, py: { xs: 1, sm: 1.5 } } }}
  //                               >
  //                                 <TableCell component="th" scope="row">
  //                                   <Box>
  //                                     <Typography
  //                                       variant={isMobile ? "body2" : "subtitle2"}
  //                                       fontWeight="bold"
  //                                     >
  //                                       {job.title}
  //                                     </Typography>
  //                                     <Stack
  //                                       direction="row" alignItems="center"
  //                                       color="text.secondary" // Use theme color
  //                                       spacing={0.5}
  //                                     >
  //                                       <LocationOn sx={{ fontSize: { xs: 14, sm: 16 } }} />
  //                                       <Typography variant={isMobile ? "caption" : "body2"}>
  //                                         {job.location}
  //                                       </Typography>
  //                                     </Stack>
  //                                   </Box>
  //                                 </TableCell>
  //                                 <TableCell>
  //                                   <IconButton size="small" onClick={() => handleOpenModal(job)}>
  //                                     <Visibility
  //                                       sx={{
  //                                         color: "text.secondary", // Use theme color
  //                                         fontSize: { xs: 18, sm: 24 },
  //                                       }}
  //                                     />
  //                                   </IconButton>
  //                                 </TableCell>
  //                               </TableRow>
  //                             ))}
  //                         {!loading && !error && jobPostings.length === 0 && (
  //                           <TableRow>
  //                             <TableCell colSpan={4} align="center" sx={{ py: { xs: 2, sm: 3 } }}>
  //                               No jobs posted yet.
  //                             </TableCell>
  //                           </TableRow>
  //                         )}
  //                       </TableBody>
  //                     </Table>
  //                   </TableContainer>
  //                 </CardContent>
  //               </Card>
  //             </Grid>

  //             {/* --- TOP MATCHED CANDIDATES (SIZE & LAYOUT PRESERVED) --- */}
  //             <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
  //               <Card
  //                 sx={{
  //                   borderRadius: 4, // Kept from original
  //                   boxShadow: "none", // Kept from original
  //                   p: { xs: 1, sm: 2 }, // Kept from original
  //                   backgroundColor: "mint.main", // CHANGED for theme
  //                   height: "100%", // Kept from original
  //                   width: { xs: '100%', md: '630px' }, // Kept from original
  //                 }}
  //               >
  //                 <CardContent sx={{ display: "flex", flexDirection: "column" }}>
  //                   <Typography
  //                     variant={isMobile ? "h6" : "h6"}
  //                     gutterBottom
  //                     fontWeight="bold"
  //                   >
  //                     Top Matched Candidates
  //                   </Typography>
  //                   {candidatesLoading ? (
  //                     <Stack spacing={2}>{renderCandidateSkeletons()}</Stack>
  //                   ) : error ? (
  //                     <Alert severity="warning">Could not load candidate data.</Alert>
  //                   ) : topCandidates.length > 0 ? (
  //                     <Box
  //                       sx={{
  //                         flexGrow: 1, overflowY: "auto",
  //                         maxHeight: { xs: "300px", sm: "450px" }, pr: 1,
  //                         // Using global scrollbar from theme
  //                       }}
  //                     >
  //                       <Stack spacing={2}>
  //                         {topCandidates.map((candidate) => (
  //                           <Paper
  //                             key={candidate.id}
  //                             sx={{
  //                               p: { xs: 1.5, sm: 2 },
  //                               borderRadius: 3,
  //                               boxShadow: "none",
  //                               border: `1px solid ${theme.palette.divider}`,
  //                               backgroundColor: "background.paper", // Use theme color
  //                             }}
  //                           >
  //                             <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
  //                               <Avatar
  //                                 sx={{
  //                                   bgcolor: "accent.main", // Use theme accent color
  //                                   color: "accent.contrastText", // Use corresponding contrast text
  //                                   fontWeight: "bold",
  //                                   width: { xs: 32, sm: 40 },
  //                                   height: { xs: 32, sm: 40 },
  //                                   fontSize: { xs: "0.875rem", sm: "1rem" },
  //                                 }}
  //                               >
  //                                 {candidate.applicant_name.charAt(0).toUpperCase()}
  //                               </Avatar>
  //                               <Box flexGrow={1}>
  //                                 <Typography
  //                                   variant={isMobile ? "subtitle2" : "subtitle1"}
  //                                   fontWeight="bold"
  //                                 >
  //                                   {candidate.applicant_name}
  //                                 </Typography>
  //                                 <Typography
  //                                   variant={isMobile ? "caption" : "body2"}
  //                                   color="text.secondary" // Use theme color
  //                                 >
  //                                   {candidate.location}
  //                                 </Typography>
  //                               </Box>
  //                             </Stack>
  //                             <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={2}>
  //                               <Button
  //                                 size="small" variant="contained" color="primary"
  //                                 sx={{
  //                                   flex: 1,
  //                                   borderRadius: 2,
  //                                   fontSize: { xs: "0.75rem", sm: "0.875rem" },
  //                                   py: { xs: 0.75, sm: 1 },
  //                                 }}
  //                                 onClick={() => handleViewResume(candidate.resume_url)}
  //                               >
  //                                 View Resume   
  //                               </Button>
  //                               <Button
  //                                 size="small" variant="outlined" color="primary"
  //                                 sx={{
  //                                   flex: 1,
  //                                   borderRadius: 2,
  //                                   "&:hover": {
  //                                     backgroundColor: alpha(theme.palette.primary.main, 0.08)
  //                                   },
  //                                   fontSize: { xs: "0.75rem", sm: "0.875rem" },
  //                                   py: { xs: 0.75, sm: 1 },
  //                                 }}
  //                                 onClick={handleScheduleInterview}
  //                               >
  //                                 Schedule Interview
  //                               </Button>
  //                             </Stack>
  //                           </Paper>
  //                         ))}
  //                       </Stack>
  //                     </Box>
  //                   ) : (
  //                     <Typography
  //                       variant="body2" color="text.secondary"
  //                       sx={{ textAlign: "center", py: { xs: 2, sm: 3 } }}
  //                     >
  //                       No potential candidates found.
  //                     </Typography>
  //                   )}
  //                 </CardContent>
  //               </Card>
  //             </Grid>
  //           </Grid>
  //         </Stack>

  //         {/* --- DIALOG MODAL (REFACTORED FOR THEME) --- */}
  //         {selectedJob && (
  //           <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
  //             <DialogTitle sx={{ pb: 1, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
  //               Job Details
  //               <IconButton
  //                 onClick={handleCloseModal}
  //                 sx={{ position: "absolute", right: { xs: 8, sm: 16 }, top: { xs: 8, sm: 12 } }}
  //               >
  //                 <Close />
  //               </IconButton>
  //             </DialogTitle>
  //             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
  //               {modalError && <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>}
  //               <Grid container spacing={3}>
  //                 <Grid item xs={12}>
  //                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Role</Typography>
  //                   {isEditing ? (
  //                     <TextField fullWidth name="title" value={editedData.title} onChange={handleInputChange} size={isMobile ? "small" : "medium"} />
  //                   ) : (
  //                     <Typography variant={isMobile ? "body1" : "h6"}>{selectedJob.title}</Typography>
  //                   )}
  //                 </Grid>
  //                 <Grid item xs={12} sm={6}>
  //                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Location</Typography>
  //                   {isEditing ? (
  //                     <TextField fullWidth name="location" value={editedData.location} onChange={handleInputChange} size={isMobile ? "small" : "medium"} />
  //                   ) : (
  //                     <Typography variant={isMobile ? "body2" : "body1"}>{selectedJob.location}</Typography>
  //                   )}
  //                 </Grid>
  //                 <Grid item xs={12} sm={6}>
  //                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Posted Date</Typography>
  //                   <Typography variant={isMobile ? "body2" : "body1"}>
  //                     {new Date(selectedJob.created_at).toLocaleDateString()}
  //                   </Typography>
  //                 </Grid>
  //                 <Grid item xs={12}>
  //                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>Required Skills</Typography>
  //                   {isEditing ? (
  //                     <Autocomplete
  //                       multiple freeSolo options={[]}
  //                       value={editedData.required_skills || []}
  //                       onChange={handleSkillsChange}
  //                       renderTags={(value, getTagProps) =>
  //                         value.map((option, index) => (
  //                           <Chip variant="outlined" label={option} {...getTagProps({ index })} size={isMobile ? "small" : "medium"} />
  //                         ))
  //                       }
  //                       renderInput={(params) => (
  //                         <TextField {...params} variant="outlined" placeholder="Add skills" size={isMobile ? "small" : "medium"} />
  //                       )}
  //                     />
  //                   ) : (
  //                     <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
  //                       {(selectedJob.required_skills || []).map((skill) => (
  //                         <Chip key={skill} label={skill} size={isMobile ? "small" : "medium"} />
  //                       ))}
  //                     </Box>
  //                   )}
  //                 </Grid>
  //               </Grid>
  //             </DialogContent>
  //             <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
  //               {isEditing ? (
  //                 <>
  //                   <Button onClick={handleCancelEdit} color="secondary" size={isMobile ? "small" : "medium"}>Cancel</Button>
  //                   <Button
  //                     onClick={handleSaveChanges} variant="contained"
  //                     startIcon={modalLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
  //                     disabled={modalLoading} size={isMobile ? "small" : "medium"}
  //                   >
  //                     Save
  //                   </Button>
  //                 </>
  //               ) : (
  //                 <>
  //                   <Button onClick={() => setIsEditing(true)} variant="outlined" startIcon={<Edit />} size={isMobile ? "small" : "medium"}>Edit</Button>
  //                   <Button onClick={handleCloseModal} size={isMobile ? "small" : "medium"}>Close</Button>
  //                 </>
  //               )}
  //             </DialogActions>
  //           </Dialog>
  //         )}
  //       </Box>
  //     </Layout>
  //   );
  // };

  // export default EmployerDashboard;




// "use client";

// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
//   useTheme,
//   useMediaQuery,
// } from "@mui/material";
// import { alpha } from "@mui/material/styles";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const EmployerDashboard = () => {
//   const navigate = useNavigate();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   const topCardsData = [
//     { label: "ACTIVE JOBS", value: stats.activeJobs },
//     { label: "TOTAL APPLICATIONS", value: stats.totalApplications },
//     { label: "INTERVIEWS SCHEDULED", value: stats.interviewsScheduled },
//     { label: "OFFERS EXTENDED", value: stats.offersExtended },
//   ];

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const [jobResponse, statsResponse] = await Promise.all([
//           apiClient.get("/job-postings/"),
//           apiClient.get("/employer-dashboard/"),
//         ]);

//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);

//         const dashboardStats = statsResponse.data;
//         setStats({
//           activeJobs: dashboardStats.active_jobs ?? 0,
//           totalApplications: dashboardStats.total_applicants ?? 0,
//           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
//           offersExtended: dashboardStats.offer_extended ?? 0,
//         });

//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience",
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//           setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ "& td, & th": { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: "1rem" }} />
//             <Skeleton
//               variant="text"
//               width="50%"
//               sx={{ fontSize: "0.8rem" }}
//             />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton
//             variant="rounded"
//             width={60}
//             height={22}
//             sx={{ borderRadius: "999px" }}
//           />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//       <Paper
//         key={index}
//         sx={{
//           p: 2,
//           borderRadius: 3,
//           boxShadow: "none",
//           border: "1px solid #F3F4F6",
//           backgroundColor: "#FFFFFF",
//         }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: "1rem" }} width="80%" />
//             <Skeleton
//               variant="text"
//               sx={{ fontSize: "0.875rem" }}
//               width="50%"
//             />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//           <Skeleton
//             variant="rounded"
//             height={36}
//             sx={{ flex: 1, borderRadius: 2 }}
//           />
//           <Skeleton
//             variant="rounded"
//             height={36}
//             sx={{ flex: 1, borderRadius: 2 }}
//           />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: "calc(100vh - 64px)",
//           overflowY: "auto",
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Stack direction="column" spacing={{ xs: 2, md: 3 }}>
//           <Paper
//             sx={{
//               p: 3,
//               backgroundColor: "#F1FFFA",
//               borderRadius: "12px",
//             }}
//           >
//             <Box>
//               <Typography
//                 variant={isMobile ? "h6" : "h5"}
//                 sx={{ mb: 2, fontWeight: "bold" }}
//               >
//                 Quick Actions
//               </Typography>
//               <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
//                 <Button
//                   variant="contained"
//                   startIcon={<PostAdd />}
//                   onClick={() => navigate("/gatep/employer/post-job")}
//                   sx={{
//                     backgroundColor: "#4F46BA",
//                     textTransform: "none",
//                     borderRadius: 2,
//                     boxShadow: "0 4px 12px 0 rgba(79,70,186,0.3)",
//                     "&:hover": { backgroundColor: "#40389e" },
//                     width: isMobile ? "100%" : "auto",
//                   }}
//                 >
//                   Post a New Job
//                 </Button>
//                 <Button
//                   variant="outlined"
//                   startIcon={<People />}
//                   onClick={() => navigate("/gatep/employer/candidates")}
//                   sx={{
//                     borderColor: "#E0E0E0",
//                     color: "#4F46BA",
//                     textTransform: "none",
//                     borderRadius: 2,
//                     backgroundColor: "#FFFFFF",
//                     width: isMobile ? "100%" : "auto",
//                   }}
//                 >
//                   Manage Candidates
//                 </Button>
//                 <Button
//                   variant="outlined"
//                   startIcon={<Schedule />}
//                   onClick={() => navigate("/gatep/employer/interviews")}
//                   sx={{
//                     borderColor: "#E0E0E0",
//                     color: "#4F46BA",
//                     textTransform: "none",
//                     borderRadius: 2,
//                     backgroundColor: "#FFFFFF",
//                     width: isMobile ? "100%" : "auto",
//                   }}
//                 >
//                   Scheduled Interviews
//                 </Button>
//               </Stack>
//             </Box>
//           </Paper>

//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: { xs: "column", sm: "row" },
//               gap: { xs: 2, md: 3 },
//             }}
//           >
//             {loading
//               ? [...Array(4)].map((_, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={140}
//                       sx={{ borderRadius: "12px" }}
//                     />
//                   </Box>
//                 ))
//               : topCardsData.map((card, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Box
//                       sx={{
//                         width: "100%",
//                         height: 140,
//                         backgroundColor: theme.palette.primary.main,
//                         borderRadius: "12px",
//                         boxShadow: theme.shadows[3],
//                         position: "relative",
//                         overflow: "hidden",
//                         p: { xs: 1.5, sm: 2, md: 2.5 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           top: 0,
//                           right: 0,
//                           width: "100%",
//                           height: "100%",
//                           zIndex: 0,
//                         }}
//                       >
//                         <svg
//                           width="100%"
//                           height="100%"
//                           viewBox="0 0 280 140"
//                           preserveAspectRatio="none"
//                           style={{ position: "absolute", right: 0, top: 0 }}
//                         >
//                           <path
//                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
//                             fill={
//                               theme.palette.accent?.main ||
//                               theme.palette.secondary.light
//                             }
//                           />
//                         </svg>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
//                             fontWeight: 600,
//                             color: alpha(
//                               theme.palette.primary.contrastText,
//                               0.8
//                             ),
//                             textTransform: "uppercase",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {card.label}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "1.75rem", md: "2.25rem" },
//                             fontWeight: "bold",
//                             color: theme.palette.primary.contrastText,
//                             lineHeight: 1,
//                           }}
//                         >
//                           {card.value}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 ))}
//           </Box>

//           <Grid container spacing={{ xs: 2, md: 3 }} sx={{ display: 'flex' }}>
//             <Grid item xs={12} lg={8}>
//               <Card
//                 sx={{
//                   borderRadius: 4,
//                   boxShadow: "none",
//                   p: { xs: 1, sm: 2 },
//                   backgroundColor: "#F1FFFA",
//                   width: "100%",
//                   height: "100%",
//                 }}
//               >
//                 <CardContent>
//                   <Stack
//                     direction="row"
//                     justifyContent="space-between"
//                     alignItems="center"
//                     mb={2}
//                   >
//                     <Typography
//                       variant={isMobile ? "h6" : "h6"}
//                       fontWeight="bold"
//                     >
//                       Your Recent Job Postings
//                     </Typography>
//                     {!loading && jobPostings.length > 5 && (
//                       <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                         sx={{
//                           color: "#4F46BA",
//                           textTransform: "none",
//                           fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                         }}
//                       >
//                         View All Jobs
//                       </Button>
//                     )}
//                   </Stack>
//                   {error && <Alert severity="error">{error}</Alert>}
//                   <TableContainer>
//                     <Table
//                       aria-label="recent job postings"
//                       sx={{ minWidth: isMobile ? 300 : 650 }}
//                     >
//                       <TableHead
//                         sx={{
//                           "& .MuiTableCell-root": {
//                             borderBottom: "none",
//                             color: "#A9A5A5",
//                             fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                           },
//                         }}
//                       >
//                         <TableRow>
//                           <TableCell>Position</TableCell>
//                           <TableCell>Action</TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {loading
//                           ? renderJobSkeletons()
//                           : jobsToDisplay.map((job) => (
//                               <TableRow
//                                 key={job.id}
//                                 hover
//                                 sx={{
//                                   "& td, & th": {
//                                     border: 0,
//                                     py: { xs: 1, sm: 1.5 },
//                                   },
//                                 }}
//                               >
//                                 <TableCell component="th" scope="row">
//                                   <Box>
//                                     <Typography
//                                       variant={
//                                         isMobile ? "body2" : "subtitle2"
//                                       }
//                                       fontWeight="bold"
//                                     >
//                                       {job.title}
//                                     </Typography>
//                                     <Stack
//                                       direction="row"
//                                       alignItems="center"
//                                       color="#A9A5A5"
//                                       spacing={0.5}
//                                     >
//                                       <LocationOn
//                                         sx={{ fontSize: { xs: 14, sm: 16 } }}
//                                       />
//                                       <Typography
//                                         variant={
//                                           isMobile ? "caption" : "body2"
//                                         }
//                                       >
//                                         {job.location}
//                                       </Typography>
//                                     </Stack>
//                                   </Box>
//                                 </TableCell>
//                                 <TableCell>
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleOpenModal(job)}
//                                   >
//                                     <Visibility
//                                       sx={{
//                                         color: "#A9A5A5",
//                                         fontSize: { xs: 18, sm: 24 },
//                                       }}
//                                     />
//                                   </IconButton>
//                                 </TableCell>
//                               </TableRow>
//                             ))}
//                         {!loading && !error && jobPostings.length === 0 && (
//                           <TableRow>
//                             <TableCell
//                               colSpan={4}
//                               align="center"
//                               sx={{ py: { xs: 2, sm: 3 } }}
//                             >
//                               No jobs posted yet.
//                             </TableCell>
//                           </TableRow>
//                         )}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                 </CardContent>
//               </Card>
//             </Grid>

//             {/* <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
//               <Card
//                 sx={{
//                   borderRadius: 4,
//                   boxShadow: "none",
//                   p: { xs: 1, sm: 2 },
//                   backgroundColor: "#F1FFFA",
//                   height: "100%",
//                   width: '650px'
//                 }}
//               >
//                 <CardContent sx={{ display: "flex", flexDirection: "column" }}>
//                   <Typography
//                     variant={isMobile ? "h6" : "h6"}
//                     gutterBottom
//                     fontWeight="bold"
//                   >
//                     Top Matched Candidates
//                   </Typography>
//                   {candidatesLoading ? (
//                     <Stack spacing={2}>{renderCandidateSkeletons()}</Stack>
//                   ) : error ? (
//                     <Alert severity="warning">
//                       Could not load candidate data.
//                     </Alert>
//                   ) : topCandidates.length > 0 ? (
//                     <Box
//                       sx={{
//                         flexGrow: 1,
//                         overflowY: "auto",
//                         maxHeight: { xs: "300px", sm: "450px" },
//                         pr: 1,
//                         "&::-webkit-scrollbar": {
//                           width: "6px",
//                         },
//                         "&::-webkit-scrollbar-track": {
//                           background: "transparent",
//                           borderRadius: "10px",
//                         },
//                         "&::-webkit-scrollbar-thumb": {
//                           background: "#d3d3d3",
//                           borderRadius: "10px",
//                         },
//                         "&::-webkit-scrollbar-thumb:hover": {
//                           background: "#a9a9a9",
//                         },
//                       }}
//                     >
//                       <Stack spacing={2}>
//                         {topCandidates.map((candidate) => (
//                           <Paper
//                             key={candidate.id}
//                             sx={{
//                               p: { xs: 1.5, sm: 2 },
//                               borderRadius: 3,
//                               boxShadow: "none",
//                               border: "1px solid #F3F4F6",
//                               backgroundColor: "#FFFFFF",
//                             }}
//                           >
//                             <Stack
//                               direction="row"
//                               spacing={2}
//                               alignItems="center"
//                               mb={1.5}
//                             >
//                               <Avatar
//                                 sx={{
//                                   bgcolor: "#FDDBB1",
//                                   color: "#B54708",
//                                   fontWeight: "bold",
//                                   width: { xs: 32, sm: 40 },
//                                   height: { xs: 32, sm: 40 },
//                                   fontSize: { xs: "0.875rem", sm: "1rem" },
//                                 }}
//                               >
//                                 {candidate.applicant_name
//                                   .charAt(0)
//                                   .toUpperCase()}
//                               </Avatar>
//                               <Box flexGrow={1}>
//                                 <Typography
//                                   variant={
//                                     isMobile ? "subtitle2" : "subtitle1"
//                                   }
//                                   fontWeight="bold"
//                                 >
//                                   {candidate.applicant_name}
//                                 </Typography>
//                                 <Typography
//                                   variant={isMobile ? "caption" : "body2"}
//                                   color="#A9A5A5"
//                                 >
//                                   {candidate.location}
//                                 </Typography>
//                               </Box>
//                             </Stack>
//                             <Stack
//                               direction={{ xs: "column", sm: "row" }}
//                               spacing={1}
//                               mt={2}
//                             >
//                               <Button
//                                 size="small"
//                                 variant="contained"
//                                 sx={{
//                                   backgroundColor: "#4F46BA",
//                                   flex: 1,
//                                   textTransform: "none",
//                                   borderRadius: 2,
//                                   "&:hover": { backgroundColor: "#40389e" },
//                                   fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                                   py: { xs: 0.75, sm: 1 },
//                                 }}
//                                 onClick={() =>
//                                   handleViewResume(candidate.resume_url)
//                                 }
//                               >
//                                 View Profile
//                               </Button>
//                               <Button
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{
//                                   borderColor: "#4F46BA",
//                                   backgroundColor: "transparent",
//                                   color: "#4F46BA",
//                                   flex: 1,
//                                   textTransform: "none",
//                                   borderRadius: 2,
//                                   "&:hover": {
//                                     backgroundColor: "#e6f2ee",
//                                     borderColor: "#4F46BA",
//                                   },
//                                   fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                                   py: { xs: 0.75, sm: 1 },
//                                 }}
//                                 onClick={handleScheduleInterview}
//                               >
//                                 Schedule Interview
//                               </Button>
//                             </Stack>
//                           </Paper>
//                         ))}
//                       </Stack>
//                     </Box>
//                   ) : (
//                     <Typography
//                       variant="body2"
//                       color="#A9A5A5"
//                       sx={{ textAlign: "center", py: { xs: 2, sm: 3 } }}
//                     >
//                       No potential candidates found.
//                     </Typography>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid> */}

//           <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
//   <Card
//     sx={{
//       borderRadius: 4,
//       boxShadow: "none",
//       p: { xs: 1, sm: 2 },
//       backgroundColor: "#F1FFFA",
//       height: "100%",
//       width: { xs: '100%', md: '650px' }, // Responsive width
//     }}
//   >
//     <CardContent sx={{ display: "flex", flexDirection: "column" }}>
//       <Typography
//         variant={isMobile ? "h6" : "h6"}
//         gutterBottom
//         fontWeight="bold"
//       >
//         Top Matched Candidates
//       </Typography>
//       {candidatesLoading ? (
//         <Stack spacing={2}>{renderCandidateSkeletons()}</Stack>
//       ) : error ? (
//         <Alert severity="warning">
//           Could not load candidate data.
//         </Alert>
//       ) : topCandidates.length > 0 ? (
//         <Box
//           sx={{
//             flexGrow: 1,
//             overflowY: "auto",
//             maxHeight: { xs: "300px", sm: "450px" },
//             pr: 1,
//             "&::-webkit-scrollbar": {
//               width: "6px",
//             },
//             "&::-webkit-scrollbar-track": {
//               background: "transparent",
//               borderRadius: "10px",
//             },
//             "&::-webkit-scrollbar-thumb": {
//               background: "#d3d3d3",
//               borderRadius: "10px",
//             },
//             "&::-webkit-scrollbar-thumb:hover": {
//               background: "#a9a9a9",
//             },
//           }}
//         >
//           <Stack spacing={2}>
//             {topCandidates.map((candidate) => (
//               <Paper
//                 key={candidate.id}
//                 sx={{
//                   p: { xs: 1.5, sm: 2 },
//                   borderRadius: 3,
//                   boxShadow: "none",
//                   border: "1px solid #F3F4F6",
//                   backgroundColor: "#FFFFFF",
//                 }}
//               >
//                 <Stack
//                   direction="row"
//                   spacing={2}
//                   alignItems="center"
//                   mb={1.5}
//                 >
//                   <Avatar
//                     sx={{
//                       bgcolor: "#FDDBB1",
//                       color: "#B54708",
//                       fontWeight: "bold",
//                       width: { xs: 32, sm: 40 },
//                       height: { xs: 32, sm: 40 },
//                       fontSize: { xs: "0.875rem", sm: "1rem" },
//                     }}
//                   >
//                     {candidate.applicant_name
//                       .charAt(0)
//                       .toUpperCase()}
//                   </Avatar>
//                   <Box flexGrow={1}>
//                     <Typography
//                       variant={
//                         isMobile ? "subtitle2" : "subtitle1"
//                       }
//                       fontWeight="bold"
//                     >
//                       {candidate.applicant_name}
//                     </Typography>
//                     <Typography
//                       variant={isMobile ? "caption" : "body2"}
//                       color="#A9A5A5"
//                     >
//                       {candidate.location}
//                     </Typography>
//                   </Box>
//                 </Stack>
//                 <Stack
//                   direction={{ xs: "column", sm: "row" }}
//                   spacing={1}
//                   mt={2}
//                 >
//                   <Button
//                     size="small"
//                     variant="contained"
//                     sx={{
//                       backgroundColor: "#4F46BA",
//                       flex: 1,
//                       textTransform: "none",
//                       borderRadius: 2,
//                       "&:hover": { backgroundColor: "#40389e" },
//                       fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                       py: { xs: 0.75, sm: 1 },
//                     }}
//                     onClick={() =>
//                       handleViewResume(candidate.resume_url)
//                     }
//                   >
//                     View Profile
//                   </Button>
//                   <Button
//                     size="small"
//                     variant="outlined"
//                     sx={{
//                       borderColor: "#4F46BA",
//                       backgroundColor: "transparent",
//                       color: "#4F46BA",
//                       flex: 1,
//                       textTransform: "none",
//                       borderRadius: 2,
//                       "&:hover": {
//                         backgroundColor: "#e6f2ee",
//                         borderColor: "#4F46BA",
//                       },
//                       fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                       py: { xs: 0.75, sm: 1 },
//                     }}
//                     onClick={handleScheduleInterview}
//                   >
//                     Schedule Interview
//                   </Button>
//                 </Stack>
//               </Paper>
//             ))}
//           </Stack>
//         </Box>
//       ) : (
//         <Typography
//           variant="body2"
//           color="#A9A5A5"
//           sx={{ textAlign: "center", py: { xs: 2, sm: 3 } }}
//         >
//           No potential candidates found.
//         </Typography>
//       )}
//     </CardContent>
//   </Card>
// </Grid>


//           </Grid>
//         </Stack>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle
//               sx={{ pb: 1, fontSize: { xs: "1rem", sm: "1.25rem" } }}
//             >
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{
//                   position: "absolute",
//                   right: { xs: 8, sm: 16 },
//                   top: { xs: 8, sm: 12 },
//                 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body1" : "h6"}>
//                       {selectedJob.title}
//                     </Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body2" : "body1"}>
//                       {selectedJob.location}
//                     </Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Posted Date
//                   </Typography>
//                   <Typography variant={isMobile ? "body2" : "body1"}>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                             size={isMobile ? "small" : "medium"}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                           size={isMobile ? "small" : "medium"}
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{
//                         display: "flex",
//                         flexWrap: "wrap",
//                         gap: 1,
//                         mt: 1,
//                       }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip
//                           key={skill}
//                           label={skill}
//                           size={isMobile ? "small" : "medium"}
//                         />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
//               {isEditing ? (
//                 <>
//                   <Button
//                     onClick={handleCancelEdit}
//                     color="secondary"
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Edit
//                   </Button>
//                   <Button
//                     onClick={handleCloseModal}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Close
//                   </Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;















// "use client";

// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
//   useTheme,
//   useMediaQuery,
// } from "@mui/material";
// import { alpha } from "@mui/material/styles";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const EmployerDashboard = () => {
//   const navigate = useNavigate();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   const topCardsData = [
//     { label: "ACTIVE JOBS", value: stats.activeJobs },
//     { label: "TOTAL APPLICATIONS", value: stats.totalApplications },
//     { label: "INTERVIEWS SCHEDULED", value: stats.interviewsScheduled },
//     { label: "OFFERS EXTENDED", value: stats.offersExtended },
//   ];

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const [jobResponse, statsResponse] = await Promise.all([
//           apiClient.get("/job-postings/"),
//           apiClient.get("/employer-dashboard/"),
//         ]);

//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);

//         const dashboardStats = statsResponse.data;
//         setStats({
//           activeJobs: dashboardStats.active_jobs ?? 0,
//           totalApplications: dashboardStats.total_applicants ?? 0,
//           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
//           offersExtended: dashboardStats.offer_extended ?? 0,
//         });

//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience",
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//           setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ "& td, & th": { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: "1rem" }} />
//             <Skeleton
//               variant="text"
//               width="50%"
//               sx={{ fontSize: "0.8rem" }}
//             />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton
//             variant="rounded"
//             width={60}
//             height={22}
//             sx={{ borderRadius: "999px" }}
//           />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//       <Paper
//         key={index}
//         sx={{
//           p: 2,
//           borderRadius: 3,
//           boxShadow: "none",
//           border: "1px solid #F3F4F6",
//           backgroundColor: "#FFFFFF",
//         }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: "1rem" }} width="80%" />
//             <Skeleton
//               variant="text"
//               sx={{ fontSize: "0.875rem" }}
//               width="50%"
//             />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//           <Skeleton
//             variant="rounded"
//             height={36}
//             sx={{ flex: 1, borderRadius: 2 }}
//           />
//           <Skeleton
//             variant="rounded"
//             height={36}
//             sx={{ flex: 1, borderRadius: 2 }}
//           />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: "calc(100vh - 64px)",
//           overflowY: "auto",
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Stack direction="column" spacing={{ xs: 2, md: 3 }}>
//           <Paper
//             sx={{
//               p: 3,
//               backgroundColor: "#F1FFFA",
//               borderRadius: "12px",
//             }}
//           >
//             <Box>
//               <Typography
//                 variant={isMobile ? "h6" : "h5"}
//                 sx={{ mb: 2, fontWeight: "bold" }}
//               >
//                 Quick Actions
//               </Typography>
//               <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
//                 <Button
//                   variant="contained"
//                   startIcon={<PostAdd />}
//                   onClick={() => navigate("/gatep/employer/post-job")}
//                   sx={{
//                     backgroundColor: "#4F46BA",
//                     textTransform: "none",
//                     borderRadius: 2,
//                     boxShadow: "0 4px 12px 0 rgba(79,70,186,0.3)",
//                     "&:hover": { backgroundColor: "#40389e" },
//                     width: isMobile ? "100%" : "auto",
//                   }}
//                 >
//                   Post a New Job
//                 </Button>
//                 <Button
//                   variant="outlined"
//                   startIcon={<People />}
//                   onClick={() => navigate("/gatep/employer/candidates")}
//                   sx={{
//                     borderColor: "#E0E0E0",
//                     color: "#4F46BA",
//                     textTransform: "none",
//                     borderRadius: 2,
//                     backgroundColor: "#FFFFFF",
//                     width: isMobile ? "100%" : "auto",
//                   }}
//                 >
//                   Manage Candidates
//                 </Button>
//                 <Button
//                   variant="outlined"
//                   startIcon={<Schedule />}
//                   onClick={() => navigate("/gatep/employer/interviews")}
//                   sx={{
//                     borderColor: "#E0E0E0",
//                     color: "#4F46BA",
//                     textTransform: "none",
//                     borderRadius: 2,
//                     backgroundColor: "#FFFFFF",
//                     width: isMobile ? "100%" : "auto",
//                   }}
//                 >
//                   Scheduled Interviews
//                 </Button>
//               </Stack>
//             </Box>
//           </Paper>

//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: { xs: "column", sm: "row" },
//               gap: { xs: 2, md: 3 },
//             }}
//           >
//             {loading
//               ? [...Array(4)].map((_, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={140}
//                       sx={{ borderRadius: "12px" }}
//                     />
//                   </Box>
//                 ))
//               : topCardsData.map((card, index) => (
//                   <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                     <Box
//                       sx={{
//                         width: "100%",
//                         height: 140,
//                         backgroundColor: theme.palette.primary.main,
//                         borderRadius: "12px",
//                         boxShadow: theme.shadows[3],
//                         position: "relative",
//                         overflow: "hidden",
//                         p: { xs: 1.5, sm: 2, md: 2.5 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           top: 0,
//                           right: 0,
//                           width: "100%",
//                           height: "100%",
//                           zIndex: 0,
//                         }}
//                       >
//                         <svg
//                           width="100%"
//                           height="100%"
//                           viewBox="0 0 280 140"
//                           preserveAspectRatio="none"
//                           style={{ position: "absolute", right: 0, top: 0 }}
//                         >
//                           <path
//                             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
//                             fill={
//                               theme.palette.accent?.main ||
//                               theme.palette.secondary.light
//                             }
//                           />
//                         </svg>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "0.65rem", sm: "0.75rem" },
//                             fontWeight: 600,
//                             color: alpha(
//                               theme.palette.primary.contrastText,
//                               0.8
//                             ),
//                             textTransform: "uppercase",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {card.label}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ position: "relative", zIndex: 1 }}>
//                         <Typography
//                           sx={{
//                             fontSize: { xs: "1.75rem", md: "2.25rem" },
//                             fontWeight: "bold",
//                             color: theme.palette.primary.contrastText,
//                             lineHeight: 1,
//                           }}
//                         >
//                           {card.value}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </Box>
//                 ))}
//           </Box>

//           <Grid container spacing={{ xs: 2, md: 3 }}>
//             <Grid item xs={12} lg={8}>
//               <Card
//                 sx={{
//                   borderRadius: 4,
//                   boxShadow: "none",
//                   p: { xs: 1, sm: 2 },
//                   backgroundColor: "#F1FFFA",
//                   width: "100%",
//                   height: "100%",
//                 }}
//               >
//                 <CardContent>
//                   <Stack
//                     direction="row"
//                     justifyContent="space-between"
//                     alignItems="center"
//                     mb={2}
//                   >
//                     <Typography
//                       variant={isMobile ? "h6" : "h6"}
//                       fontWeight="bold"
//                     >
//                       Your Recent Job Postings
//                     </Typography>
//                     {!loading && jobPostings.length > 5 && (
//                       <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                         sx={{
//                           color: "#4F46BA",
//                           textTransform: "none",
//                           fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                         }}
//                       >
//                         View All Jobs
//                       </Button>
//                     )}
//                   </Stack>
//                   {error && <Alert severity="error">{error}</Alert>}
//                   <TableContainer>
//                     <Table
//                       aria-label="recent job postings"
//                       sx={{ minWidth: isMobile ? 300 : 650 }}
//                     >
//                       <TableHead
//                         sx={{
//                           "& .MuiTableCell-root": {
//                             borderBottom: "none",
//                             color: "#A9A5A5",
//                             fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                           },
//                         }}
//                       >
//                         <TableRow>
//                           <TableCell>Position</TableCell>
//                           <TableCell>Action</TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {loading
//                           ? renderJobSkeletons()
//                           : jobsToDisplay.map((job) => (
//                               <TableRow
//                                 key={job.id}
//                                 hover
//                                 sx={{
//                                   "& td, & th": {
//                                     border: 0,
//                                     py: { xs: 1, sm: 1.5 },
//                                   },
//                                 }}
//                               >
//                                 <TableCell component="th" scope="row">
//                                   <Box>
//                                     <Typography
//                                       variant={
//                                         isMobile ? "body2" : "subtitle2"
//                                       }
//                                       fontWeight="bold"
//                                     >
//                                       {job.title}
//                                     </Typography>
//                                     <Stack
//                                       direction="row"
//                                       alignItems="center"
//                                       color="#A9A5A5"
//                                       spacing={0.5}
//                                     >
//                                       <LocationOn
//                                         sx={{ fontSize: { xs: 14, sm: 16 } }}
//                                       />
//                                       <Typography
//                                         variant={
//                                           isMobile ? "caption" : "body2"
//                                         }
//                                       >
//                                         {job.location}
//                                       </Typography>
//                                     </Stack>
//                                   </Box>
//                                 </TableCell>
//                                 <TableCell>
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleOpenModal(job)}
//                                   >
//                                     <Visibility
//                                       sx={{
//                                         color: "#A9A5A5",
//                                         fontSize: { xs: 18, sm: 24 },
//                                       }}
//                                     />
//                                   </IconButton>
//                                 </TableCell>
//                               </TableRow>
//                             ))}
//                         {!loading && !error && jobPostings.length === 0 && (
//                           <TableRow>
//                             <TableCell
//                               colSpan={4}
//                               align="center"
//                               sx={{ py: { xs: 2, sm: 3 } }}
//                             >
//                               No jobs posted yet.
//                             </TableCell>
//                           </TableRow>
//                         )}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} lg={4}>
//               <Card
//                 sx={{
//                   borderRadius: 4,
//                   boxShadow: "none",
//                   p: { xs: 1, sm: 2 },
//                   backgroundColor: "#F1FFFA",
//                   height: "100%",
//                 }}
//               >
//                 <CardContent sx={{ display: "flex", flexDirection: "column" }}>
//                   <Typography
//                     variant={isMobile ? "h6" : "h6"}
//                     gutterBottom
//                     fontWeight="bold"
//                   >
//                     Top Matched Candidates
//                   </Typography>
//                   {candidatesLoading ? (
//                     <Stack spacing={2}>{renderCandidateSkeletons()}</Stack>
//                   ) : error ? (
//                     <Alert severity="warning">
//                       Could not load candidate data.
//                     </Alert>
//                   ) : topCandidates.length > 0 ? (
//                     <Box
//                       sx={{
//                         flexGrow: 1,
//                         overflowY: "auto",
//                         maxHeight: { xs: "300px", sm: "450px" },
//                         pr: 1,
//                         "&::-webkit-scrollbar": {
//                           width: "6px",
//                         },
//                         "&::-webkit-scrollbar-track": {
//                           background: "transparent",
//                           borderRadius: "10px",
//                         },
//                         "&::-webkit-scrollbar-thumb": {
//                           background: "#d3d3d3",
//                           borderRadius: "10px",
//                         },
//                         "&::-webkit-scrollbar-thumb:hover": {
//                           background: "#a9a9a9",
//                         },
//                       }}
//                     >
//                       <Stack spacing={2}>
//                         {topCandidates.map((candidate) => (
//                           <Paper
//                             key={candidate.id}
//                             sx={{
//                               p: { xs: 1.5, sm: 2 },
//                               borderRadius: 3,
//                               boxShadow: "none",
//                               border: "1px solid #F3F4F6",
//                               backgroundColor: "#FFFFFF",
//                             }}
//                           >
//                             <Stack
//                               direction="row"
//                               spacing={2}
//                               alignItems="center"
//                               mb={1.5}
//                             >
//                               <Avatar
//                                 sx={{
//                                   bgcolor: "#FDDBB1",
//                                   color: "#B54708",
//                                   fontWeight: "bold",
//                                   width: { xs: 32, sm: 40 },
//                                   height: { xs: 32, sm: 40 },
//                                   fontSize: { xs: "0.875rem", sm: "1rem" },
//                                 }}
//                               >
//                                 {candidate.applicant_name
//                                   .charAt(0)
//                                   .toUpperCase()}
//                               </Avatar>
//                               <Box flexGrow={1}>
//                                 <Typography
//                                   variant={
//                                     isMobile ? "subtitle2" : "subtitle1"
//                                   }
//                                   fontWeight="bold"
//                                 >
//                                   {candidate.applicant_name}
//                                 </Typography>
//                                 <Typography
//                                   variant={isMobile ? "caption" : "body2"}
//                                   color="#A9A5A5"
//                                 >
//                                   {candidate.location}
//                                 </Typography>
//                               </Box>
//                             </Stack>
//                             <Stack
//                               direction={{ xs: "column", sm: "row" }}
//                               spacing={1}
//                               mt={2}
//                             >
//                               <Button
//                                 size="small"
//                                 variant="contained"
//                                 sx={{
//                                   backgroundColor: "#4F46BA",
//                                   flex: 1,
//                                   textTransform: "none",
//                                   borderRadius: 2,
//                                   "&:hover": { backgroundColor: "#40389e" },
//                                   fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                                   py: { xs: 0.75, sm: 1 },
//                                 }}
//                                 onClick={() =>
//                                   handleViewResume(candidate.resume_url)
//                                 }
//                               >
//                                 View Profile
//                               </Button>
//                               <Button
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{
//                                   borderColor: "#4F46BA",
//                                   backgroundColor: "transparent",
//                                   color: "#4F46BA",
//                                   flex: 1,
//                                   textTransform: "none",
//                                   borderRadius: 2,
//                                   "&:hover": {
//                                     backgroundColor: "#e6f2ee",
//                                     borderColor: "#4F46BA",
//                                   },
//                                   fontSize: { xs: "0.75rem", sm: "0.875rem" },
//                                   py: { xs: 0.75, sm: 1 },
//                                 }}
//                                 onClick={handleScheduleInterview}
//                               >
//                                 Schedule Interview
//                               </Button>
//                             </Stack>
//                           </Paper>
//                         ))}
//                       </Stack>
//                     </Box>
//                   ) : (
//                     <Typography
//                       variant="body2"
//                       color="#A9A5A5"
//                       sx={{ textAlign: "center", py: { xs: 2, sm: 3 } }}
//                     >
//                       No potential candidates found.
//                     </Typography>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         </Stack>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle
//               sx={{ pb: 1, fontSize: { xs: "1rem", sm: "1.25rem" } }}
//             >
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{
//                   position: "absolute",
//                   right: { xs: 8, sm: 16 },
//                   top: { xs: 8, sm: 12 },
//                 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body1" : "h6"}>
//                       {selectedJob.title}
//                     </Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body2" : "body1"}>
//                       {selectedJob.location}
//                     </Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Posted Date
//                   </Typography>
//                   <Typography variant={isMobile ? "body2" : "body1"}>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography
//                     variant="overline"
//                     color="text.secondary"
//                     sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
//                   >
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                             size={isMobile ? "small" : "medium"}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                           size={isMobile ? "small" : "medium"}
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{
//                         display: "flex",
//                         flexWrap: "wrap",
//                         gap: 1,
//                         mt: 1,
//                       }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip
//                           key={skill}
//                           label={skill}
//                           size={isMobile ? "small" : "medium"}
//                         />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
//               {isEditing ? (
//                 <>
//                   <Button
//                     onClick={handleCancelEdit}
//                     color="secondary"
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Edit
//                   </Button>
//                   <Button
//                     onClick={handleCloseModal}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Close
//                   </Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;





// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
//   useTheme,
//   useMediaQuery,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//             <svg
//             width="100"
//             height="150"
//             viewBox="0 0 80 120"
//             style={{
//               position: "absolute",
//               right: 0,
//               top: 0,
//             }}
//           >
//             <path d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z" fill="#F9B64C" />
//           </svg>
//   </Box>
// );

// const EmployerDashboard = () => {
//   const navigate = useNavigate();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const [jobResponse, statsResponse] = await Promise.all([
//           apiClient.get("/job-postings/"),
//           apiClient.get("/employer-dashboard/")
//         ]);

//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);

//         const dashboardStats = statsResponse.data;
//         setStats({
//           activeJobs: dashboardStats.active_jobs ?? 0,
//           totalApplications: dashboardStats.total_applicants ?? 0,
//           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
//           offersExtended: dashboardStats.offer_extended ?? 0,
//         });

//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience"
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err)
//       {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ '& td, & th': { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem' }} />
//             <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem' }} />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '999px' }} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//        <Paper
//         key={index}
//         sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="80%" />
//             <Skeleton variant="text" sx={{ fontSize: '0.875rem' }} width="50%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={{ xs: 2, md: 3 }}>
//            <Grid item xs={12}>
//             <Paper sx={{
//             p: 3,
//             mb: 3,
//             backgroundColor: '#F1FFFA',
//             borderRadius: "12px",
//           }}  >
//               <Box>
//                 <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 2, fontWeight: 'bold' }}>
//                   Quick Actions
//                 </Typography>
//                 <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
//                   <Button
//                     variant="contained"
//                     startIcon={<PostAdd />}
//                     onClick={() => navigate("/gatep/employer/post-job")}
//                     sx={{
//                       backgroundColor: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                       '&:hover': { backgroundColor: '#40389e' },
//                       width: isMobile ? '100%' : 'auto',
//                     }}
//                   >
//                     Post a New Job
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<People />}
//                     onClick={() => navigate("/gatep/employer/candidates")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF',
//                       width: isMobile ? '100%' : 'auto',
//                     }}
//                   >
//                     Manage Candidates
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<Schedule />}
//                     onClick={() => navigate("/gatep/employer/interviews")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF',
//                       width: isMobile ? '100%' : 'auto',
//                     }}
//                   >
//                     Scheduled Interviews
//                   </Button>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Grid>

//           {/* === START: REVISED CARD LAYOUT (GRID) === */}
//           <Grid item xs={12}>
//             <Grid container spacing={{ xs: 2, md: 3 }}>
//               {[
//                 { value: stats.activeJobs, label: "ACTIVE JOBS" },
//                 { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//                 { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//                 { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//               ].map((stat, index) => (
//                 <Grid item xs={12} sm={6} md={3} key={index}>
//                   <Card
//                     sx={{
//                       display: 'flex',
//                       flexDirection: 'column',
//                       backgroundColor: '#5A34C6',
//                       color: 'white',
//                       borderRadius: '20px',
//                       position: 'relative',
//                       overflow: 'hidden',
//                       boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                       height: '100%' // Ensures all cards in a row have the same height
//                     }}
//                   >
//                     <CardWave />
//                     <CardContent
//                       sx={{
//                         flexGrow: 1,
//                         display: 'flex',
//                         flexDirection: 'column',
//                         justifyContent: 'center',
//                         position: 'relative',
//                         zIndex: 1,
//                         p: { xs: 2, sm: 3 },
//                       }}
//                     >
//                       <Typography
//                         variant="body2"
//                         component="p"
//                         sx={{
//                           fontWeight: 'bold',
//                           color: '#A0A0C0',
//                           textTransform: 'uppercase',
//                           mb: 1,
//                           wordBreak: 'break-word',
//                         }}
//                       >
//                         {stat.label}
//                       </Typography>
//                       <Typography
//                         variant={isMobile ? "h4" : "h3"}
//                         component="p"
//                         sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                       >
//                         {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                       </Typography>
//                     </CardContent>
//                   </Card>
//                 </Grid>
//               ))}
//             </Grid>
//           </Grid>
//           {/* === END: REVISED CARD LAYOUT (GRID) === */}


//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: { xs: 1, sm: 2 }, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                   <Typography variant={isMobile ? "h6" : "h6"} fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                     <Button
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                       endIcon={<ArrowForward />}
//                       sx={{ color: '#4F46BA', textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
//                     >
//                       View All Jobs
//                     </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: isMobile ? 300 : 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5', fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         {/* <TableCell>Status</TableCell> */}
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: { xs: 1, sm: 1.5 } } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant={isMobile ? "body2" : "subtitle2"} fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: { xs: 14, sm: 16 } }} />
//                                     <Typography variant={isMobile ? "caption" : "body2"}>{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               {/* <TableCell>
//                                 <Chip
//                                   label="Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500,
//                                       fontSize: { xs: '0.65rem', sm: '0.75rem' }
//                                   }}
//                                 />
//                               </TableCell> */}
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5', fontSize: { xs: 18, sm: 24 } }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: { xs: 2, sm: 3 } }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//               {/* MODIFICATION: Removed height: '100%' to allow card to adapt to content height */}
//               <Card sx={{ borderRadius: 4, boxShadow: 'none', p: { xs: 1, sm: 2 }, backgroundColor: '#F1FFFA' }}>
//                 {/* MODIFICATION: Removed height: '100%' */}
//                 <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant={isMobile ? "h6" : "h6"} gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   <Stack spacing={2}>
//                     {renderCandidateSkeletons()}
//                   </Stack>
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     flexGrow: 1,
//                     overflowY: 'auto',
//                     maxHeight: { xs: '300px', sm: '450px' }, // maxHeight remains to handle long lists
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: 'transparent',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#d3d3d3',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#a9a9a9',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant={isMobile ? "caption" : "body2"} color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' },
//                                   fontSize: { xs: '0.75rem', sm: '0.875rem' },
//                                   py: { xs: 0.75, sm: 1 },
//                               }}
//                               onClick={() => handleViewResume(candidate.resume_url)}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   },
//                                   fontSize: { xs: '0.75rem', sm: '0.875rem' },
//                                   py: { xs: 0.75, sm: 1 },
//                               }}
//                               onClick={handleScheduleInterview}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: { xs: 8, sm: 16 }, top: { xs: 8, sm: 12 } }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body1" : "h6"}>{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body2" : "body1"}>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Posted Date
//                   </Typography>
//                   <Typography variant={isMobile ? "body2" : "body1"}>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                             size={isMobile ? "small" : "medium"}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                           size={isMobile ? "small" : "medium"}
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} size={isMobile ? "small" : "medium"} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary" size={isMobile ? "small" : "medium"}>
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal} size={isMobile ? "small" : "medium"}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;



// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
//   useTheme,
//   useMediaQuery,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//             <svg
//             width="100"
//             height="150"
//             viewBox="0 0 80 120"
//             style={{
//               position: "absolute",
//               right: 0,
//               top: 0,
//             }}
//           >
//             <path d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z" fill="#F9B64C" />
//           </svg>
//   </Box>
// );

// const EmployerDashboard = () => {
//   const navigate = useNavigate();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const [jobResponse, statsResponse] = await Promise.all([
//           apiClient.get("/job-postings/"),
//           apiClient.get("/employer-dashboard/")
//         ]);

//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);

//         const dashboardStats = statsResponse.data;
//         setStats({
//           activeJobs: dashboardStats.active_jobs ?? 0,
//           totalApplications: dashboardStats.total_applicants ?? 0,
//           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
//           offersExtended: dashboardStats.offer_extended ?? 0,
//         });

//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience"
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err)
//       {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ '& td, & th': { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem' }} />
//             <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem' }} />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '999px' }} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//        <Paper
//         key={index}
//         sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="80%" />
//             <Skeleton variant="text" sx={{ fontSize: '0.875rem' }} width="50%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={{ xs: 2, md: 3 }}>
//            <Grid item xs={12}>
//             <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#F1FFFA' }}>
//               <Box>
//                 <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 2, fontWeight: 'bold' }}>
//                   Quick Actions
//                 </Typography>
//                 <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
//                   <Button
//                     variant="contained"
//                     startIcon={<PostAdd />}
//                     onClick={() => navigate("/gatep/employer/post-job")}
//                     sx={{
//                       backgroundColor: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                       '&:hover': { backgroundColor: '#40389e' },
//                       width: isMobile ? '100%' : 'auto',
//                     }}
//                   >
//                     Post a New Job
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<People />}
//                     onClick={() => navigate("/gatep/employer/candidates")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF',
//                       width: isMobile ? '100%' : 'auto',
//                     }}
//                   >
//                     Manage Candidates
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<Schedule />}
//                     onClick={() => navigate("/gatep/employer/interviews")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF',
//                       width: isMobile ? '100%' : 'auto',
//                     }}
//                   >
//                     Scheduled Interviews
//                   </Button>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Grid>

//           {/* === START: REVISED CARD LAYOUT WITH HORIZONTAL SCROLL === */}
//           <Grid item xs={12}>
//             <Box
//               sx={{
//                 display: 'flex',
//                 overflowX: 'auto',
//                 py: 1, // Add some vertical padding for the scrollbar
//                 '&::-webkit-scrollbar': {
//                   height: '8px',
//                 },
//                 '&::-webkit-scrollbar-track': {
//                   background: '#f1f1f1',
//                   borderRadius: '10px',
//                 },
//                 '&::-webkit-scrollbar-thumb': {
//                   background: '#888',
//                   borderRadius: '10px',
//                 },
//                 '&::-webkit-scrollbar-thumb:hover': {
//                   background: '#555',
//                 },
//               }}
//             >
//               {[
//                 { value: stats.activeJobs, label: "ACTIVE JOBS" },
//                 { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//                 { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//                 { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//               ].map((stat, index) => (
//                 <Card
//                   key={index}
//                   sx={{
//                     minWidth: 250, // Fixed width for each card to enable scrolling
//                     flexShrink: 0, // Prevent cards from shrinking
//                     mx: 1.5, // Margin between cards
//                     display: 'flex',
//                     flexDirection: 'column',
//                     backgroundColor: '#5A34C6',
//                     color: 'white',
//                     borderRadius: '20px',
//                     position: 'relative',
//                     overflow: 'hidden',
//                     boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                   }}
//                 >
//                   <CardWave />
//                   <CardContent
//                     sx={{
//                       flexGrow: 1,
//                       display: 'flex',
//                       flexDirection: 'column',
//                       justifyContent: 'center',
//                       position: 'relative',
//                       zIndex: 1,
//                       p: { xs: 2, sm: 3 },
//                     }}
//                   >
//                     <Typography
//                       variant="body2"
//                       component="p"
//                       sx={{
//                         fontWeight: 'bold',
//                         color: '#A0A0C0',
//                         textTransform: 'uppercase',
//                         mb: 1,
//                         wordBreak: 'break-word', 
//                       }}
//                     >
//                       {stat.label}
//                     </Typography>
//                     <Typography
//                       variant={isMobile ? "h4" : "h3"}
//                       component="p"
//                       sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                     >
//                       {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               ))}
//             </Box>
//           </Grid>
//           {/* === END: REVISED CARD LAYOUT WITH HORIZONTAL SCROLL === */}

//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: { xs: 1, sm: 2 }, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                   <Typography variant={isMobile ? "h6" : "h6"} fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                     <Button
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                       endIcon={<ArrowForward />}
//                       sx={{ color: '#4F46BA', textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
//                     >
//                       View All Jobs
//                     </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: isMobile ? 300 : 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5', fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         {/* <TableCell>Status</TableCell> */}
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: { xs: 1, sm: 1.5 } } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant={isMobile ? "body2" : "subtitle2"} fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: { xs: 14, sm: 16 } }} />
//                                     <Typography variant={isMobile ? "caption" : "body2"}>{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               {/* <TableCell>
//                                 <Chip
//                                   label="Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500,
//                                       fontSize: { xs: '0.65rem', sm: '0.75rem' }
//                                   }}
//                                 />
//                               </TableCell> */}
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5', fontSize: { xs: 18, sm: 24 } }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: { xs: 2, sm: 3 } }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//               <Card sx={{ borderRadius: 4, boxShadow: 'none', p: { xs: 1, sm: 2 }, height: '100%',backgroundColor: '#F1FFFA' }}>
//                 <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant={isMobile ? "h6" : "h6"} gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   <Stack spacing={2}>
//                     {renderCandidateSkeletons()}
//                   </Stack>
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     flexGrow: 1,
//                     overflowY: 'auto',
//                     maxHeight: { xs: '300px', sm: '450px' },
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: 'transparent',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#d3d3d3',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#a9a9a9',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant={isMobile ? "caption" : "body2"} color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' },
//                                   fontSize: { xs: '0.75rem', sm: '0.875rem' },
//                                   py: { xs: 0.75, sm: 1 },
//                               }}
//                               onClick={() => handleViewResume(candidate.resume_url)}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   },
//                                   fontSize: { xs: '0.75rem', sm: '0.875rem' },
//                                   py: { xs: 0.75, sm: 1 },
//                               }}
//                               onClick={handleScheduleInterview}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: { xs: 8, sm: 16 }, top: { xs: 8, sm: 12 } }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body1" : "h6"}>{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                       size={isMobile ? "small" : "medium"}
//                     />
//                   ) : (
//                     <Typography variant={isMobile ? "body2" : "body1"}>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Posted Date
//                   </Typography>
//                   <Typography variant={isMobile ? "body2" : "body1"}>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                             size={isMobile ? "small" : "medium"}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                           size={isMobile ? "small" : "medium"}
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} size={isMobile ? "small" : "medium"} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary" size={isMobile ? "small" : "medium"}>
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                     size={isMobile ? "small" : "medium"}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal} size={isMobile ? "small" : "medium"}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//             <svg
//             width="100"
//             height="150"
//             viewBox="0 0 80 120"
//             style={{
//               position: "absolute",
//               right: 0,
//               top: 0,
//             }}
//           >
//             <path d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z" fill="#F9B64C" />
//           </svg>
//   </Box>
// );

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const [jobResponse, statsResponse] = await Promise.all([
//           apiClient.get("/job-postings/"),
//           apiClient.get("/employer-dashboard/")
//         ]);

//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);

//         const dashboardStats = statsResponse.data;
//         setStats({
//           activeJobs: dashboardStats.active_jobs ?? 0,
//           totalApplications: dashboardStats.total_applicants ?? 0,
//           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
//           offersExtended: dashboardStats.offer_extended ?? 0,
//         });

//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience"
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err)
//       {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ '& td, & th': { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem' }} />
//             <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem' }} />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '999px' }} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//        <Paper
//         key={index}
//         sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="80%" />
//             <Skeleton variant="text" sx={{ fontSize: '0.875rem' }} width="50%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//            <Grid item xs={12}>
//             <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#F1FFFA' }}>
//               <Box>
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//                   Quick Actions
//                 </Typography>
//                 <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
//                   <Button
//                     variant="contained"
//                     startIcon={<PostAdd />}
//                     onClick={() => navigate("/gatep/employer/post-job")}
//                     sx={{
//                       backgroundColor: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                       '&:hover': { backgroundColor: '#40389e' }
//                     }}
//                   >
//                     Post a New Job
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<People />}
//                     onClick={() => navigate("/gatep/employer/candidates")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF'
//                     }}
//                   >
//                     Manage Candidates
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<Schedule />}
//                     onClick={() => navigate("/gatep/employer/interviews")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF'
//                     }}
//                   >
//                     Scheduled Interviews
//                   </Button>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Grid>

//           {/* === START: REVISED CARD LAYOUT USING CSS GRID === */}
//           <Grid item xs={12}>
//             <Box
//               display="grid"
//               gridTemplateColumns={{
//                 xs: "repeat(1, 1fr)",
//                 sm: "repeat(2, 1fr)",
//                 md: "repeat(4, 1fr)",
//               }}
//               gap={3}
//             >
//               {[
//                 { value: stats.activeJobs, label: "ACTIVE JOBS" },
//                 { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//                 { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//                 { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//               ].map((stat, index) => (
//                 <Card
//                   key={index}
//                   sx={{
//                     minWidth: 0, // <-- CRITICAL FIX: Forces card to obey grid boundaries
//                     display: 'flex',
//                     flexDirection: 'column',
//                     backgroundColor: '#5A34C6',
//                     color: 'white',
//                     borderRadius: '20px',
//                     position: 'relative',
//                     overflow: 'hidden',
//                     boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                   }}
//                 >
//                   <CardWave />
//                   <CardContent
//                     sx={{
//                       flexGrow: 1,
//                       display: 'flex',
//                       flexDirection: 'column',
//                       justifyContent: 'center',
//                       position: 'relative',
//                       zIndex: 1,
//                       p: 3,
//                     }}
//                   >
//                     <Typography
//                       variant="body2"
//                       component="p"
//                       sx={{
//                         fontWeight: 'bold',
//                         color: '#A0A0C0',
//                         textTransform: 'uppercase',
//                         mb: 1,
//                         wordBreak: 'break-word', // <-- Safety net for long text
//                       }}
//                     >
//                       {stat.label}
//                     </Typography>
//                     <Typography
//                       variant="h3"
//                       component="p"
//                       sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                     >
//                       {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               ))}
//             </Box>
//           </Grid>
//           {/* === END: REVISED CARD LAYOUT === */}

//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                   <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                     <Button
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                       endIcon={<ArrowForward />}
//                       sx={{ color: '#4F46BA', textTransform: 'none' }}
//                     >
//                       View All Jobs
//                     </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               <TableCell>
//                                 <Chip
//                                   label="Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={6} sx={{width: '39%'}}>
//               <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA' }}>
//                 <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   <Stack spacing={2}>
//                     {renderCandidateSkeletons()}
//                   </Stack>
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     flexGrow: 1,
//                     overflowY: 'auto',
//                     maxHeight: '450px',
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: 'transparent',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#d3d3d3',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#a9a9a9',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant="body2" color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' }
//                               }}
//                               onClick={() => handleViewResume(candidate.resume_url)}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   }
//                               }}
//                               onClick={handleScheduleInterview}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//             <svg
//             width="100"
//             height="150"
//             viewBox="0 0 80 120"
//             style={{
//               position: "absolute",
//               right: 0,
//               top: 0,
//             }}
//           >
//             <path d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z" fill="#F9B64C" />
//           </svg>
//   </Box>
// );

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         // Fetch jobs and dashboard stats in parallel
//         const [jobResponse, statsResponse] = await Promise.all([
//           apiClient.get("/job-postings/"),
//           apiClient.get("/employer-dashboard/")
//         ]);

//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);

//         const dashboardStats = statsResponse.data;
//         setStats({
//           activeJobs: dashboardStats.active_jobs ?? 0,
//           totalApplications: dashboardStats.total_applicants ?? 0,
//           interviewsScheduled: dashboardStats.interview_scheduled ?? 0,
//           offersExtended: dashboardStats.offer_extended ?? 0,
//         });

//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience"
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ '& td, & th': { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem' }} />
//             <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem' }} />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '999px' }} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//        <Paper
//         key={index}
//         sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="80%" />
//             <Skeleton variant="text" sx={{ fontSize: '0.875rem' }} width="50%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//            <Grid item xs={12}>
//             <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#F1FFFA' }}>
//               <Box>
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//                   Quick Actions
//                 </Typography>
//                 <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
//                   <Button
//                     variant="contained"
//                     startIcon={<PostAdd />}
//                     onClick={() => navigate("/gatep/employer/post-job")}
//                     sx={{
//                       backgroundColor: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                       '&:hover': { backgroundColor: '#40389e' }
//                     }}
//                   >
//                     Post a New Job
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<People />}
//                     onClick={() => navigate("/gatep/employer/candidates")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF'
//                     }}
//                   >
//                     Manage Candidates
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<Schedule />}
//                     onClick={() => navigate("/gatep/employer/interviews")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF'
//                     }}
//                   >
//                     Scheduled Interviews
//                   </Button>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Grid>

//           <Grid item xs={12}>
//             <Grid container spacing={3}>
//                 {[
//                   { value: stats.activeJobs, label: "ACTIVE JOBS" },
//                   { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//                   { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//                   { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//                 ].map((stat, index) => (
//                   <Grid item xs={12} sm={6} md={3} key={index}>
//                     <Card sx={{
//                         backgroundColor: '#5A34C6',
//                         color: 'white',
//                         borderRadius: '20px',
//                         position: 'relative',
//                         overflow: 'hidden',
//                         height: '140px',
//                         boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                     }}>
//                       <CardWave />
//                       <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                           <Typography
//                             variant="body2"
//                             component="p"
//                             sx={{
//                                 fontWeight: 'bold',
//                                 color: '#A0A0C0',
//                                 textTransform: 'uppercase',
//                                 mb: 1
//                             }}
//                           >
//                             {stat.label}
//                           </Typography>
//                           <Typography
//                             variant="h3"
//                             component="p"
//                             sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                           >
//                             {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                           </Typography>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 ))}
//             </Grid>
//           </Grid>

//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                   <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                     <Button
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                       endIcon={<ArrowForward />}
//                       sx={{ color: '#4F46BA', textTransform: 'none' }}
//                     >
//                       View All Jobs
//                     </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         {/* <TableCell>Application</TableCell> */}
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                                 {/* <Typography variant="body2" color="#000000">{job.application_count || }</Typography> */}
//                               <TableCell>
//                                 <Chip
//                                   label="Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//               <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA' }}>
//                 <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   <Stack spacing={2}>
//                     {renderCandidateSkeletons()}
//                   </Stack>
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     flexGrow: 1,
//                     overflowY: 'auto',
//                     maxHeight: '450px',
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: 'transparent',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#d3d3d3',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#a9a9a9',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant="body2" color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' }
//                               }}
//                               onClick={() => handleViewResume(candidate.resume_url)}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   }
//                               }}
//                               onClick={handleScheduleInterview}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//     <svg
//       width="100%"
//       height="100%"
//       viewBox="0 0 100 150"
//       preserveAspectRatio="none"
//     >
//       <path
//         d="M 50 0 C 100 50, 100 100, 50 150 L 100 150 L 100 0 Z"
//         fill="#FDBA4E"
//       />
//     </svg>
//   </Box>
// );

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const jobResponse = await apiClient.get("/job-postings/");
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await apiClient.get("/applications/");
//         const summaryStats = appResponse.data?.summary_stats;

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//           offersExtended: 120, // Assuming static value as in original
//         }));
//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience"
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index} sx={{ '& td, & th': { border: 0, py: 1.5 } }}>
//         <TableCell component="th" scope="row">
//           <Box>
//             <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem' }} />
//             <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem' }} />
//           </Box>
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width={25} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '999px' }} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={32} height={32} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(4)].map((_, index) => (
//        <Paper
//         key={index}
//         sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box flexGrow={1}>
//             <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="80%" />
//             <Skeleton variant="text" sx={{ fontSize: '0.875rem' }} width="50%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} mt={2}>
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//            <Skeleton variant="rounded" height={36} sx={{ flex: 1, borderRadius: 2 }} />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//            <Grid item xs={12}>
//             <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#ffffffff' }}>
//               <Box>
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//                   Quick Actions
//                 </Typography>
//                 <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
//                   <Button
//                     variant="contained"
//                     startIcon={<PostAdd />}
//                     onClick={() => navigate("/gatep/employer/post-job")}
//                     sx={{
//                       backgroundColor: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                       '&:hover': { backgroundColor: '#40389e' }
//                     }}
//                   >
//                     Post a New Job
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<People />}
//                     onClick={() => navigate("/gatep/employer/candidates")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF'
//                     }}
//                   >
//                     Manage Candidates
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     startIcon={<Schedule />}
//                     onClick={() => navigate("/gatep/employer/interviews")}
//                     sx={{
//                       borderColor: '#E0E0E0',
//                       color: '#4F46BA',
//                       textTransform: 'none',
//                       borderRadius: 2,
//                       backgroundColor: '#FFFFFF'
//                     }}
//                   >
//                     Scheduled Interviews
//                   </Button>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Grid>

//           <Grid item xs={12}>
//             <Grid container spacing={3}>
//                 {[
//                   { value: stats.activeJobs, label: "ACTIVE JOBS" },
//                   { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//                   { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//                   { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//                 ].map((stat, index) => (
//                   <Grid item xs={12} sm={6} md={3} key={index}>
//                     <Card sx={{
//                         backgroundColor: '#5A34C6',
//                         color: 'white',
//                         borderRadius: '20px',
//                         position: 'relative',
//                         overflow: 'hidden',
//                         height: '140px',
//                         boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                     }}>
//                       <CardWave />
//                       <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                           <Typography
//                             variant="body2"
//                             component="p"
//                             sx={{
//                                 fontWeight: 'bold',
//                                 color: '#A0A0C0',
//                                 textTransform: 'uppercase',
//                                 mb: 1
//                             }}
//                           >
//                             {stat.label}
//                           </Typography>
//                           <Typography
//                             variant="h3"
//                             component="p"
//                             sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                           >
//                             {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                           </Typography>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 ))}
//             </Grid>
//           </Grid>

//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                   <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                     <Button
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                       endIcon={<ArrowForward />}
//                       sx={{ color: '#4F46BA', textTransform: 'none' }}
//                     >
//                       View All Jobs 
//                     </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         <TableCell>Application</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               <TableCell>
//                                 <Typography variant="body2" color="#000000">{job.application_count || 85}</Typography>
//                               </TableCell>
//                               <TableCell>
//                                 <Chip
//                                   label="Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//               <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA' }}>
//                 <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   <Stack spacing={2}>
//                     {renderCandidateSkeletons()}
//                   </Stack>
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     flexGrow: 1,
//                     overflowY: 'auto',
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: 'transparent',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#d3d3d3',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#a9a9a9',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant="body2" color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' }
//                               }}
//                               onClick={() => handleViewResume(candidate.resume_url)}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   }
//                               }}
//                               onClick={handleScheduleInterview}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//     <svg
//       width="100%"
//       height="100%"
//       viewBox="0 0 100 150"
//       preserveAspectRatio="none"
//     >
//       <path
//         d="M 50 0 C 100 50, 100 100, 50 150 L 100 150 L 100 0 Z"
//         fill="#FDBA4E"
//       />
//     </svg>
//   </Box>
// );

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 120,
//     totalApplications: 120,
//     interviewsScheduled: 120,
//     offersExtended: 120,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const jobResponse = await apiClient.get("/job-postings/");
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await apiClient.get("/applications/");
//         const summaryStats = appResponse.data?.summary_stats;

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//           offersExtended: 120,
//         }));
//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             resume_url: candidate.resume_url,
//             experience: "5+ year of Experience"
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width="50%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={24} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <Paper
//         key={index}
//         sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #E0E0E0' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
//            <Skeleton variant="rounded" height={36} width={100} />
//            <Skeleton variant="rounded" height={36} width={150} />
//         </Stack>
//       </Paper>
//     ));

//   const handleViewResume = (resumeUrl) => {
//     if (resumeUrl) {
//       window.open(resumeUrl, "_blank", "noopener,noreferrer");
//     }
//   };

//   const handleScheduleInterview = () => {
//     navigate("/gatep/employer/analytics?tab=candidates");
//   };

//   return (
//     <Layout title="Emplyoee Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//           <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#ffffffff' }}>
//             <Box>
//               <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//                 Quick Actions
//               </Typography>
//               <Stack direction="row" spacing={2}>
//                 <Button
//                   variant="contained"
//                   startIcon={<PostAdd />}
//                   onClick={() => navigate("/gatep/employer/post-job")}
//                   sx={{
//                     backgroundColor: '#4F46BA',
//                     textTransform: 'none',
//                     borderRadius: 2,
//                     boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                     '&:hover': { backgroundColor: '#40389e' }
//                   }}
//                 >
//                   Post a New Job
//                 </Button>
//                 <Button
//                   variant="outlined"
//                   startIcon={<People />}
//                   onClick={() => navigate("/gatep/employer/candidates")}
//                   sx={{
//                     borderColor: '#E0E0E0',
//                     color: '#4F46BA',
//                     textTransform: 'none',
//                     borderRadius: 2,
//                     backgroundColor: '#FFFFFF'
//                   }}
//                 >
//                   Manage Candidates
//                 </Button>
//                 <Button
//                   variant="outlined"
//                   startIcon={<Schedule />}
//                   onClick={() => navigate("/gatep/employer/interviews")}
//                   sx={{
//                     borderColor: '#E0E0E0',
//                     color: '#4F46BA',
//                     textTransform: 'none',
//                     borderRadius: 2,
//                     backgroundColor: '#FFFFFF'
//                   }}
//                 >
//                   Scheduled Interviews
//                 </Button>
//               </Stack>
//             </Box>
//           </Paper>

//           <Grid container spacing={3} sx={{ mt: 0.1 }}>
//             {[
//               { value: stats.activeJobs, label: "ACTIVE JOBS" },
//               { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//               { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//               { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//             ].map((stat, index) => (
//               <Grid item xs={12} sm={6} md={3} key={index}>
//                 <Card sx={{
//                     backgroundColor: '#5A34C6',
//                     color: 'white',
//                     borderRadius: '20px',
//                     position: 'relative',
//                     overflow: 'hidden',
//                     height: '140px',
//                     boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                 }}>
//                   <CardWave />
//                   <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                       <Typography
//                         variant="body2"
//                         component="p"
//                         sx={{
//                             fontWeight: 'bold',
//                             color: '#A0A0C0',
//                             textTransform: 'uppercase',
//                             mb: 1
//                         }}
//                       >
//                         {stat.label}
//                       </Typography>
//                       <Typography
//                         variant="h3"
//                         component="p"
//                         sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                       >
//                         {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                       </Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             ))}
//           </Grid>

//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                   <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                     <Button
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                       endIcon={<ArrowForward />}
//                       sx={{ color: '#4F46BA', textTransform: 'none' }}
//                     >
//                       View All Jobs 
//                     </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         <TableCell>Application</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               <TableCell>
//                                 <Typography variant="body2" color="#000000">{job.application_count || 85}</Typography>
//                               </TableCell>
//                               <TableCell>
//                                 <Chip
//                                   label="Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//               <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA', width:"450px"}}>
//                 <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   renderCandidateSkeletons()
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     maxHeight: 440,
//                     overflowY: 'auto',
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: '#f1f1f1',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#888',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#555',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant="body2" color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' }
//                               }}
//                               onClick={() => handleViewResume(candidate.resume_url)}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   }
//                               }}
//                               onClick={handleScheduleInterview}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// // This is the accent shape for the metric cards. Using an SVG for a precise curve.
// const CardWave = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       top: 0,
//       right: 0,
//       width: '65%',
//       height: '100%',
//       zIndex: 0,
//     }}
//   >
//     <svg
//       width="100%"
//       height="100%"
//       viewBox="0 0 100 150"
//       preserveAspectRatio="none"
//     >
//       <path
//         d="M 50 0 C 100 50, 100 100, 50 150 L 100 150 L 100 0 Z"
//         fill="#FDBA4E"
//       />
//     </svg>
//   </Box>
// );


// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 120,
//     totalApplications: 120,
//     interviewsScheduled: 120,
//     offersExtended: 120,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const jobResponse = await apiClient.get("/job-postings/");
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await apiClient.get("/applications/");
//         const summaryStats = appResponse.data?.summary_stats;

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//           offersExtended: 120, // Keep static value from design
//         }));
//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             experience: "5+ year of Experience" // Static data from design
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5)); // Fetching 5 to make it scrollable
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6); // show 6 as per design
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width="50%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={24} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <Paper
//         key={index}
//         sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #E0E0E0' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
//            <Skeleton variant="rounded" height={36} width={100} />
//            <Skeleton variant="rounded" height={36} width={150} />
//         </Stack>
//       </Paper>
//     ));

//   return (
//     <Layout title="Emplyoee Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#ffffffff' }}>
//                           <Box>
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//               Quick Actions
//             </Typography> 
//             <Stack direction="row" spacing={2}>
//               <Button
//                 variant="contained"
//                 startIcon={<PostAdd />}
//                 onClick={() => navigate("/gatep/employer/post-job")}
//                 sx={{
//                   backgroundColor: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                   '&:hover': { backgroundColor: '#40389e' }
//                 }}
//               >
//                 Post a New Job
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={<People />}
//                 onClick={() => navigate("/gatep/employer/candidates")}
//                 sx={{
//                   borderColor: '#E0E0E0',
//                   color: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   backgroundColor: '#FFFFFF'
//                 }}
//               >
//                 Manage Candidates
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={<Schedule />}
//                 onClick={() => navigate("/gatep/employer/interviews")}
//                  sx={{
//                   borderColor: '#E0E0E0',
//                   color: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   backgroundColor: '#FFFFFF'
//                 }}
//               >
//                 Scheduled Interviews
//               </Button>
//             </Stack>
//             </Box>
//        </Paper>
//         <Grid container spacing={3} sx={{ mt: 0.1 }}>
//             {[
//               { value: stats.activeJobs, label: "ACTIVE JOBS" },
//               { value: stats.totalApplications, label: "TOTAL APPLICATIONS" },
//               { value: stats.interviewsScheduled, label: "INTERVIEWS SCHEDULED" },
//               { value: stats.offersExtended, label: "OFFERS EXTENDED" },
//             ].map((stat, index) => (
//               <Grid item xs={12} sm={6} md={3} key={index}>
//                 <Card sx={{
//                     backgroundColor: '#5A34C6',
//                     color: 'white',
//                     borderRadius: '20px',
//                     position: 'relative',
//                     overflow: 'hidden',
//                     height: '140px',
//                     boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
//                 }}>
//                   <CardWave />
//                   <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
//                       <Typography
//                         variant="body2"
//                         component="p"
//                         sx={{
//                             fontWeight: 'bold',
//                             color: '#A0A0C0',
//                             textTransform: 'uppercase',
//                             mb: 1
//                         }}
//                       >
//                         {stat.label}
//                       </Typography>
//                       <Typography
//                         variant="h3"
//                         component="p"
//                         sx={{ fontWeight: 'bold', color: '#FFFFFF' }}
//                       >
//                         {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'grey.700' }} /> : stat.value}
//                       </Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             ))}
//         </Grid>
       


//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                    <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                      <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                         sx={{ color: '#4F46BA', textTransform: 'none' }}
//                       >
//                         View All Jobs 
//                       </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         <TableCell>Application</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               <TableCell>
//                                 <Typography variant="body2" color="#000000">{job.application_count || 85}</Typography>
//                               </TableCell>
//                               <TableCell>
//                                 <Chip
//                                   label="Active" // Design uses "Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA', width:"450px"}}>
//               <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   renderCandidateSkeletons()
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     maxHeight: 440, // Height to fit 3 cards
//                     overflowY: 'auto',
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: '#f1f1f1',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#888',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#555',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant="body2" color="#A9A5A5">
//                                  {candidate.location}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' }
//                               }}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   }
//                               }}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import React, { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// // This is the accent shape for the metric cards. Using an SVG for a precise curve.
// const CardAccent = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       bottom: 0,
//       right: 0,
//       width: '100%',
//       height: '50%',
//       zIndex: 0,
//     }}
//   >
//     <svg
//       width="100%"
//       height="100%"
//       viewBox="0 0 140 70"
//       preserveAspectRatio="none"
//     >
//       <path
//         d="M-20 70 C 40 70, 70 20, 140 30 L 140 70 Z"
//         fill="#FDDBB1"
//       />
//     </svg>
//   </Box>
// );


// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 120, // Value from design
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const jobResponse = await apiClient.get("/job-postings/");
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await apiClient.get("/applications/");
//         const summaryStats = appResponse.data?.summary_stats;

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//         }));
//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             experience: "5+ year of Experience" // Static data from design
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5)); // Fetching 5 to make it scrollable
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6); // show 6 as per design
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width="50%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={24} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <Paper
//         key={index}
//         sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #E0E0E0' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
//            <Skeleton variant="rounded" height={36} width={100} />
//            <Skeleton variant="rounded" height={36} width={150} />
//         </Stack>
//       </Paper>
//     ));

//   return (
//     <Layout title="Emplyoee Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#ffffffff' }}>
//                           <Box>
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//               Quick Actions
//             </Typography> 
//             <Stack direction="row" spacing={2}>
//               <Button
//                 variant="contained"
//                 startIcon={<PostAdd />}
//                 onClick={() => navigate("/gatep/employer/post-job")}
//                 sx={{
//                   backgroundColor: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                   '&:hover': { backgroundColor: '#40389e' }
//                 }}
//               >
//                 Post a New Job
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={<People />}
//                 onClick={() => navigate("/gatep/employer/candidates")}
//                 sx={{
//                   borderColor: '#E0E0E0',
//                   color: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   backgroundColor: '#FFFFFF'
//                 }}
//               >
//                 Manage Candidates
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={<Schedule />}
//                 onClick={() => navigate("/gatep/employer/interviews")}
//                  sx={{
//                   borderColor: '#E0E0E0',
//                   color: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   backgroundColor: '#FFFFFF'
//                 }}
//               >
//                 Scheduled Interviews
//               </Button>
//             </Stack>
//             </Box>
//        </Paper>
//               <Grid container spacing={3}>

//           {[
//             { value: stats.activeJobs, label: "Active Jobs" },
//             { value: stats.totalApplications, label: "Total Applications" },
//             { value: stats.interviewsScheduled, label: "Interviews Scheduled" },
//             { value: stats.offersExtended, label: "Offers Extended" },
//           ].map((stat, index) => (
//             <Grid item xs={12} sm={6} md={3} key={index}>
//               <Card sx={{
//                   backgroundColor: '#4F46BA',
//                   color: 'white',
//                   borderRadius: '16px',
//                   position: 'relative',
//                   overflow: 'hidden',
//                   height: '100%',
//                   width:"280px",
//                   boxShadow: '0 8px 16px -4px rgba(79,70,186,0.35)'
//               }}>
//                 <CardAccent />
//                 <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
//                     <Typography
//                       variant="h3"
//                       component="p"
//                       sx={{ fontWeight: 'bold', mb: 1 }}
//                     >
//                       {loading ? <Skeleton width={60} sx={{ bgcolor: 'grey.600' }}/> : stat.value}
//                     </Typography>
//                     <Typography variant="body1" sx={{ opacity: 0.9 }}>
//                       {stat.label}
//                     </Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//        </Grid>
       


//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                    <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                      <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                         sx={{ color: '#4F46BA', textTransform: 'none' }}
//                       >
//                         View All Jobs 
//                       </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         <TableCell>Application</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               <TableCell>
//                                 <Typography variant="body2" color="#000000">{job.application_count || 85}</Typography>
//                               </TableCell>
//                               <TableCell>
//                                 <Chip
//                                   label="Active" // Design uses "Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA', width:"450px"}}>
//               <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
//                   renderCandidateSkeletons()
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
//                   <Box sx={{
//                     maxHeight: 440, // Height to fit 3 cards
//                     overflowY: 'auto',
//                     pr: 1,
//                     '&::-webkit-scrollbar': {
//                       width: '6px',
//                     },
//                     '&::-webkit-scrollbar-track': {
//                       background: '#f1f1f1',
//                       borderRadius: '10px',
//                     },
//                     '&::-webkit-scrollbar-thumb': {
//                       background: '#888',
//                       borderRadius: '10px',
//                     },
//                      '&::-webkit-scrollbar-thumb:hover': {
//                       background: '#555',
//                     }
//                   }}>
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                         >
//                           <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                             <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography variant="body2" color="#A9A5A5">
//                                  {candidate.experience}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button
//                               size="small"
//                               variant="contained"
//                               sx={{
//                                   backgroundColor: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': { backgroundColor: '#40389e' }
//                               }}
//                             >
//                               View Profile
//                             </Button>
//                             <Button
//                               size="small"
//                               variant="outlined"
//                               sx={{
//                                   borderColor: '#4F46BA',
//                                   backgroundColor: 'transparent',
//                                   color: '#4F46BA',
//                                   flex: 1,
//                                   textTransform: 'none',
//                                   borderRadius: 2,
//                                   '&:hover': {
//                                       backgroundColor: '#e6f2ee',
//                                       borderColor: '#4F46BA'
//                                   }
//                               }}
//                             >
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   </Box>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;

// // import { useState, useEffect } from "react";
// // import {
// //   Grid,
// //   Card,
// //   CardContent,
// //   Typography,
// //   Button,
// //   Box,
// //   Chip,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,import React, { useState, useEffect } from "react";
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
//   LocationOn,
// } from "@mui/icons-material";
// import apiClient from "../context/apiClient";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";

// // This is the accent shape for the metric cards. Using an SVG for a precise curve.
// const CardAccent = () => (
//   <Box
//     sx={{
//       position: 'absolute',
//       bottom: 0,
//       right: 0,
//       width: '100%',
//       height: '50%',
//       zIndex: 0,
//     }}
//   >
//     <svg
//       width="100%"
//       height="100%"
//       viewBox="0 0 140 70"
//       preserveAspectRatio="none"
//     >
//       <path
//         d="M-20 70 C 40 70, 70 20, 140 30 L 140 70 Z"
//         fill="#FDDBB1"
//       />
//     </svg>
//   </Box>
// );


// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 120, // Value from design
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const jobResponse = await apiClient.get("/job-postings/");
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await apiClient.get("/applications/");
//         const summaryStats = appResponse.data?.summary_stats;

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//         }));
//         setLoading(false);

//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await apiClient.get(
//             `/job-postings/${mostRecentJobId}/potential-candidates/`
//           );

//           const potentialCandidates = candidatesResponse.data || [];

//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//             experience: "5+ year of Experience" // Static data from design
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 5)); // Show 5 as per design
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage = `Error: ${
//             err.response.data?.detail || err.response.statusText
//           }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         if (err.response?.status !== 401) {
//             setError(errorMessage);
//         }
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 6); // show 6 as per design
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };

//   const renderJobSkeletons = () =>
//     [...Array(5)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="text" width="50%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={24} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <Paper
//         key={index}
//         sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #E0E0E0' }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
//            <Skeleton variant="rounded" height={36} width={100} />
//            <Skeleton variant="rounded" height={36} width={150} />
//         </Stack>
//       </Paper>
//     ));

//   return (
//     <Layout title="Emplyoee Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           height: 'calc(100vh - 64px)',
//           overflowY: 'auto',
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={3}>
//            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: '#F1FFFA' }}>
//                           <Box>
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
//               Quick Actions
//             </Typography> 
//             <Stack direction="row" spacing={2}>
//               <Button
//                 variant="contained"
//                 startIcon={<PostAdd />}
//                 onClick={() => navigate("/gatep/employer/post-job")}
//                 sx={{
//                   backgroundColor: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   boxShadow: '0 4px 12px 0 rgba(79,70,186,0.3)',
//                   '&:hover': { backgroundColor: '#40389e' }
//                 }}
//               >
//                 Post a New Job
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={<People />}
//                 onClick={() => navigate("/gatep/employer/candidates")}
//                 sx={{
//                   borderColor: '#E0E0E0',
//                   color: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   backgroundColor: '#FFFFFF'
//                 }}
//               >
//                 Manage Candidates
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={<Schedule />}
//                 onClick={() => navigate("/gatep/employer/interviews")}
//                  sx={{
//                   borderColor: '#E0E0E0',
//                   color: '#4F46BA',
//                   textTransform: 'none',
//                   borderRadius: 2,
//                   backgroundColor: '#FFFFFF'
//                 }}
//               >
//                 Scheduled Interviews
//               </Button>
//             </Stack>
//             </Box>
//        </Paper>
//               <Grid container spacing={3}>

//           {[
//             { value: stats.activeJobs, label: "Active Jobs" },
//             { value: stats.totalApplications, label: "Total Applications" },
//             { value: stats.interviewsScheduled, label: "Interviews Scheduled" },
//             { value: stats.offersExtended, label: "Offers Extended" },
//           ].map((stat, index) => (
//             <Grid item xs={12} sm={6} md={3} key={index}>
//               <Card sx={{
//                   backgroundColor: '#4F46BA',
//                   color: 'white',
//                   borderRadius: '16px',
//                   position: 'relative',
//                   overflow: 'hidden',
//                   height: '100%',
//                   width:"280px",
//                   boxShadow: '0 8px 16px -4px rgba(79,70,186,0.35)'
//               }}>
//                 <CardAccent />
//                 <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
//                     <Typography
//                       variant="h3"
//                       component="p"
//                       sx={{ fontWeight: 'bold', mb: 1 }}
//                     >
//                       {loading ? <Skeleton width={60} sx={{ bgcolor: 'grey.600' }}/> : stat.value}
//                     </Typography>
//                     <Typography variant="body1" sx={{ opacity: 0.9 }}>
//                       {stat.label}
//                     </Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//        </Grid>
       


//           <Grid item xs={12} lg={8}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, backgroundColor: '#F1FFFA', width:"100%" }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
//                    <Typography variant="h6" fontWeight="bold">
//                     Your Recent Job Postings
//                   </Typography>
//                   {!loading && jobPostings.length > 5 && (
//                      <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                         sx={{ color: '#4F46BA', textTransform: 'none' }}
//                       >
//                         View All Jobs 
//                       </Button>
//                   )}
//                 </Stack>
//                 {error && <Alert severity="error">{error}</Alert>}
//                 <TableContainer>
//                   <Table aria-label="recent job postings" sx={{ minWidth: 650 }}>
//                     <TableHead sx={{ '& .MuiTableCell-root': { borderBottom: 'none', color: '#A9A5A5' } }}>
//                       <TableRow>
//                         <TableCell>Position</TableCell>
//                         <TableCell>Application</TableCell>
//                         <TableCell>Status</TableCell>
//                         <TableCell>Action</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {loading
//                         ? renderJobSkeletons()
//                         : jobsToDisplay.map((job) => (
//                             <TableRow
//                               key={job.id}
//                               hover
//                               sx={{ '& td, & th': { border: 0, py: 1.5 } }}
//                             >
//                               <TableCell component="th" scope="row">
//                                 <Box>
//                                   <Typography variant="subtitle2" fontWeight="bold">
//                                     {job.title}
//                                   </Typography>
//                                   <Stack direction="row" alignItems="center" color="#A9A5A5" spacing={0.5}>
//                                     <LocationOn sx={{ fontSize: 16 }} />
//                                     <Typography variant="body2">{job.location}</Typography>
//                                   </Stack>
//                                 </Box>
//                               </TableCell>
//                               <TableCell>
//                                 <Typography variant="body2" color="#000000">{job.application_count || 85}</Typography>
//                               </TableCell>
//                               <TableCell>
//                                 <Chip
//                                   label="Active" // Design uses "Active"
//                                   size="small"
//                                   sx={{
//                                       backgroundColor: '#22C55E',
//                                       color: '#FFFFFF',
//                                       borderRadius: '999px',
//                                       fontWeight: 500
//                                   }}
//                                 />
//                               </TableCell>
//                               <TableCell>
//                                 <IconButton
//                                   size="small"
//                                   onClick={() => handleOpenModal(job)}
//                                 >
//                                   <Visibility sx={{ color: '#A9A5A5' }} />
//                                 </IconButton>
//                               </TableCell>
//                             </TableRow>
//                           ))}
//                       {!loading && !error && jobPostings.length === 0 && (
//                         <TableRow>
//                           <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
//                             No jobs posted yet.
//                           </TableCell>
//                         </TableRow>
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//             <Card sx={{ borderRadius: 4, boxShadow: 'none', p: 2, height: '100%',backgroundColor: '#F1FFFA', width:"450px"}}>
//               <CardContent>
//                 <Typography variant="h6" gutterBottom fontWeight="bold">
//                   Top Matched Candidates
//                 </Typography>
//                 {candidatesLoading ? (
                  
//                   renderCandidateSkeletons()
//                 ) : error ? (
//                   <Alert severity="warning">Could not load candidate data.</Alert>
//                 ) : topCandidates.length > 0 ? (
                  
//                   <Stack spacing={2}>
//                     {topCandidates.map((candidate) => (
//                       <Paper
//                         key={candidate.id}
//                         sx={{ p: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #F3F4F6', backgroundColor: '#FFFFFF' }}
//                       >
//                         <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
//                           <Avatar sx={{ bgcolor: "#FDDBB1", color: "#B54708", fontWeight: 'bold' }}>
//                             {candidate.applicant_name.charAt(0).toUpperCase()}
//                           </Avatar>
//                           <Box flexGrow={1}>
//                             <Typography variant="subtitle1" fontWeight="bold">
//                               {candidate.applicant_name}
//                             </Typography>
//                             <Typography variant="body2" color="#A9A5A5">
//                                {candidate.experience}
//                             </Typography>
//                           </Box>
//                         </Stack>
//                         <Stack direction="row" spacing={1} mt={2}>
//                           <Button
//                             size="small"
//                             variant="contained"
//                             sx={{
//                                 backgroundColor: '#4F46BA',
//                                 flex: 1,
//                                 textTransform: 'none',
//                                 borderRadius: 2,
//                                 '&:hover': { backgroundColor: '#40389e' }
//                             }}
//                           >
//                             View Profile
//                           </Button>
//                           <Button
//                             size="small"
//                             variant="outlined"
//                             sx={{
//                                 borderColor: '#4F46BA',
//                                 backgroundColor: 'transparent',
//                                 color: '#4F46BA',
//                                 flex: 1,
//                                 textTransform: 'none',
//                                 borderRadius: 2,
//                                 '&:hover': {
//                                     backgroundColor: '#e6f2ee',
//                                     borderColor: '#4F46BA'
//                                 }
//                             }}
//                           >
//                             Schedule Interview
//                           </Button>
//                         </Stack>
//                       </Paper>
//                     ))}
//                   </Stack>
//                 ) : (
//                   <Typography variant="body2" color="#A9A5A5" sx={{ textAlign: 'center', py: 3 }}>
//                     No potential candidates found.
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
// import { useState, useEffect } from "react";
  // import {
  //   Grid,
  //   Card,
  //   CardContent,
  //   Typography,
  //   Button,
  //   Box,
  //   Chip,
  //   Table,
  //   TableBody,
  //   TableCell,
  //   TableContainer,
  //   TableHead,
  //   TableRow,
  //   Avatar,
  //   IconButton,
  //   Alert,
  //   Skeleton,
  //   Dialog,
  //   DialogTitle,
  //   DialogContent,
  //   DialogActions,
  //   TextField,
  //   CircularProgress,
  //   Autocomplete,
  //   Stack,
  //   Paper,
  // } from "@mui/material";
  // import {
  //   PostAdd,
  //   People,
  //   Visibility,
  //   Schedule,
  //   TrendingUp,
  //   LocationOn,
  //   ArrowForward,
  //   Close,
  //   Edit,
  //   Save,
  // } from "@mui/icons-material";
  // import apiClient from "../context/apiClient";
  // import Layout from "../components/common/Layout";
  // import { useNavigate } from "react-router-dom";

  // const cardStyles = {
  //   borderRadius: 3,
  //   boxShadow: "0 4px 12px 0 rgba(0,0,0,0.07)",
  //   height: "100%",
  //   display: "flex",
  //   flexDirection: "column",
  // };

  // const EmployerDashboard = () => {
  //   const navigate = useNavigate();

  //   const [jobPostings, setJobPostings] = useState([]);
  //   const [topCandidates, setTopCandidates] = useState([]);
  //   const [loading, setLoading] = useState(true);
  //   const [error, setError] = useState(null);
  //   const [candidatesLoading, setCandidatesLoading] = useState(true);

  //   const [isModalOpen, setIsModalOpen] = useState(false);
  //   const [selectedJob, setSelectedJob] = useState(null);

  //   const [isEditing, setIsEditing] = useState(false);
  //   const [editedData, setEditedData] = useState({});
  //   const [modalLoading, setModalLoading] = useState(false);
  //   const [modalError, setModalError] = useState("");

  //   const [stats, setStats] = useState({
  //     activeJobs: 0,
  //     totalApplications: 0,
  //     interviewsScheduled: 0,
  //     offersExtended: 8,
  //   });

  //   useEffect(() => {
  //     const fetchDashboardData = async () => {
  //       setLoading(true);
  //       setCandidatesLoading(true);
  //       setError(null);

  //       try {
  //         const jobResponse = await apiClient.get("/job-postings/");
  //         const jobs = jobResponse?.data || [];
  //         setJobPostings(jobs);
  //         const activeJobsCount = jobs.filter(
  //           (job) => job.status === "PUBLISHED"
  //         ).length;

  //         const appResponse = await apiClient.get("/applications/");
  //         const summaryStats = appResponse.data?.summary_stats;

  //         setStats((prevStats) => ({
  //           ...prevStats,
  //           activeJobs: activeJobsCount,
  //           totalApplications: summaryStats?.total_applications ?? 0,
  //           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
  //         }));
  //         setLoading(false);

  //         if (jobs.length > 0) {
  //           const mostRecentJobId = jobs[0].id;
  //           const candidatesResponse = await apiClient.get(
  //             `/job-postings/${mostRecentJobId}/potential-candidates/`
  //           );

  //           const potentialCandidates = candidatesResponse.data || [];

  //           const formattedCandidates = potentialCandidates.map((candidate) => ({
  //             id: candidate.talent_id,
  //             applicant_name: candidate.name,
  //             matching_percentage: Math.round(candidate.ai_match_score),
  //             location: candidate.location,
  //           }));

  //           const sortedCandidates = formattedCandidates.sort(
  //             (a, b) => b.matching_percentage - a.matching_percentage
  //           );

  //           setTopCandidates(sortedCandidates.slice(0, 2));
  //         } else {
  //           setTopCandidates([]);
  //         }
  //       } catch (err) {
  //         let errorMessage = "An unexpected error occurred.";
  //         if (err.response) {
  //           errorMessage = `Error: ${
  //             err.response.data?.detail || err.response.statusText
  //           }`;
  //         } else if (err.request) {
  //           errorMessage = "No response from server. Check your connection.";
  //         } else {
  //           errorMessage = err.message;
  //         }
  //         // The apiClient interceptor will handle 401s, but we still show other errors.
  //         if (err.response?.status !== 401) {
  //             setError(errorMessage);
  //         }
  //         setLoading(false);
  //       } finally {
  //         setCandidatesLoading(false);
  //       }
  //     };

  //     fetchDashboardData();
  //   }, []);

  //   useEffect(() => {
  //     if (selectedJob) {
  //       setEditedData({
  //         title: selectedJob.title || "",
  //         location: selectedJob.location || "",
  //         required_skills: selectedJob.required_skills || [],
  //       });
  //       setIsEditing(false);
  //       setModalError("");
  //     }
  //   }, [selectedJob]);

  //   const jobsToDisplay = jobPostings.slice(0, 5);
  //   const handleOpenModal = (job) => {
  //     setSelectedJob(job);
  //     setIsModalOpen(true);
  //   };
  //   const handleCloseModal = () => {
  //     setIsModalOpen(false);
  //     setSelectedJob(null);
  //   };
  //   const handleJobUpdate = (updatedJob) => {
  //     setJobPostings((prevJobs) =>
  //       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
  //     );
  //   };
  //   const handleInputChange = (e) => {
  //     const { name, value } = e.target;
  //     setEditedData((prev) => ({ ...prev, [name]: value }));
  //   };
  //   const handleSkillsChange = (event, newValue) => {
  //     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
  //   };
  //   const handleSaveChanges = async () => {
  //     setModalLoading(true);
  //     setModalError("");
  //     try {
  //       const response = await apiClient.put(
  //         `/job-postings/${selectedJob.id}/`,
  //         { ...selectedJob, ...editedData }
  //       );
  //       handleJobUpdate(response.data);
  //       setIsEditing(false);
  //       handleCloseModal();
  //     } catch (err) {
  //       setModalError("Failed to save changes. Please try again.");
  //     } finally {
  //       setModalLoading(false);
  //     }
  //   };
  //   const handleCancelEdit = () => {
  //     if (selectedJob) {
  //       setEditedData({
  //         title: selectedJob.title,
  //         location: selectedJob.location,
  //         required_skills: selectedJob.required_skills,
  //       });
  //     }
  //     setIsEditing(false);
  //   };
  //   const getStatusColor = (statusDisplay) => {
  //     return statusDisplay === "Published" ? "success" : "warning";
  //   };

  //   const renderJobSkeletons = () =>
  //     [...Array(3)].map((_, index) => (
  //       <TableRow key={index}>
  //         <TableCell>
  //           <Skeleton variant="text" width="80%" />
  //         </TableCell>
  //         <TableCell>
  //           <Skeleton variant="rounded" width={60} height={20} />
  //         </TableCell>
  //         <TableCell>
  //           <Skeleton variant="circular" width={30} height={30} />
  //         </TableCell>
  //       </TableRow>
  //     ));

  //   const renderCandidateSkeletons = () =>
  //     [...Array(2)].map((_, index) => (
  //       <Paper
  //         key={index}
  //         variant="outlined"
  //         sx={{ p: 2, mb: 2, borderRadius: 2 }}
  //       >
  //         <Stack direction="row" spacing={2} alignItems="center">
  //           <Skeleton variant="circular" width={40} height={40} />
  //           <Box sx={{ flexGrow: 1 }}>
  //             <Skeleton variant="text" width="60%" />
  //             <Skeleton variant="text" width="40%" />
  //           </Box>
  //         </Stack>
  //         <Skeleton variant="rounded" height={60} sx={{ mt: 2 }} />
  //       </Paper>
  //     ));

  //   return (
  //     <Layout title="Employer Dashboard">
  //       <Box
  //         sx={{
  //           position: "absolute",
  //           top: 0,
  //           left: 0,
  //           right: 0,
  //           bottom: 0,
  //           overflowY: "auto",
  //           py: { xs: 3, md: 4 },
  //           px: { xs: 2, md: 4 },
  //           "&::-webkit-scrollbar": { display: "none" },
  //           "-ms-overflow-style": "none",
  //           "scrollbar-width": "none",
  //         }}
  //       >
  //         <Grid container spacing={{ xs: 2, md: 4 }}>
  //           <Grid item xs={12} lg={8}>
  //             <Stack spacing={{ xs: 2, md: 4 }}>
  //               <Card sx={cardStyles}>
  //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
  //                   <Typography variant="h6" gutterBottom>
  //                     Quick Actions
  //                   </Typography>
  //                   <Stack spacing={2}>
  //                     <Button
  //                       variant="contained"
  //                       startIcon={<PostAdd />}
  //                       onClick={() => navigate("/gatep/employer/post-job")}
  //                     >
  //                       Post New Job
  //                     </Button>
  //                     <Button
  //                       variant="outlined"
  //                       startIcon={<People />}
  //                       onClick={() => navigate("/gatep/employer/candidates")}
  //                     >
  //                       Manage Candidates
  //                     </Button>
  //                     <Button
  //                       variant="outlined"
  //                       startIcon={<Schedule />}
  //                       onClick={() => navigate("/gatep/employer/interviews")}
  //                     >
  //                       Scheduled Interviews
  //                     </Button>
  //                   </Stack>
  //                 </CardContent>
  //               </Card>

  //               <Grid container spacing={2}>
  //                 {[
  //                   {
  //                     icon: <PostAdd color="primary" sx={{ fontSize: 40 }} />,
  //                     value: stats.activeJobs,
  //                     label: "Active Jobs",
  //                   },
  //                   {
  //                     icon: <People color="success" sx={{ fontSize: 40 }} />,
  //                     value: stats.totalApplications,
  //                     label: "Total Applications",
  //                   },
  //                   {
  //                     icon: <Schedule color="warning" sx={{ fontSize: 40 }} />,
  //                     value: stats.interviewsScheduled,
  //                     label: "Interviews Scheduled",
  //                   },
  //                   {
  //                     icon: <TrendingUp color="info" sx={{ fontSize: 40 }} />,
  //                     value: stats.offersExtended,
  //                     label: "Offers Extended",
  //                   },
  //                 ].map((stat, index) => (
  //                   <Grid item xs={6} sm={3} lg={3} key={index}>
  //                     <Card sx={cardStyles}>
  //                       <CardContent
  //                         sx={{ textAlign: "center", p: 2, flexGrow: 1 }}
  //                       >
  //                         {stat.icon}
  //                         <Typography variant="h4" sx={{ my: 1 }}>
  //                           {loading ? (
  //                             <Skeleton width={40} sx={{ mx: "auto" }} />
  //                           ) : (
  //                             stat.value
  //                           )}
  //                         </Typography>
  //                         <Typography variant="body2" color="text.secondary">
  //                           {stat.label}
  //                         </Typography>
  //                       </CardContent>
  //                     </Card>
  //                   </Grid>
  //                 ))}
  //               </Grid>

  //               <Card sx={cardStyles}>
  //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
  //                   <Typography variant="h6" gutterBottom>
  //                     Your Recent Job Postings
  //                   </Typography>
  //                   {error && <Alert severity="error">{error}</Alert>}
  //                   <TableContainer>
  //                     <Table size="small" aria-label="recent job postings">
  //                       <TableHead>
  //                         <TableRow>
  //                           <TableCell>Position</TableCell>
  //                           <TableCell>Status</TableCell>
  //                           <TableCell align="right">Actions</TableCell>
  //                         </TableRow>
  //                       </TableHead>
  //                       <TableBody>
  //                         {loading
  //                           ? renderJobSkeletons()
  //                           : jobsToDisplay.map((job) => (
  //                               <TableRow
  //                                 key={job.id}
  //                                 hover
  //                                 sx={{
  //                                   "&:last-child td, &:last-child th": {
  //                                     border: 0,
  //                                   },
  //                                 }}
  //                               >
  //                                 <TableCell component="th" scope="row">
  //                                   <Box>
  //                                     <Typography
  //                                       variant="subtitle2"
  //                                       fontWeight="bold"
  //                                     >
  //                                       {job.title}
  //                                     </Typography>
  //                                     <Stack
  //                                       direction="row"
  //                                       alignItems="center"
  //                                       color="text.secondary"
  //                                       spacing={0.5}
  //                                     >
  //                                       <LocationOn sx={{ fontSize: 14 }} />
  //                                       <Typography variant="caption">
  //                                         {job.location}
  //                                       </Typography>
  //                                     </Stack>
  //                                   </Box>
  //                                 </TableCell>
  //                                 <TableCell>
  //                                   <Chip
  //                                     label={job.status_display}
  //                                     size="small"
  //                                     color={getStatusColor(job.status_display)}
  //                                   />
  //                                 </TableCell>
  //                                 <TableCell align="right">
  //                                   <IconButton
  //                                     size="small"
  //                                     onClick={() => handleOpenModal(job)}
  //                                     title="View & Edit Job Details"
  //                                   >
  //                                     <Visibility />
  //                                   </IconButton>
  //                                 </TableCell>
  //                               </TableRow>
  //                             ))}
  //                         {!loading && !error && jobPostings.length === 0 && (
  //                           <TableRow>
  //                             <TableCell
  //                               colSpan={3}
  //                               align="center"
  //                               sx={{ py: 3 }}
  //                             >
  //                               No jobs posted yet.
  //                             </TableCell>
  //                           </TableRow>
  //                         )}
  //                       </TableBody>
  //                     </Table>
  //                   </TableContainer>
  //                   {!loading && jobPostings.length > 5 && (
  //                     <Box
  //                       sx={{
  //                         mt: 2,
  //                         display: "flex",
  //                         justifyContent: "flex-end",
  //                       }}
  //                     >
  //                       <Button
  //                         onClick={() => navigate("/gatep/employer/candidates")}
  //                         endIcon={<ArrowForward />}
  //                       >
  //                         View All Jobs
  //                       </Button>
  //                     </Box>
  //                   )}
  //                 </CardContent>
  //               </Card>
  //             </Stack>
  //           </Grid>

  //           <Grid item xs={12} lg={4}>
  //             <Stack spacing={{ xs: 2, md: 4 }}>
  //               <Card sx={cardStyles}>
  //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
  //                   <Typography variant="h6" gutterBottom>
  //                     Top Matched Candidates
  //                   </Typography>
  //                   {candidatesLoading ? (
  //                     renderCandidateSkeletons()
  //                   ) : error ? (
  //                     <Alert severity="warning">
  //                       Could not load candidate data.
  //                     </Alert>
  //                   ) : topCandidates.length > 0 ? (
  //                     <Stack spacing={2}>
  //                       {topCandidates.map((candidate) => (
  //                         <Paper
  //                           key={candidate.id}
  //                           variant="outlined"
  //                           sx={{ p: 2, borderRadius: 2 }}
  //                         >
  //                           <Stack
  //                             direction="row"
  //                             spacing={2}
  //                             alignItems="center"
  //                             mb={1.5}
  //                           >
  //                             <Avatar sx={{ bgcolor: "primary.main" }}>
  //                               {candidate.applicant_name.charAt(0).toUpperCase()}
  //                             </Avatar>
  //                             <Box flexGrow={1}>
  //                               <Typography variant="subtitle1" fontWeight="bold">
  //                                 {candidate.applicant_name}
  //                               </Typography>
  //                               {candidate.location && (
  //                                 <Typography
  //                                   variant="body2"
  //                                   color="text.secondary"
  //                                 >
  //                                   Location: {candidate.location}
  //                                 </Typography>
  //                               )}
  //                             </Box>
  //                             <Chip
  //                               label={`${candidate.matching_percentage}% Match`}
  //                               color="success"
  //                               variant="outlined"
  //                             />
  //                           </Stack>

  //                           <Stack direction="row" spacing={1} mt={2}>
  //                             <Button size="small" variant="contained">
  //                               View Profile
  //                             </Button>
  //                             <Button size="small" variant="outlined">
  //                               Schedule Interview
  //                             </Button>
  //                           </Stack>
  //                         </Paper>
  //                       ))}
  //                     </Stack>
  //                   ) : (
  //                     <Typography
  //                       variant="body2"
  //                       color="text.secondary"
  //                       sx={{ textAlign: "center", py: 3 }}
  //                     >
  //                       No potential candidates found for the latest job.
  //                     </Typography>
  //                   )}
  //                 </CardContent>
  //               </Card>

  //               <Card sx={cardStyles}>
  //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
  //                   <Typography variant="h6" gutterBottom>
  //                     Recent Activity
  //                   </Typography>
  //                   <Stack spacing={1.5}>
  //                     <Typography variant="body2">
  //                       • 5 new applications received for "Senior AI Engineer"
  //                       position.
  //                     </Typography>
  //                     <Typography variant="body2">
  //                       • Interview scheduled with Rajesh Kumar for tomorrow at
  //                       2:00 PM.
  //                     </Typography>
  //                     <Typography variant="body2">
  //                       • Priya Sharma accepted the offer for "ML Specialist"
  //                       position.
  //                     </Typography>
  //                   </Stack>
  //                 </CardContent>
  //               </Card>
  //             </Stack>
  //           </Grid>
  //         </Grid>

  //         {selectedJob && (
  //           <Dialog
  //             open={isModalOpen}
  //             onClose={handleCloseModal}
  //             fullWidth
  //             maxWidth="sm"
  //           >
  //             <DialogTitle sx={{ pb: 1 }}>
  //               Job Details
  //               <IconButton
  //                 onClick={handleCloseModal}
  //                 sx={{ position: "absolute", right: 16, top: 12 }}
  //               >
  //                 <Close />
  //               </IconButton>
  //             </DialogTitle>
  //             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
  //               {modalError && (
  //                 <Alert severity="error" sx={{ mb: 2 }}>
  //                   {modalError}
  //                 </Alert>
  //               )}
  //               <Grid container spacing={3}>
  //                 <Grid item xs={12}>
  //                   <Typography variant="overline" color="text.secondary">
  //                     Role
  //                   </Typography>
  //                   {isEditing ? (
  //                     <TextField
  //                       fullWidth
  //                       name="title"
  //                       value={editedData.title}
  //                       onChange={handleInputChange}
  //                     />
  //                   ) : (
  //                     <Typography variant="h6">{selectedJob.title}</Typography>
  //                   )}
  //                 </Grid>
  //                 <Grid item xs={12} sm={6}>
  //                   <Typography variant="overline" color="text.secondary">
  //                     Location
  //                   </Typography>
  //                   {isEditing ? (
  //                     <TextField
  //                       fullWidth
  //                       name="location"
  //                       value={editedData.location}
  //                       onChange={handleInputChange}
  //                     />
  //                   ) : (
  //                     <Typography>{selectedJob.location}</Typography>
  //                   )}
  //                 </Grid>
  //                 <Grid item xs={12} sm={6}>
  //                   <Typography variant="overline" color="text.secondary">
  //                     Posted Date
  //                   </Typography>
  //                   <Typography>
  //                     {new Date(selectedJob.created_at).toLocaleDateString()}
  //                   </Typography>
  //                 </Grid>
  //                 <Grid item xs={12}>
  //                   <Typography variant="overline" color="text.secondary">
  //                     Required Skills
  //                   </Typography>
  //                   {isEditing ? (
  //                     <Autocomplete
  //                       multiple
  //                       freeSolo
  //                       options={[]}
  //                       value={editedData.required_skills || []}
  //                       onChange={handleSkillsChange}
  //                       renderTags={(value, getTagProps) =>
  //                         value.map((option, index) => (
  //                           <Chip
  //                             variant="outlined"
  //                             label={option}
  //                             {...getTagProps({ index })}
  //                           />
  //                         ))
  //                       }
  //                       renderInput={(params) => (
  //                         <TextField
  //                           {...params}
  //                           variant="outlined"
  //                           placeholder="Add skills"
  //                         />
  //                       )}
  //                     />
  //                   ) : (
  //                     <Box
  //                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
  //                     >
  //                       {(selectedJob.required_skills || []).map((skill) => (
  //                         <Chip key={skill} label={skill} />
  //                       ))}
  //                     </Box>
  //                   )}
  //                 </Grid>
  //               </Grid>
  //             </DialogContent>
  //             <DialogActions sx={{ p: 2 }}>
  //               {isEditing ? (
  //                 <>
  //                   <Button onClick={handleCancelEdit} color="secondary">
  //                     Cancel
  //                   </Button>
  //                   <Button
  //                     onClick={handleSaveChanges}
  //                     variant="contained"
  //                     startIcon={
  //                       modalLoading ? (
  //                         <CircularProgress size={20} color="inherit" />
  //                       ) : (
  //                         <Save />
  //                       )
  //                     }
  //                     disabled={modalLoading}
  //                   >
  //                     Save
  //                   </Button>
  //                 </>
  //               ) : (
  //                 <>
  //                   <Button
  //                     onClick={() => setIsEditing(true)}
  //                     variant="outlined"
  //                     startIcon={<Edit />}
  //                   >
  //                     Edit
  //                   </Button>
  //                   <Button onClick={handleCloseModal}>Close</Button>
  //                 </>
  //               )}
  //             </DialogActions>
  //           </Dialog>
  //         )}
  //       </Box>
  //     </Layout>
  //   );
  // };

  // export default EmployerDashboard;
// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   TrendingUp,
//   LocationOn,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
// } from "@mui/icons-material";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";

// const cardStyles = {
//   borderRadius: 3,
//   boxShadow: "0 4px 12px 0 rgba(0,0,0,0.07)",
//   height: "100%",
//   display: "flex",
//   flexDirection: "column",
// };

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 8,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const token = localStorage.getItem("access_token");
//         if (!token)
//           throw new Error("Authentication token not found. Please log in.");

//         const headers = { Authorization: `Bearer ${token}` };

//         const jobResponse = await axios.get(
//           "https://tdtlworld.com/gatep-backend/api/job-postings/",
//           { headers }
//         );
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await axios.get(
//           "https://tdtlworld.com/gatep-backend/api/applications/",
//           { headers }
//         );
//         const summaryStats = appResponse.data?.summary_stats;

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//         }));
//         setLoading(false);
        
//         if (jobs.length > 0) {
//           const mostRecentJobId = jobs[0].id;
//           const candidatesResponse = await axios.get(
//             `http://localhost:8000/api/job-postings/${mostRecentJobId}/potential-candidates/`,
//             { headers }
//           );

//           const potentialCandidates = candidatesResponse.data || [];
          
//           const formattedCandidates = potentialCandidates.map((candidate) => ({
//             id: candidate.talent_id,
//             applicant_name: candidate.name,
//             matching_percentage: Math.round(candidate.ai_match_score),
//             location: candidate.location,
//           }));

//           const sortedCandidates = formattedCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 2));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage =
//             err.response.status === 401
//               ? "Your session has expired. Please log in again."
//               : `Error: ${
//                   err.response.data?.detail || err.response.statusText
//                 }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         setError(errorMessage);
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 5);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const token = localStorage.getItem("access_token");
//       const response = await axios.put(
//         // `https://tdtlworld.com/gatep-backend/api/job-postings/${selectedJob.id}/`,
//                 `http://localhost:8000/api/job-postings/${selectedJob.id}/`,

//         { ...selectedJob, ...editedData },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };
//   const getStatusColor = (statusDisplay) => {
//     return statusDisplay === "Published" ? "success" : "warning";
//   };

//   const renderJobSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={20} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(2)].map((_, index) => (
//       <Paper
//         key={index}
//         variant="outlined"
//         sx={{ p: 2, mb: 2, borderRadius: 2 }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Skeleton variant="rounded" height={60} sx={{ mt: 2 }} />
//       </Paper>
//     ));

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           position: "absolute",
//           top: 0,
//           left: 0,
//           right: 0,
//           bottom: 0,
//           overflowY: "auto",
//           py: { xs: 3, md: 4 },
//           px: { xs: 2, md: 4 },
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={{ xs: 2, md: 4 }}>
//           <Grid item xs={12} lg={8}>
//             <Stack spacing={{ xs: 2, md: 4 }}>
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Quick Actions
//                   </Typography>
//                   <Stack spacing={2}>
//                     <Button
//                       variant="contained"
//                       startIcon={<PostAdd />}
//                       onClick={() => navigate("/gatep/employer/post-job")}
//                     >
//                       Post New Job
//                     </Button>
//                     <Button
//                       variant="outlined"
//                       startIcon={<People />}
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                     >
//                       Manage Candidates
//                     </Button>
//                     <Button
//                       variant="outlined"
//                       startIcon={<Schedule />}
//                       onClick={() => navigate("/gatep/employer/interviews")}
//                     >
//                       Scheduled Interviews
//                     </Button>
//                   </Stack>
//                 </CardContent>
//               </Card>

//               <Grid container spacing={2}>
//                 {[
//                   {
//                     icon: <PostAdd color="primary" sx={{ fontSize: 40 }} />,
//                     value: stats.activeJobs,
//                     label: "Active Jobs",
//                   },
//                   {
//                     icon: <People color="success" sx={{ fontSize: 40 }} />,
//                     value: stats.totalApplications,
//                     label: "Total Applications",
//                   },
//                   {
//                     icon: <Schedule color="warning" sx={{ fontSize: 40 }} />,
//                     value: stats.interviewsScheduled,
//                     label: "Interviews Scheduled",
//                   },
//                   {
//                     icon: <TrendingUp color="info" sx={{ fontSize: 40 }} />,
//                     value: stats.offersExtended,
//                     label: "Offers Extended",
//                   },
//                 ].map((stat, index) => (
//                   <Grid item xs={6} sm={3} lg={3} key={index}>
//                     <Card sx={cardStyles}>
//                       <CardContent
//                         sx={{ textAlign: "center", p: 2, flexGrow: 1 }}
//                       >
//                         {stat.icon}
//                         <Typography variant="h4" sx={{ my: 1 }}>
//                           {loading ? (
//                             <Skeleton width={40} sx={{ mx: "auto" }} />
//                           ) : (
//                             stat.value
//                           )}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {stat.label}
//                         </Typography>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 ))}
//               </Grid>

//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Your Recent Job Postings
//                   </Typography>
//                   {error && <Alert severity="error">{error}</Alert>}
//                   <TableContainer>
//                     <Table size="small" aria-label="recent job postings">
//                       <TableHead>
//                         <TableRow>
//                           <TableCell>Position</TableCell>
//                           <TableCell>Status</TableCell>
//                           <TableCell align="right">Actions</TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {loading
//                           ? renderJobSkeletons()
//                           : jobsToDisplay.map((job) => (
//                               <TableRow
//                                 key={job.id}
//                                 hover
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                 }}
//                               >
//                                 <TableCell component="th" scope="row">
//                                   <Box>
//                                     <Typography
//                                       variant="subtitle2"
//                                       fontWeight="bold"
//                                     >
//                                       {job.title}
//                                     </Typography>
//                                     <Stack
//                                       direction="row"
//                                       alignItems="center"
//                                       color="text.secondary"
//                                       spacing={0.5}
//                                     >
//                                       <LocationOn sx={{ fontSize: 14 }} />
//                                       <Typography variant="caption">
//                                         {job.location}
//                                       </Typography>
//                                     </Stack>
//                                   </Box>
//                                 </TableCell>
//                                 <TableCell>
//                                   <Chip
//                                     label={job.status_display}
//                                     size="small"
//                                     color={getStatusColor(job.status_display)}
//                                   />
//                                 </TableCell>
//                                 <TableCell align="right">
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleOpenModal(job)}
//                                     title="View & Edit Job Details"
//                                   >
//                                     <Visibility />
//                                   </IconButton>
//                                 </TableCell>
//                               </TableRow>
//                             ))}
//                         {!loading && !error && jobPostings.length === 0 && (
//                           <TableRow>
//                             <TableCell
//                               colSpan={3}
//                               align="center"
//                               sx={{ py: 3 }}
//                             >
//                               No jobs posted yet.
//                             </TableCell>
//                           </TableRow>
//                         )}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                   {!loading && jobPostings.length > 5 && (
//                     <Box
//                       sx={{
//                         mt: 2,
//                         display: "flex",
//                         justifyContent: "flex-end",
//                       }}
//                     >
//                       <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                       >
//                         View All Jobs
//                       </Button>
//                     </Box>
//                   )}
//                 </CardContent>
//               </Card>
//             </Stack>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//             <Stack spacing={{ xs: 2, md: 4 }}>
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Top Matched Candidates
//                   </Typography>
//                   {candidatesLoading ? (
//                     renderCandidateSkeletons()
//                   ) : error ? (
//                     <Alert severity="warning">
//                       Could not load candidate data.
//                     </Alert>
//                   ) : topCandidates.length > 0 ? (
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           variant="outlined"
//                           sx={{ p: 2, borderRadius: 2 }}
//                         >
//                           <Stack
//                             direction="row"
//                             spacing={2}
//                             alignItems="center"
//                             mb={1.5}
//                           >
//                             <Avatar sx={{ bgcolor: "primary.main" }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               {candidate.location &&
//                                 <Typography
//                                   variant="body2"
//                                   color="text.secondary"
//                                 >
//                                   Location: {candidate.location}
//                                 </Typography>
//                               }
//                             </Box>
//                             <Chip
//                               label={`${candidate.matching_percentage}% Match`}
//                               color="success"
//                               variant="outlined"
//                             />
//                           </Stack>
                          
//                           <Stack direction="row" spacing={1} mt={2}>
//                             <Button size="small" variant="contained">
//                               View Profile
//                             </Button>
//                             <Button size="small" variant="outlined">
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   ) : (
//                     <Typography
//                       variant="body2"
//                       color="text.secondary"
//                       sx={{ textAlign: "center", py: 3 }}
//                     >
//                       No potential candidates found for the latest job.
//                     </Typography>
//                   )}
//                 </CardContent>
//               </Card>

//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Recent Activity
//                   </Typography>
//                   <Stack spacing={1.5}>
//                     <Typography variant="body2">
//                       • 5 new applications received for "Senior AI Engineer"
//                       position.
//                     </Typography>
//                     <Typography variant="body2">
//                       • Interview scheduled with Rajesh Kumar for tomorrow at
//                       2:00 PM.
//                     </Typography>
//                     <Typography variant="body2">
//                       • Priya Sharma accepted the offer for "ML Specialist"
//                       position.
//                     </Typography>
//                   </Stack>
//                 </CardContent>
//               </Card>
//             </Stack>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;

// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   TrendingUp,
//   LocationOn,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
// } from "@mui/icons-material";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";
// // Corrected import path assuming 'context' folder is now inside 'src'
// import apiClient from "../context/apiClient";

// const cardStyles = {
//   borderRadius: 3,
//   boxShadow: "0 4px 12px 0 rgba(0,0,0,0.07)",
//   height: "100%",
//   display: "flex",
//   flexDirection: "column",
// };

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 8,
//   });

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const jobResponse = await apiClient.get("/job-postings/");
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await apiClient.get("/applications/");
//         const summaryStats = appResponse.data?.summary_stats;
//         const applications = appResponse.data?.applications || [];

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//         }));
//         setLoading(false);

//         if (applications.length > 0) {
//           const scorePromises = applications.map((app) =>
//             apiClient
//               .get(
//                 `/job-postings/${app.job_posting}/ai-score-totalent/${app.id}/`
//               )
//               .catch((err) => ({ error: true, data: null }))
//           );

//           const scoreResults = await Promise.all(scorePromises);

//           const combinedCandidates = applications
//             .map((app, index) => {
//               const scoreResult = scoreResults[index];
//               if (scoreResult.error || !scoreResult.data) {
//                 return null;
//               }
//               return {
//                 ...app,
//                 matching_percentage: scoreResult.data?.matching_percentage ?? 0,
//                 user_skills: scoreResult.data?.user_skills || [],
//               };
//             })
//             .filter(Boolean);

//           const filteredByScoreCandidates = combinedCandidates.filter(
//             (candidate) => candidate.matching_percentage > 60
//           );

//           const sortedCandidates = filteredByScoreCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 2));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage =
//             err.response.status === 401
//               ? "Your session has expired. Please log in again."
//               : `Error: ${
//                   err.response.data?.detail || err.response.statusText
//                 }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         setError(errorMessage);
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   const jobsToDisplay = jobPostings.slice(0, 5);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const response = await apiClient.put(
//         `/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };
//   const getStatusColor = (statusDisplay) => {
//     return statusDisplay === "Published" ? "success" : "warning";
//   };

//   const renderJobSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={20} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(2)].map((_, index) => (
//       <Paper
//         key={index}
//         variant="outlined"
//         sx={{ p: 2, mb: 2, borderRadius: 2 }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Skeleton variant="rounded" height={60} sx={{ mt: 2 }} />
//       </Paper>
//     ));

//   return (
//     <Layout title="Employer Dashboard">
//       <Box
//         sx={{
//           position: "absolute",
//           top: 0,
//           left: 0,
//           right: 0,
//           bottom: 0,
//           overflowY: "auto",
//           py: { xs: 3, md: 4 },
//           px: { xs: 2, md: 4 },
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={{ xs: 2, md: 4 }}>
//           <Grid item xs={12} lg={8}>
//             <Stack spacing={{ xs: 2, md: 4 }}>
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Quick Actions
//                   </Typography>
//                   <Stack spacing={2}>
//                     <Button
//                       variant="contained"
//                       startIcon={<PostAdd />}
//                       onClick={() => navigate("/gatep/employer/post-job")}
//                     >
//                       Post New Job
//                     </Button>
//                     <Button
//                       variant="outlined"
//                       startIcon={<People />}
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                     >
//                       Manage Candidates
//                     </Button>
//                     <Button
//                       variant="outlined"
//                       startIcon={<Schedule />}
//                       onClick={() => navigate("/gatep/employer/interviews")}
//                     >
//                       Scheduled Interviews
//                     </Button>
//                   </Stack>
//                 </CardContent>
//               </Card>

//               <Grid container spacing={2}>
//                 {[
//                   {
//                     icon: <PostAdd color="primary" sx={{ fontSize: 40 }} />,
//                     value: stats.activeJobs,
//                     label: "Active Jobs",
//                   },
//                   {
//                     icon: <People color="success" sx={{ fontSize: 40 }} />,
//                     value: stats.totalApplications,
//                     label: "Total Applications",
//                   },
//                   {
//                     icon: <Schedule color="warning" sx={{ fontSize: 40 }} />,
//                     value: stats.interviewsScheduled,
//                     label: "Interviews Scheduled",
//                   },
//                   {
//                     icon: <TrendingUp color="info" sx={{ fontSize: 40 }} />,
//                     value: stats.offersExtended,
//                     label: "Offers Extended",
//                   },
//                 ].map((stat, index) => (
//                   <Grid item xs={6} sm={3} lg={3} key={index}>
//                     <Card sx={cardStyles}>
//                       <CardContent
//                         sx={{ textAlign: "center", p: 2, flexGrow: 1 }}
//                       >
//                         {stat.icon}
//                         <Typography variant="h4" sx={{ my: 1 }}>
//                           {loading ? (
//                             <Skeleton width={40} sx={{ mx: "auto" }} />
//                           ) : (
//                             stat.value
//                           )}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {stat.label}
//                         </Typography>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 ))}
//               </Grid>

//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Your Recent Job Postings
//                   </Typography>
//                   {error && <Alert severity="error">{error}</Alert>}
//                   <TableContainer>
//                     <Table size="small" aria-label="recent job postings">
//                       <TableHead>
//                         <TableRow>
//                           <TableCell>Position</TableCell>
//                           <TableCell>Status</TableCell>
//                           <TableCell align="right">Actions</TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {loading
//                           ? renderJobSkeletons()
//                           : jobsToDisplay.map((job) => (
//                               <TableRow
//                                 key={job.id}
//                                 hover
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                 }}
//                               >
//                                 <TableCell component="th" scope="row">
//                                   <Box>
//                                     <Typography
//                                       variant="subtitle2"
//                                       fontWeight="bold"
//                                     >
//                                       {job.title}
//                                     </Typography>
//                                     <Stack
//                                       direction="row"
//                                       alignItems="center"
//                                       color="text.secondary"
//                                       spacing={0.5}
//                                     >
//                                       <LocationOn sx={{ fontSize: 14 }} />
//                                       <Typography variant="caption">
//                                         {job.location}
//                                       </Typography>
//                                     </Stack>
//                                   </Box>
//                                 </TableCell>
//                                 <TableCell>
//                                   <Chip
//                                     label={job.status_display}
//                                     size="small"
//                                     color={getStatusColor(job.status_display)}
//                                   />
//                                 </TableCell>
//                                 <TableCell align="right">
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleOpenModal(job)}
//                                     title="View & Edit Job Details"
//                                   >
//                                     <Visibility />
//                                   </IconButton>
//                                 </TableCell>
//                               </TableRow>
//                             ))}
//                         {!loading && !error && jobPostings.length === 0 && (
//                           <TableRow>
//                             <TableCell
//                               colSpan={3}
//                               align="center"
//                               sx={{ py: 3 }}
//                             >
//                               No jobs posted yet.
//                             </TableCell>
//                           </TableRow>
//                         )}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                   {!loading && jobPostings.length > 5 && (
//                     <Box
//                       sx={{
//                         mt: 2,
//                         display: "flex",
//                         justifyContent: "flex-end",
//                       }}
//                     >
//                       <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                       >
//                         View All Jobs
//                       </Button>
//                     </Box>
//                   )}
//                 </CardContent>
//               </Card>
//             </Stack>
//           </Grid>

//           <Grid item xs={12} lg={4}>
//             <Stack spacing={{ xs: 2, md: 4 }}>
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Top Matched Candidates
//                   </Typography>
//                   {candidatesLoading ? (
//                     renderCandidateSkeletons()
//                   ) : error ? (
//                     <Alert severity="warning">
//                       Could not load candidate data.
//                     </Alert>
//                   ) : topCandidates.length > 0 ? (
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           variant="outlined"
//                           sx={{ p: 2, borderRadius: 2 }}
//                         >
//                           <Stack
//                             direction="row"
//                             spacing={2}
//                             alignItems="center"
//                             mb={1.5}
//                           >
//                             <Avatar sx={{ bgcolor: "primary.main" }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography
//                                 variant="body2"
//                                 color="text.secondary"
//                               >
//                                 Status: {candidate.status_display}
//                               </Typography>
//                             </Box>
//                             <Chip
//                               label={`${candidate.matching_percentage}% Match`}
//                               color="success"
//                               variant="outlined"
//                             />
//                           </Stack>
//                           <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
//                             {(candidate.user_skills || [])
//                               .slice(0, 7)
//                               .map((skill) => (
//                                 <Chip key={skill} label={skill} size="small" />
//                               ))}
//                           </Box>
//                           <Stack direction="row" spacing={1}>
//                             <Button size="small" variant="contained">
//                               View Profile
//                             </Button>
//                             <Button size="small" variant="outlined">
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   ) : (
//                     <Typography
//                       variant="body2"
//                       color="text.secondary"
//                       sx={{ textAlign: "center", py: 3 }}
//                     >
//                       No candidates found with an AI score above 60%.
//                     </Typography>
//                   )}
//                 </CardContent>
//               </Card>

//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Recent Activity
//                   </Typography>
//                   <Stack spacing={1.5}>
//                     <Typography variant="body2">
//                       • 5 new applications received for "Senior AI Engineer"
//                       position.
//                     </Typography>
//                     <Typography variant="body2">
//                       • Interview scheduled with Rajesh Kumar for tomorrow at
//                       2:00 PM.
//                     </Typography>
//                     <Typography variant="body2">
//                       • Priya Sharma accepted the offer for "ML Specialist"
//                       position.
//                     </Typography>
//                   </Stack>
//                 </CardContent>
//               </Card>
//             </Stack>
//           </Grid>
//         </Grid>

//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;


// // import { useState, useEffect } from "react";
// // import {
// //   Grid,
// //   Card,
// //   CardContent,
// //   Typography,
// //   Button,
// //   Box,
// //   Chip,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   Avatar,
// //   IconButton,
// //   Alert,
// //   Skeleton,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions,
// //   TextField,
// //   CircularProgress,
// //   Autocomplete,
// //   Container,
// //   Stack,
// //   Paper,
// // } from "@mui/material";
// // import {
// //   PostAdd,
// //   People,
// //   Visibility,
// //   Schedule,
// //   TrendingUp,
// //   LocationOn,
// //   ArrowForward,
// //   Close,
// //   Edit,
// //   Save,
// // } from "@mui/icons-material";
// // import Layout from "../components/common/Layout";
// // import { useNavigate } from "react-router-dom";
// // import axios from "axios";

// // // Helper for consistent card styling
// // const cardStyles = {
// //   borderRadius: 3,
// //   boxShadow: "0 4px 12px 0 rgba(0,0,0,0.07)",
// //   height: "100%",
// //   display: "flex", // Added for flexbox layout
// //   flexDirection: "column", // Added for flexbox layout
// // };

// // const EmployerDashboard = () => {
// //   const navigate = useNavigate();

// //   // State for page data
// //   const [jobPostings, setJobPostings] = useState([]);
// //   const [topCandidates, setTopCandidates] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState(null);
// //   const [candidatesLoading, setCandidatesLoading] = useState(true);

// //   // State for modal
// //   const [isModalOpen, setIsModalOpen] = useState(false);
// //   const [selectedJob, setSelectedJob] = useState(null);

// //   // State for modal's internal logic
// //   const [isEditing, setIsEditing] = useState(false);
// //   const [editedData, setEditedData] = useState({});
// //   const [modalLoading, setModalLoading] = useState(false);
// //   const [modalError, setModalError] = useState("");

// //   const [stats, setStats] = useState({
// //     activeJobs: 0,
// //     totalApplications: 0,
// //     interviewsScheduled: 0,
// //     offersExtended: 8, // Assuming this is static or from another source
// //   });

// //   // Fetch all dashboard data from the API on component mount
// //   useEffect(() => {
// //     const fetchDashboardData = async () => {
// //       setLoading(true);
// //       setCandidatesLoading(true);
// //       setError(null);

// //       try {
// //         const token = localStorage.getItem("access_token");
// //         if (!token)
// //           throw new Error("Authentication token not found. Please log in.");

// //         const headers = { Authorization: `Bearer ${token}` };

// //         const jobResponse = await axios.get(
// //           "https://tdtlworld.com/gatep-backend/api/job-postings/",
// //           { headers }
// //         );
// //         const jobs = jobResponse?.data || [];
// //         setJobPostings(jobs);
// //         const activeJobsCount = jobs.filter(
// //           (job) => job.status === "PUBLISHED"
// //         ).length;

// //         const appResponse = await axios.get(
// //           "https://tdtlworld.com/gatep-backend/api/applications/",
// //           { headers }
// //         );
// //         const summaryStats = appResponse.data?.summary_stats;
// //         const applications = appResponse.data?.applications || [];

// //         setStats((prevStats) => ({
// //           ...prevStats,
// //           activeJobs: activeJobsCount,
// //           totalApplications: summaryStats?.total_applications ?? 0,
// //           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
// //         }));
// //         setLoading(false);

// //         if (applications.length > 0) {
// //           // --- FIXED API LOGIC ---
// //           // Each application's score is fetched individually using its job_posting ID and application ID.
// //           const scorePromises = applications.map((app) =>
// //             axios
// //               .get(
// //                 `https://tdtlworld.com/gatep-backend/api/job-postings/${app.job_posting}/ai-score-totalent/${app.id}/`,
// //                 { headers }
// //               )
// //               .catch((err) => {
// //                 console.error(`Failed to fetch score for app ${app.id}:`, err);
// //                 return { error: true, data: null }; // Gracefully handle individual failures
// //               })
// //           );

// //           const scoreResults = await Promise.all(scorePromises);

// //           const combinedCandidates = applications
// //             .map((app, index) => {
// //               const scoreResult = scoreResults[index];
// //               if (scoreResult.error || !scoreResult.data) {
// //                 return null;
// //               }
// //               return {
// //                 ...app,
// //                 matching_percentage: scoreResult.data?.matching_percentage ?? 0,
// //                 user_skills: scoreResult.data?.user_skills || [],
// //               };
// //             })
// //             .filter(Boolean); // Remove any null entries from failed API calls

// //           const filteredByScoreCandidates = combinedCandidates.filter(
// //             (candidate) => candidate.matching_percentage > 60
// //           );

// //           const sortedCandidates = filteredByScoreCandidates.sort(
// //             (a, b) => b.matching_percentage - a.matching_percentage
// //           );

// //           setTopCandidates(sortedCandidates.slice(0, 2)); // Show top 2 matched candidates
// //         } else {
// //           setTopCandidates([]);
// //         }
// //       } catch (err) {
// //         console.error("Error fetching dashboard data:", err);
// //         let errorMessage =
// //           "An unexpected error occurred. Please try again later.";
// //         if (err.response) {
// //           errorMessage =
// //             err.response.status === 401
// //               ? "Your session has expired. Please log in again."
// //               : `Error: ${
// //                   err.response.data?.detail || err.response.statusText
// //                 }`;
// //         } else if (err.request) {
// //           errorMessage =
// //             "No response from server. Please check your network connection.";
// //         } else {
// //           errorMessage = err.message;
// //         }
// //         setError(errorMessage);
// //         setLoading(false);
// //       } finally {
// //         setCandidatesLoading(false);
// //       }
// //     };

// //     fetchDashboardData();
// //   }, []);

// //   useEffect(() => {
// //     if (selectedJob) {
// //       setEditedData({
// //         title: selectedJob.title || "",
// //         location: selectedJob.location || "",
// //         required_skills: selectedJob.required_skills || [],
// //       });
// //       setIsEditing(false);
// //       setModalError("");
// //     }
// //   }, [selectedJob]);

// //   // --- Modal and data handling logic (no changes needed) ---
// //   const jobsToDisplay = jobPostings.slice(0, 5);
// //   const handleOpenModal = (job) => {
// //     setSelectedJob(job);
// //     setIsModalOpen(true);
// //   };
// //   const handleCloseModal = () => {
// //     setIsModalOpen(false);
// //     setSelectedJob(null);
// //   };
// //   const handleJobUpdate = (updatedJob) => {
// //     setJobPostings((prevJobs) =>
// //       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
// //     );
// //   };
// //   const handleInputChange = (e) => {
// //     const { name, value } = e.target;
// //     setEditedData((prev) => ({ ...prev, [name]: value }));
// //   };
// //   const handleSkillsChange = (event, newValue) => {
// //     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
// //   };
// //   const handleSaveChanges = async () => {
// //     setModalLoading(true);
// //     setModalError("");
// //     try {
// //       const token = localStorage.getItem("access_token");
// //       const response = await axios.put(
// //         `https://tdtlworld.com/gatep-backend/api/job-postings/${selectedJob.id}/`,
// //         { ...selectedJob, ...editedData },
// //         { headers: { Authorization: `Bearer ${token}` } }
// //       );
// //       handleJobUpdate(response.data);
// //       setIsEditing(false);
// //       handleCloseModal();
// //     } catch (err) {
// //       console.error("Failed to update job:", err);
// //       setModalError("Failed to save changes. Please try again.");
// //     } finally {
// //       setModalLoading(false);
// //     }
// //   };
// //   const handleCancelEdit = () => {
// //     if (selectedJob) {
// //       setEditedData({
// //         title: selectedJob.title || "",
// //         location: selectedJob.location || "",
// //         required_skills: selectedJob.required_skills || [],
// //       });
// //     }
// //     setIsEditing(false);
// //   };
// //   const getStatusColor = (statusDisplay) => {
// //     return statusDisplay === "Published" ? "success" : "warning";
// //   };

// //   // --- SKELETON RENDER FUNCTIONS (Excellent Implementation!) ---
// //   const renderJobSkeletons = () =>
// //     [...Array(3)].map((_, index) => (
// //       <TableRow key={index}>
// //         <TableCell>
// //           <Skeleton variant="text" width="80%" />
// //         </TableCell>
// //         <TableCell>
// //           <Skeleton variant="rounded" width={60} height={20} />
// //         </TableCell>
// //         <TableCell>
// //           <Skeleton variant="circular" width={30} height={30} />
// //         </TableCell>
// //       </TableRow>
// //     ));

// //   const renderCandidateSkeletons = () =>
// //     [...Array(2)].map((_, index) => (
// //       <Paper
// //         key={index}
// //         variant="outlined"
// //         sx={{ p: 2, mb: 2, borderRadius: 2 }}
// //       >
// //         <Stack direction="row" spacing={2} alignItems="center">
// //           <Skeleton variant="circular" width={40} height={40} />
// //           <Box sx={{ flexGrow: 1 }}>
// //             <Skeleton variant="text" width="60%" />
// //             <Skeleton variant="text" width="40%" />
// //           </Box>
// //         </Stack>
// //         <Skeleton variant="rounded" height={60} sx={{ mt: 2 }} />
// //       </Paper>
// //     ));

// //   return (
// //     <Layout title="Employer Dashboard">
// //       <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
// //         <Grid container spacing={{ xs: 2, md: 4 }}>
// //           {/* --- MAIN CONTENT COLUMN (LEFT ON DESKTOP) --- */}
// //           {/* --- CORRECTED LAYOUT: Changed lg={4} to lg={8} for better spacing --- */}
// //           <Grid item xs={12} lg={8}>
// //             <Stack spacing={{ xs: 2, md: 4 }}>
// //               {/* 1. Quick Actions */}
// //               <Card sx={cardStyles}>
// //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
// //                   <Typography variant="h6" gutterBottom>
// //                     Quick Actions
// //                   </Typography>
// //                   <Stack spacing={2}>
// //                     <Button
// //                       variant="contained"
// //                       startIcon={<PostAdd />}
// //                       onClick={() => navigate("/gatep/employer/post-job")}
// //                     >
// //                       Post New Job
// //                     </Button>
// //                     <Button
// //                       variant="outlined"
// //                       startIcon={<People />}
// //                       onClick={() => navigate("/gatep/employer/candidates")}
// //                     >
// //                       Manage Candidates
// //                     </Button>
// //                     <Button
// //                       variant="outlined"
// //                       startIcon={<Schedule />}
// //                       onClick={() => navigate("/gatep/employer/interviews")}
// //                     >
// //                       Scheduled Interviews
// //                     </Button>
// //                   </Stack>
// //                 </CardContent>
// //               </Card>

// //               {/* 2. Stats Overview */}
// //               <Grid container spacing={2}>
// //                 {[
// //                   {
// //                     icon: <PostAdd color="primary" sx={{ fontSize: 40 }} />,
// //                     value: stats.activeJobs,
// //                     label: "Active Jobs",
// //                   },
// //                   {
// //                     icon: <People color="success" sx={{ fontSize: 40 }} />,
// //                     value: stats.totalApplications,
// //                     label: "Total Applications",
// //                   },
// //                   {
// //                     icon: <Schedule color="warning" sx={{ fontSize: 40 }} />,
// //                     value: stats.interviewsScheduled,
// //                     label: "Interviews Scheduled",
// //                   },
// //                   {
// //                     icon: <TrendingUp color="info" sx={{ fontSize: 40 }} />,
// //                     value: stats.offersExtended,
// //                     label: "Offers Extended",
// //                   },
// //                 ].map((stat, index) => (
// //                   <Grid item xs={6} sm={3} lg={3} key={index}>
// //                     <Card sx={cardStyles}>
// //                       <CardContent
// //                         sx={{ textAlign: "center", p: 2, flexGrow: 1 }}
// //                       >
// //                         {stat.icon}
// //                         <Typography variant="h4" sx={{ my: 1 }}>
// //                           {loading ? (
// //                             <Skeleton width={40} sx={{ mx: "auto" }} />
// //                           ) : (
// //                             stat.value
// //                           )}
// //                         </Typography>
// //                         <Typography variant="body2" color="text.secondary">
// //                           {stat.label}
// //                         </Typography>
// //                       </CardContent>
// //                     </Card>
// //                   </Grid>
// //                 ))}
// //               </Grid>

// //               {/* 3. Job Postings */}
// //               <Card sx={cardStyles}>
// //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
// //                   <Typography variant="h6" gutterBottom>
// //                     Your Recent Job Postings
// //                   </Typography>
// //                   {error && <Alert severity="error">{error}</Alert>}
// //                   <TableContainer>
// //                     <Table size="small" aria-label="recent job postings">
// //                       <TableHead>
// //                         <TableRow>
// //                           <TableCell>Position</TableCell>
// //                           <TableCell>Status</TableCell>
// //                           <TableCell align="right">Actions</TableCell>
// //                         </TableRow>
// //                       </TableHead>
// //                       <TableBody>
// //                         {loading
// //                           ? renderJobSkeletons()
// //                           : jobsToDisplay.map((job) => (
// //                               <TableRow
// //                                 key={job.id}
// //                                 hover
// //                                 sx={{
// //                                   "&:last-child td, &:last-child th": {
// //                                     border: 0,
// //                                   },
// //                                 }}
// //                               >
// //                                 <TableCell component="th" scope="row">
// //                                   <Box>
// //                                     <Typography
// //                                       variant="subtitle2"
// //                                       fontWeight="bold"
// //                                     >
// //                                       {job.title}
// //                                     </Typography>
// //                                     <Stack
// //                                       direction="row"
// //                                       alignItems="center"
// //                                       color="text.secondary"
// //                                       spacing={0.5}
// //                                     >
// //                                       <LocationOn sx={{ fontSize: 14 }} />
// //                                       <Typography variant="caption">
// //                                         {job.location}
// //                                       </Typography>
// //                                     </Stack>
// //                                   </Box>
// //                                 </TableCell>
// //                                 <TableCell>
// //                                   <Chip
// //                                     label={job.status_display}
// //                                     size="small"
// //                                     color={getStatusColor(job.status_display)}
// //                                   />
// //                                 </TableCell>
// //                                 <TableCell align="right">
// //                                   <IconButton
// //                                     size="small"
// //                                     onClick={() => handleOpenModal(job)}
// //                                     title="View & Edit Job Details"
// //                                   >
// //                                     <Visibility />
// //                                   </IconButton>
// //                                 </TableCell>
// //                               </TableRow>
// //                             ))}
// //                         {!loading && !error && jobPostings.length === 0 && (
// //                           <TableRow>
// //                             <TableCell
// //                               colSpan={3}
// //                               align="center"
// //                               sx={{ py: 3 }}
// //                             >
// //                               No jobs posted yet.
// //                             </TableCell>
// //                           </TableRow>
// //                         )}
// //                       </TableBody>
// //                     </Table>
// //                   </TableContainer>
// //                   {!loading && jobPostings.length > 5 && (
// //                     <Box
// //                       sx={{
// //                         mt: 2,
// //                         display: "flex",
// //                         justifyContent: "flex-end",
// //                       }}
// //                     >
// //                       <Button
// //                         onClick={() => navigate("/gatep/employer/candidates")}
// //                         endIcon={<ArrowForward />}
// //                       >
// //                         View All Jobs
// //                       </Button>
// //                     </Box>
// //                   )}
// //                 </CardContent>
// //               </Card>
// //             </Stack>
// //           </Grid>

// //           {/* --- SIDEBAR COLUMN (RIGHT ON DESKTOP) --- */}
// //           <Grid item xs={12} lg={4}>
// //             <Stack spacing={{ xs: 2, md: 4 }}>
// //               {/* 4. Top Matched Candidates */}
// //               <Card sx={cardStyles}>
// //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
// //                   <Typography variant="h6" gutterBottom>
// //                     Top Matched Candidates
// //                   </Typography>
// //                   {candidatesLoading ? (
// //                     renderCandidateSkeletons()
// //                   ) : error ? (
// //                     <Alert severity="warning">
// //                       Could not load candidate data.
// //                     </Alert>
// //                   ) : topCandidates.length > 0 ? (
// //                     <Stack spacing={2}>
// //                       {topCandidates.map((candidate) => (
// //                         <Paper
// //                           key={candidate.id}
// //                           variant="outlined"
// //                           sx={{ p: 2, borderRadius: 2 }}
// //                         >
// //                           <Stack
// //                             direction="row"
// //                             spacing={2}
// //                             alignItems="center"
// //                             mb={1.5}
// //                           >
// //                             <Avatar sx={{ bgcolor: "primary.main" }}>
// //                               {candidate.applicant_name.charAt(0).toUpperCase()}
// //                             </Avatar>
// //                             <Box flexGrow={1}>
// //                               <Typography variant="subtitle1" fontWeight="bold">
// //                                 {candidate.applicant_name}
// //                               </Typography>
// //                               <Typography
// //                                 variant="body2"
// //                                 color="text.secondary"
// //                               >
// //                                 Status: {candidate.status_display}
// //                               </Typography>
// //                             </Box>
// //                             <Chip
// //                               label={`${candidate.matching_percentage}% Match`}
// //                               color="success"
// //                               variant="outlined"
// //                             />
// //                           </Stack>
// //                           <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
// //                             {(candidate.user_skills || [])
// //                               .slice(0, 7)
// //                               .map((skill) => (
// //                                 <Chip key={skill} label={skill} size="small" />
// //                               ))}
// //                           </Box>
// //                           <Stack direction="row" spacing={1}>
// //                             <Button size="small" variant="contained">
// //                               View Profile
// //                             </Button>
// //                             <Button size="small" variant="outlined">
// //                               Schedule Interview
// //                             </Button>
// //                           </Stack>
// //                         </Paper>
// //                       ))}
// //                     </Stack>
// //                   ) : (
// //                     <Typography
// //                       variant="body2"
// //                       color="text.secondary"
// //                       sx={{ textAlign: "center", py: 3 }}
// //                     >
// //                       No candidates found with an AI score above 60%.
// //                     </Typography>
// //                   )}
// //                 </CardContent>
// //               </Card>

// //               {/* 5. Recent Activity */}
// //               <Card sx={cardStyles}>
// //                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
// //                   <Typography variant="h6" gutterBottom>
// //                     Recent Activity
// //                   </Typography>
// //                   <Stack spacing={1.5}>
// //                     {/* Note: This is static data. For a real app, this would come from an API. */}
// //                     <Typography variant="body2">
// //                       • 5 new applications received for "Senior AI Engineer"
// //                       position.
// //                     </Typography>
// //                     <Typography variant="body2">
// //                       • Interview scheduled with Rajesh Kumar for tomorrow at
// //                       2:00 PM.
// //                     </Typography>
// //                     <Typography variant="body2">
// //                       • Priya Sharma accepted the offer for "ML Specialist"
// //                       position.
// //                     </Typography>
// //                   </Stack>
// //                 </CardContent>
// //               </Card>
// //             </Stack>
// //           </Grid>
// //         </Grid>

// //         {/* --- MODAL DIALOG (No layout changes needed here) --- */}
// //         {selectedJob && (
// //           <Dialog
// //             open={isModalOpen}
// //             onClose={handleCloseModal}
// //             fullWidth
// //             maxWidth="sm"
// //           >
// //             <DialogTitle sx={{ pb: 1 }}>
// //               Job Details
// //               <IconButton
// //                 onClick={handleCloseModal}
// //                 sx={{ position: "absolute", right: 16, top: 12 }}
// //               >
// //                 <Close />
// //               </IconButton>
// //             </DialogTitle>
// //             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
// //               {modalError && (
// //                 <Alert severity="error" sx={{ mb: 2 }}>
// //                   {modalError}
// //                 </Alert>
// //               )}
// //               <Grid container spacing={3}>
// //                 <Grid item xs={12}>
// //                   <Typography variant="overline" color="text.secondary">
// //                     Role
// //                   </Typography>
// //                   {isEditing ? (
// //                     <TextField
// //                       fullWidth
// //                       name="title"
// //                       value={editedData.title}
// //                       onChange={handleInputChange}
// //                     />
// //                   ) : (
// //                     <Typography variant="h6">{selectedJob.title}</Typography>
// //                   )}
// //                 </Grid>
// //                 <Grid item xs={12} sm={6}>
// //                   <Typography variant="overline" color="text.secondary">
// //                     Location
// //                   </Typography>
// //                   {isEditing ? (
// //                     <TextField
// //                       fullWidth
// //                       name="location"
// //                       value={editedData.location}
// //                       onChange={handleInputChange}
// //                     />
// //                   ) : (
// //                     <Typography>{selectedJob.location}</Typography>
// //                   )}
// //                 </Grid>
// //                 <Grid item xs={12} sm={6}>
// //                   <Typography variant="overline" color="text.secondary">
// //                     Posted Date
// //                   </Typography>
// //                   <Typography>
// //                     {new Date(selectedJob.created_at).toLocaleDateString()}
// //                   </Typography>
// //                 </Grid>
// //                 <Grid item xs={12}>
// //                   <Typography variant="overline" color="text.secondary">
// //                     Required Skills
// //                   </Typography>
// //                   {isEditing ? (
// //                     <Autocomplete
// //                       multiple
// //                       freeSolo
// //                       options={[]}
// //                       value={editedData.required_skills || []}
// //                       onChange={handleSkillsChange}
// //                       renderTags={(value, getTagProps) =>
// //                         value.map((option, index) => (
// //                           <Chip
// //                             variant="outlined"
// //                             label={option}
// //                             {...getTagProps({ index })}
// //                           />
// //                         ))
// //                       }
// //                       renderInput={(params) => (
// //                         <TextField
// //                           {...params}
// //                           variant="outlined"
// //                           placeholder="Add skills"
// //                         />
// //                       )}
// //                     />
// //                   ) : (
// //                     <Box
// //                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
// //                     >
// //                       {(selectedJob.required_skills || []).map((skill) => (
// //                         <Chip key={skill} label={skill} />
// //                       ))}
// //                     </Box>
// //                   )}
// //                 </Grid>
// //               </Grid>
// //             </DialogContent>
// //             <DialogActions sx={{ p: 2 }}>
// //               {isEditing ? (
// //                 <>
// //                   <Button onClick={handleCancelEdit} color="secondary">
// //                     Cancel
// //                   </Button>
// //                   <Button
// //                     onClick={handleSaveChanges}
// //                     variant="contained"
// //                     startIcon={
// //                       modalLoading ? (
// //                         <CircularProgress size={20} color="inherit" />
// //                       ) : (
// //                         <Save />
// //                       )
// //                     }
// //                     disabled={modalLoading}
// //                   >
// //                     Save
// //                   </Button>
// //                 </>
// //               ) : (
// //                 <>
// //                   <Button
// //                     onClick={() => setIsEditing(true)}
// //                     variant="outlined"
// //                     startIcon={<Edit />}
// //                   >
// //                     Edit
// //                   </Button>
// //                   <Button onClick={handleCloseModal}>Close</Button>
// //                 </>
// //               )}
// //             </DialogActions>
// //           </Dialog>
// //         )}
// //       </Container>
// //     </Layout>
// //   );
// // };

// // export default EmployerDashboard;
// import { useState, useEffect } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Alert,
//   Skeleton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   CircularProgress,
//   Autocomplete,
//   Stack,
//   Paper,
// } from "@mui/material";
// import {
//   PostAdd,
//   People,
//   Visibility,
//   Schedule,
//   TrendingUp,
//   LocationOn,
//   ArrowForward,
//   Close,
//   Edit,
//   Save,
// } from "@mui/icons-material";
// import Layout from "../components/common/Layout";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";

// // Helper for consistent card styling
// const cardStyles = {
//   borderRadius: 3,
//   boxShadow: "0 4px 12px 0 rgba(0,0,0,0.07)",
//   height: "100%",
//   display: "flex",
//   flexDirection: "column",
// };

// const EmployerDashboard = () => {
//   const navigate = useNavigate();

//   // State for page data
//   const [jobPostings, setJobPostings] = useState([]);
//   const [topCandidates, setTopCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [candidatesLoading, setCandidatesLoading] = useState(true);

//   // State for modal
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);

//   // State for modal's internal logic
//   const [isEditing, setIsEditing] = useState(false);
//   const [editedData, setEditedData] = useState({});
//   const [modalLoading, setModalLoading] = useState(false);
//   const [modalError, setModalError] = useState("");

//   const [stats, setStats] = useState({
//     activeJobs: 0,
//     totalApplications: 0,
//     interviewsScheduled: 0,
//     offersExtended: 8,
//   });

//   // Fetch all dashboard data from the API on component mount
//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       setCandidatesLoading(true);
//       setError(null);

//       try {
//         const token = localStorage.getItem("access_token");
//         if (!token)
//           throw new Error("Authentication token not found. Please log in.");

//         const headers = { Authorization: `Bearer ${token}` };

//         const jobResponse = await axios.get(
//           "https://tdtlworld.com/gatep-backend/api/job-postings/",
//           { headers }
//         );
//         const jobs = jobResponse?.data || [];
//         setJobPostings(jobs);
//         const activeJobsCount = jobs.filter(
//           (job) => job.status === "PUBLISHED"
//         ).length;

//         const appResponse = await axios.get(
//           "https://tdtlworld.com/gatep-backend/api/applications/",
//           { headers }
//         );
//         const summaryStats = appResponse.data?.summary_stats;
//         const applications = appResponse.data?.applications || [];

//         setStats((prevStats) => ({
//           ...prevStats,
//           activeJobs: activeJobsCount,
//           totalApplications: summaryStats?.total_applications ?? 0,
//           interviewsScheduled: summaryStats?.interviews_scheduled_count ?? 0,
//         }));
//         setLoading(false);

//         if (applications.length > 0) {
//           const scorePromises = applications.map((app) =>
//             axios
//               .get(
//                 `https://tdtlworld.com/gatep-backend/api/job-postings/${app.job_posting}/ai-score-totalent/${app.id}/`,
//                 { headers }
//               )
//               .catch((err) => ({ error: true, data: null }))
//           );

//           const scoreResults = await Promise.all(scorePromises);

//           const combinedCandidates = applications
//             .map((app, index) => {
//               const scoreResult = scoreResults[index];
//               if (scoreResult.error || !scoreResult.data) {
//                 return null;
//               }
//               return {
//                 ...app,
//                 matching_percentage: scoreResult.data?.matching_percentage ?? 0,
//                 user_skills: scoreResult.data?.user_skills || [],
//               };
//             })
//             .filter(Boolean);

//           const filteredByScoreCandidates = combinedCandidates.filter(
//             (candidate) => candidate.matching_percentage > 60
//           );

//           const sortedCandidates = filteredByScoreCandidates.sort(
//             (a, b) => b.matching_percentage - a.matching_percentage
//           );

//           setTopCandidates(sortedCandidates.slice(0, 2));
//         } else {
//           setTopCandidates([]);
//         }
//       } catch (err) {
//         let errorMessage = "An unexpected error occurred.";
//         if (err.response) {
//           errorMessage =
//             err.response.status === 401
//               ? "Your session has expired. Please log in again."
//               : `Error: ${
//                   err.response.data?.detail || err.response.statusText
//                 }`;
//         } else if (err.request) {
//           errorMessage = "No response from server. Check your connection.";
//         } else {
//           errorMessage = err.message;
//         }
//         setError(errorMessage);
//         setLoading(false);
//       } finally {
//         setCandidatesLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   useEffect(() => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title || "",
//         location: selectedJob.location || "",
//         required_skills: selectedJob.required_skills || [],
//       });
//       setIsEditing(false);
//       setModalError("");
//     }
//   }, [selectedJob]);

//   // --- Modal and data handling logic ---
//   const jobsToDisplay = jobPostings.slice(0, 5);
//   const handleOpenModal = (job) => {
//     setSelectedJob(job);
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setSelectedJob(null);
//   };
//   const handleJobUpdate = (updatedJob) => {
//     setJobPostings((prevJobs) =>
//       prevJobs.map((job) => (job.id === updatedJob.id ? updatedJob : job))
//     );
//   };
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditedData((prev) => ({ ...prev, [name]: value }));
//   };
//   const handleSkillsChange = (event, newValue) => {
//     setEditedData((prev) => ({ ...prev, required_skills: newValue }));
//   };
//   const handleSaveChanges = async () => {
//     setModalLoading(true);
//     setModalError("");
//     try {
//       const token = localStorage.getItem("access_token");
//       const response = await axios.put(
//         `https://tdtlworld.com/gatep-backend/api/job-postings/${selectedJob.id}/`,
//         { ...selectedJob, ...editedData },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       handleJobUpdate(response.data);
//       setIsEditing(false);
//       handleCloseModal();
//     } catch (err) {
//       setModalError("Failed to save changes. Please try again.");
//     } finally {
//       setModalLoading(false);
//     }
//   };
//   const handleCancelEdit = () => {
//     if (selectedJob) {
//       setEditedData({
//         title: selectedJob.title,
//         location: selectedJob.location,
//         required_skills: selectedJob.required_skills,
//       });
//     }
//     setIsEditing(false);
//   };
//   const getStatusColor = (statusDisplay) => {
//     return statusDisplay === "Published" ? "success" : "warning";
//   };

//   // --- Skeleton Render Functions ---
//   const renderJobSkeletons = () =>
//     [...Array(3)].map((_, index) => (
//       <TableRow key={index}>
//         <TableCell>
//           <Skeleton variant="text" width="80%" />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="rounded" width={60} height={20} />
//         </TableCell>
//         <TableCell>
//           <Skeleton variant="circular" width={30} height={30} />
//         </TableCell>
//       </TableRow>
//     ));

//   const renderCandidateSkeletons = () =>
//     [...Array(2)].map((_, index) => (
//       <Paper
//         key={index}
//         variant="outlined"
//         sx={{ p: 2, mb: 2, borderRadius: 2 }}
//       >
//         <Stack direction="row" spacing={2} alignItems="center">
//           <Skeleton variant="circular" width={40} height={40} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="60%" />
//             <Skeleton variant="text" width="40%" />
//           </Box>
//         </Stack>
//         <Skeleton variant="rounded" height={60} sx={{ mt: 2 }} />
//       </Paper>
//     ));

//   return (
//     <Layout title="Employer Dashboard">
//       {/* --- THE FIX: This Box is positioned absolutely within its parent from Layout.js --- */}
//       <Box
//         sx={{
//           position: "absolute",
//           top: 0,
//           left: 0,
//           right: 0,
//           bottom: 0,
         
//           overflowY: "auto", // Important: handles scrolling within this component
//           py: { xs: 3, md: 4 }, // Vertical padding
//           px: { xs: 2, md: 4 }, // Horizontal padding
//           "&::-webkit-scrollbar": { display: "none" },
//           "-ms-overflow-style": "none",
//           "scrollbar-width": "none",
//         }}
//       >
//         <Grid container spacing={{ xs: 2, md: 4 }}>
//           {/* Main Content Column */}
//           <Grid item xs={12} lg={8}>
//             <Stack spacing={{ xs: 2, md: 4 }}>
//               {/* 1. Quick Actions */}
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Quick Actions
//                   </Typography>
//                   <Stack spacing={2}>
//                     <Button
//                       variant="contained"
//                       startIcon={<PostAdd />}
//                       onClick={() => navigate("/gatep/employer/post-job")}
//                     >
//                       Post New Job
//                     </Button>
//                     <Button
//                       variant="outlined"
//                       startIcon={<People />}
//                       onClick={() => navigate("/gatep/employer/candidates")}
//                     >
//                       Manage Candidates
//                     </Button>
//                     <Button
//                       variant="outlined"
//                       startIcon={<Schedule />}
//                       onClick={() => navigate("/gatep/employer/interviews")}
//                     >
//                       Scheduled Interviews
//                     </Button>
//                   </Stack>
//                 </CardContent>
//               </Card>

//               {/* 2. Stats Overview */}
//               <Grid container spacing={2}>
//                 {[
//                   {
//                     icon: <PostAdd color="primary" sx={{ fontSize: 40 }} />,
//                     value: stats.activeJobs,
//                     label: "Active Jobs",
//                   },
//                   {
//                     icon: <People color="success" sx={{ fontSize: 40 }} />,
//                     value: stats.totalApplications,
//                     label: "Total Applications",
//                   },
//                   {
//                     icon: <Schedule color="warning" sx={{ fontSize: 40 }} />,
//                     value: stats.interviewsScheduled,
//                     label: "Interviews Scheduled",
//                   },
//                   {
//                     icon: <TrendingUp color="info" sx={{ fontSize: 40 }} />,
//                     value: stats.offersExtended,
//                     label: "Offers Extended",
//                   },
//                 ].map((stat, index) => (
//                   <Grid item xs={6} sm={3} lg={3} key={index}>
//                     <Card sx={cardStyles}>
//                       <CardContent
//                         sx={{ textAlign: "center", p: 2, flexGrow: 1 }}
//                       >
//                         {stat.icon}
//                         <Typography variant="h4" sx={{ my: 1 }}>
//                           {loading ? (
//                             <Skeleton width={40} sx={{ mx: "auto" }} />
//                           ) : (
//                             stat.value
//                           )}
//                         </Typography>
//                         <Typography variant="body2" color="text.secondary">
//                           {stat.label}
//                         </Typography>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 ))}
//               </Grid>

//               {/* 3. Job Postings */}
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Your Recent Job Postings
//                   </Typography>
//                   {error && <Alert severity="error">{error}</Alert>}
//                   <TableContainer>
//                     <Table size="small" aria-label="recent job postings">
//                       <TableHead>
//                         <TableRow>
//                           <TableCell>Position</TableCell>
//                           <TableCell>Status</TableCell>
//                           <TableCell align="right">Actions</TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {loading
//                           ? renderJobSkeletons()
//                           : jobsToDisplay.map((job) => (
//                               <TableRow
//                                 key={job.id}
//                                 hover
//                                 sx={{
//                                   "&:last-child td, &:last-child th": {
//                                     border: 0,
//                                   },
//                                 }}
//                               >
//                                 <TableCell component="th" scope="row">
//                                   <Box>
//                                     <Typography
//                                       variant="subtitle2"
//                                       fontWeight="bold"
//                                     >
//                                       {job.title}
//                                     </Typography>
//                                     <Stack
//                                       direction="row"
//                                       alignItems="center"
//                                       color="text.secondary"
//                                       spacing={0.5}
//                                     >
//                                       <LocationOn sx={{ fontSize: 14 }} />
//                                       <Typography variant="caption">
//                                         {job.location}
//                                       </Typography>
//                                     </Stack>
//                                   </Box>
//                                 </TableCell>
//                                 <TableCell>
//                                   <Chip
//                                     label={job.status_display}
//                                     size="small"
//                                     color={getStatusColor(job.status_display)}
//                                   />
//                                 </TableCell>
//                                 <TableCell align="right">
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleOpenModal(job)}
//                                     title="View & Edit Job Details"
//                                   >
//                                     <Visibility />
//                                   </IconButton>
//                                 </TableCell>
//                               </TableRow>
//                             ))}
//                         {!loading && !error && jobPostings.length === 0 && (
//                           <TableRow>
//                             <TableCell
//                               colSpan={3}
//                               align="center"
//                               sx={{ py: 3 }}
//                             >
//                               No jobs posted yet.
//                             </TableCell>
//                           </TableRow>
//                         )}
//                       </TableBody>
//                     </Table>
//                   </TableContainer>
//                   {!loading && jobPostings.length > 5 && (
//                     <Box
//                       sx={{
//                         mt: 2,
//                         display: "flex",
//                         justifyContent: "flex-end",
//                       }}
//                     >
//                       <Button
//                         onClick={() => navigate("/gatep/employer/candidates")}
//                         endIcon={<ArrowForward />}
//                       >
//                         View All Jobs
//                       </Button>
//                     </Box>
//                   )}
//                 </CardContent>
//               </Card>
//             </Stack>
//           </Grid>

//           {/* Sidebar Column */}
//           <Grid item xs={12} lg={4}>
//             <Stack spacing={{ xs: 2, md: 4 }}>
//               {/* 4. Top Matched Candidates */}
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Top Matched Candidates
//                   </Typography>
//                   {candidatesLoading ? (
//                     renderCandidateSkeletons()
//                   ) : error ? (
//                     <Alert severity="warning">
//                       Could not load candidate data.
//                     </Alert>
//                   ) : topCandidates.length > 0 ? (
//                     <Stack spacing={2}>
//                       {topCandidates.map((candidate) => (
//                         <Paper
//                           key={candidate.id}
//                           variant="outlined"
//                           sx={{ p: 2, borderRadius: 2 }}
//                         >
//                           <Stack
//                             direction="row"
//                             spacing={2}
//                             alignItems="center"
//                             mb={1.5}
//                           >
//                             <Avatar sx={{ bgcolor: "primary.main" }}>
//                               {candidate.applicant_name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box flexGrow={1}>
//                               <Typography variant="subtitle1" fontWeight="bold">
//                                 {candidate.applicant_name}
//                               </Typography>
//                               <Typography
//                                 variant="body2"
//                                 color="text.secondary"
//                               >
//                                 Status: {candidate.status_display}
//                               </Typography>
//                             </Box>
//                             <Chip
//                               label={`${candidate.matching_percentage}% Match`}
//                               color="success"
//                               variant="outlined"
//                             />
//                           </Stack>
//                           <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
//                             {(candidate.user_skills || [])
//                               .slice(0, 7)
//                               .map((skill) => (
//                                 <Chip key={skill} label={skill} size="small" />
//                               ))}
//                           </Box>
//                           <Stack direction="row" spacing={1}>
//                             <Button size="small" variant="contained">
//                               View Profile
//                             </Button>
//                             <Button size="small" variant="outlined">
//                               Schedule Interview
//                             </Button>
//                           </Stack>
//                         </Paper>
//                       ))}
//                     </Stack>
//                   ) : (
//                     <Typography
//                       variant="body2"
//                       color="text.secondary"
//                       sx={{ textAlign: "center", py: 3 }}
//                     >
//                       No candidates found with an AI score above 60%.
//                     </Typography>
//                   )}
//                 </CardContent>
//               </Card>

//               {/* 5. Recent Activity */}
//               <Card sx={cardStyles}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
//                   <Typography variant="h6" gutterBottom>
//                     Recent Activity
//                   </Typography>
//                   <Stack spacing={1.5}>
//                     <Typography variant="body2">
//                       • 5 new applications received for "Senior AI Engineer"
//                       position.
//                     </Typography>
//                     <Typography variant="body2">
//                       • Interview scheduled with Rajesh Kumar for tomorrow at
//                       2:00 PM.
//                     </Typography>
//                     <Typography variant="body2">
//                       • Priya Sharma accepted the offer for "ML Specialist"
//                       position.
//                     </Typography>
//                   </Stack>
//                 </CardContent>
//               </Card>
//             </Stack>
//           </Grid>
//         </Grid>

//         {/* --- MODAL DIALOG (Unchanged) --- */}
//         {selectedJob && (
//           <Dialog
//             open={isModalOpen}
//             onClose={handleCloseModal}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle sx={{ pb: 1 }}>
//               Job Details
//               <IconButton
//                 onClick={handleCloseModal}
//                 sx={{ position: "absolute", right: 16, top: 12 }}
//               >
//                 <Close />
//               </IconButton>
//             </DialogTitle>
//             <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
//               {modalError && (
//                 <Alert severity="error" sx={{ mb: 2 }}>
//                   {modalError}
//                 </Alert>
//               )}
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Role
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="title"
//                       value={editedData.title}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography variant="h6">{selectedJob.title}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Location
//                   </Typography>
//                   {isEditing ? (
//                     <TextField
//                       fullWidth
//                       name="location"
//                       value={editedData.location}
//                       onChange={handleInputChange}
//                     />
//                   ) : (
//                     <Typography>{selectedJob.location}</Typography>
//                   )}
//                 </Grid>
//                 <Grid item xs={12} sm={6}>
//                   <Typography variant="overline" color="text.secondary">
//                     Posted Date
//                   </Typography>
//                   <Typography>
//                     {new Date(selectedJob.created_at).toLocaleDateString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Typography variant="overline" color="text.secondary">
//                     Required Skills
//                   </Typography>
//                   {isEditing ? (
//                     <Autocomplete
//                       multiple
//                       freeSolo
//                       options={[]}
//                       value={editedData.required_skills || []}
//                       onChange={handleSkillsChange}
//                       renderTags={(value, getTagProps) =>
//                         value.map((option, index) => (
//                           <Chip
//                             variant="outlined"
//                             label={option}
//                             {...getTagProps({ index })}
//                           />
//                         ))
//                       }
//                       renderInput={(params) => (
//                         <TextField
//                           {...params}
//                           variant="outlined"
//                           placeholder="Add skills"
//                         />
//                       )}
//                     />
//                   ) : (
//                     <Box
//                       sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
//                     >
//                       {(selectedJob.required_skills || []).map((skill) => (
//                         <Chip key={skill} label={skill} />
//                       ))}
//                     </Box>
//                   )}
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <DialogActions sx={{ p: 2 }}>
//               {isEditing ? (
//                 <>
//                   <Button onClick={handleCancelEdit} color="secondary">
//                     Cancel
//                   </Button>
//                   <Button
//                     onClick={handleSaveChanges}
//                     variant="contained"
//                     startIcon={
//                       modalLoading ? (
//                         <CircularProgress size={20} color="inherit" />
//                       ) : (
//                         <Save />
//                       )
//                     }
//                     disabled={modalLoading}
//                   >
//                     Save
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => setIsEditing(true)}
//                     variant="outlined"
//                     startIcon={<Edit />}
//                   >
//                     Edit
//                   </Button>
//                   <Button onClick={handleCloseModal}>Close</Button>
//                 </>
//               )}
//             </DialogActions>
//           </Dialog>
//         )}
//       </Box>
//     </Layout>
//   );
// };

// export default EmployerDashboard;
